import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ClinicalDocument, UserRole, UserSession } from '../types';
import { feedingSoapViewerTitle, formatClinicalDocTypeLabel, isAuthoredEvalDoc, isAuthoredPlanDoc } from '../utils/clinicalDocDisplay';
import { soapGuardianEsignAccess } from '../utils/teletherapyEsign';
import { documentFullySignedAndCleared, guardianSignatureShownSigned } from '../utils/soapGuardianClearance';
import { canManageClientOperations } from '../utils/clientOperationsAccess';
import { signatureCanvasPoint } from '../utils/signatureCanvasPoint';
import { mintedGuardianSend } from '../utils/mintedGuardianSend';
import { formatArizonaTimestamp, GUARDIAN_CLOCK_MISSING_MESSAGE, GUARDIAN_OVERDUE_PAY_MESSAGE, guardianRemoteDeadlineSentence } from '../utils/guardianEncounterDeadline';
import { inPersonEncounterAcknowledgment, soapSessionRemoteMessage } from '../utils/soapEncounterAcknowledgment';
import {
  FileText,
  PenTool,
  CheckCircle2,
  Clock,
  ShieldCheck,
  Search,
  Filter,
  AlertCircle,
  AlertTriangle,
  Plus,
  Edit2,
  Save,
  X,
  Layers,
  Award,
  Calendar,
  Download,
  Printer,
  Share2,
  Target,
  RefreshCw,
  FileCheck,
  ExternalLink,
  File,
  Bell,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  User,
  Users,
  Stethoscope,
  Lock,
  Loader2,
  ArrowLeft,
  ArrowRight,
  MoreHorizontal,
  Check
} from 'lucide-react';
import { PulseIntelligenceMark } from './PulseIntelligenceMark';
import { NewClinicalDocModal } from './NewClinicalDocModal';
import { ClientFaxModal } from './ClientFaxModal';
import { ClinicalDocStructuredDisplay } from './clinical/ClinicalDocStructuredDisplay';
import { DailyTreatmentNoteBody } from './clinical/DailyTreatmentNoteBody';
import { dailyNoteDraftUpdate, DailyNoteGoal, mapDailyTreatmentNote, DAILY_NOTE_NOT_STORED, preserveStoredDailyNoteFile } from '../utils/dailyTreatmentNote';
import { BillingOperationalEvidencePanel } from './clinical/BillingOperationalEvidencePanel';
import { playPulseSeal } from '../utils/pulseAudio';
import { canBulkExportRecords, recordsExportCompletionNote } from '../utils/recordsExportAccess';
import {
  formatDisplayDate,
  getCurrentPayPeriod,
  getAdjacentPayPeriod,
  getRecentPayPeriods,
  getPayPeriodWeeks,
  AllegiantPayPeriod,
  CanonicalPeriodMode,
  ResolvedPeriodContext,
  resolveCanonicalPeriod,
  getCanonicalDocumentDate
} from '../utils/payPeriod';

interface ClinicalDocsViewProps {
  user: UserSession;
  initialSelectedDocId?: string | null;
  onConsumeInitialDocId?: () => void;
  onRefreshStats: () => void;
  onSelectClient: (clientId: string) => void;
  onNavigateTab?: (tab: string, subTab?: string) => void;
  onOpenAIWithPrompt?: (prompt: string) => void;
  initialClientId?: string | null;
}

export const ClinicalDocsView: React.FC<ClinicalDocsViewProps> = ({
  user,
  initialSelectedDocId,
  onConsumeInitialDocId,
  onRefreshStats,
  onSelectClient,
  onNavigateTab,
  onOpenAIWithPrompt,
  initialClientId
}) => {
  const isOwnerOrRc = user.role === 'OWNER' || user.role === 'REFERRAL_COORDINATOR';
  const isSupervisorMobile = user.role === 'SUPERVISOR';
  const isAssistantMobile = user.role === 'ASSISTANT';
  const isClinicalLightMobile = true;
  const isClinicalStaff = user.role === 'SUPERVISOR' || user.role === 'ASSISTANT';
  const [docs, setDocs] = useState<ClinicalDocument[]>([]);
  const docsRef = useRef<ClinicalDocument[]>([]);
  const openingDocIdRef = useRef<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [docTypeFilter, setDocTypeFilter] = useState<'ALL' | 'EVAL' | 'POC' | 'QPR' | 'SOAP' | 'INTAKE' | 'EXTERNAL'>('ALL');
  const [selectedDoc, setSelectedDoc] = useState<ClinicalDocument | null>(null);
  const [showNewDocModal, setShowNewDocModal] = useState(false);
  const [newDocInitialType, setNewDocInitialType] = useState<'EVAL' | 'POC' | 'QPR' | 'SOAP'>('POC');

  // --- CANONICAL HIERARCHY STATE: CLIENT & PERIOD CONTEXT ---
  const [selectedClientId, setSelectedClientId] = useState<string>(() => initialClientId || 'ALL');
  const [clientsList, setClientsList] = useState<any[]>([]);
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientDropdownRef = useRef<HTMLDivElement>(null);
  const mobileClientDropdownRef = useRef<HTMLDivElement>(null);

  // --- CANONICAL HIERARCHY STATE: THERAPIST CONTEXT (Owner / RC) ---
  const [selectedProviderId, setSelectedProviderId] = useState<string>('ALL');
  const [providersList, setProvidersList] = useState<any[]>([]);
  const [providerSearchQuery, setProviderSearchQuery] = useState('');
  const [showProviderDropdown, setShowProviderDropdown] = useState(false);
  const providerDropdownRef = useRef<HTMLDivElement>(null);

  // Canonical Periodicity State
  const [periodMode, setPeriodMode] = useState<CanonicalPeriodMode>('PAY_PERIOD');
  const [selectedPayPeriod, setSelectedPayPeriod] = useState<AllegiantPayPeriod>(() => getCurrentPayPeriod());
  const [selectedWeekNumber, setSelectedWeekNumber] = useState<1 | 2>(1);
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);
  const periodDropdownRef = useRef<HTMLDivElement>(null);
  const mobilePeriodDropdownRef = useRef<HTMLDivElement>(null);

  // Mobile Secondary Document-Type Inline Strip Swap State
  const [showMobileSecondaryFilters, setShowMobileSecondaryFilters] = useState(false);

  const recentPayPeriods = useMemo(() => getRecentPayPeriods(8, 4), []);

  const resolvedPeriod: ResolvedPeriodContext = useMemo(() => {
    return resolveCanonicalPeriod(periodMode, {
      payPeriod: selectedPayPeriod,
      weekNumber: selectedWeekNumber,
      customStartDate,
      customEndDate
    });
  }, [periodMode, selectedPayPeriod, selectedWeekNumber, customStartDate, customEndDate]);

  // Click-outside and Escape key listener for Context Row popovers
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideClient =
        (clientDropdownRef.current && clientDropdownRef.current.contains(target)) ||
        (mobileClientDropdownRef.current && mobileClientDropdownRef.current.contains(target));
      if (!insideClient) {
        setShowClientDropdown(false);
      }

      const insideProvider =
        providerDropdownRef.current && providerDropdownRef.current.contains(target);
      if (!insideProvider) {
        setShowProviderDropdown(false);
      }

      const insidePeriod =
        (periodDropdownRef.current && periodDropdownRef.current.contains(target)) ||
        (mobilePeriodDropdownRef.current && mobilePeriodDropdownRef.current.contains(target));
      if (!insidePeriod) {
        setShowPeriodDropdown(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowClientDropdown(false);
        setShowProviderDropdown(false);
        setShowPeriodDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Fetch canonical client roster from API
  useEffect(() => {
    fetch('/api/clients')
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (Array.isArray(data)) setClientsList(data);
      })
      .catch(() => {});
  }, []);

  // Keep selectedClientId in sync if initialClientId changes
  useEffect(() => {
    if (initialClientId && initialClientId !== selectedClientId) {
      setSelectedClientId(initialClientId);
    }
  }, [initialClientId]);

  // Distinct canonical clients combining API records and active document identities
  const allCanonicalClients = useMemo(() => {
    const map = new Map<string, { id: string; name: string; firstName?: string; lastName?: string; ahcccs_id?: string }>();
    clientsList.forEach((c: any) => {
      if (!c.id) return;
      const firstName = (c.first_name || '').trim();
      const lastName = (c.last_name || '').trim();
      const name = `${firstName} ${lastName}`.trim() || c.name || `Client ${c.id}`;
      map.set(c.id, { id: c.id, name, firstName, lastName, ahcccs_id: c.ahcccs_id });
    });
    docs.forEach((d: any) => {
      if (d.client_id && !map.has(d.client_id)) {
        const firstName = (d.first_name || '').trim();
        const lastName = (d.last_name || '').trim();
        const name = (d.client_name || `${firstName} ${lastName}`).trim() || `Client ${d.client_id}`;
        map.set(d.client_id, { id: d.client_id, name, firstName, lastName, ahcccs_id: d.ahcccs_id });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [clientsList, docs]);

  // Filtered canonical clients supporting first name, last name, full name, canonical client ID, case-insensitive
  const filteredCanonicalClients = useMemo(() => {
    const q = clientSearchQuery.trim().toLowerCase();
    if (!q) return allCanonicalClients;
    return allCanonicalClients.filter((c) => {
      const nameMatch = c.name.toLowerCase().includes(q);
      const idMatch = c.id.toLowerCase().includes(q);
      const firstMatch = c.firstName ? c.firstName.toLowerCase().includes(q) : false;
      const lastMatch = c.lastName ? c.lastName.toLowerCase().includes(q) : false;
      const ahcccsMatch = c.ahcccs_id ? c.ahcccs_id.toLowerCase().includes(q) : false;
      return nameMatch || idMatch || firstMatch || lastMatch || ahcccsMatch;
    });
  }, [allCanonicalClients, clientSearchQuery]);

  const activeClientRecord = useMemo(() => {
    if (selectedClientId === 'ALL' || !selectedClientId) return null;
    return allCanonicalClients.find((c) => c.id === selectedClientId) || null;
  }, [selectedClientId, allCanonicalClients]);

  const selectedClientDisplayName = activeClientRecord ? activeClientRecord.name : 'All Clients';

  // Fetch canonical provider roster from API
  useEffect(() => {
    fetch('/api/providers')
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (Array.isArray(data)) setProvidersList(data);
      })
      .catch(() => {});
  }, []);

  // Distinct canonical treating providers combining API records and active document provider relationships
  const allCanonicalProviders = useMemo(() => {
    const map = new Map<string, { id: string; name: string; role?: string; title?: string; active?: number }>();
    providersList.forEach((p: any) => {
      if (!p.id) return;
      map.set(p.id, {
        id: p.id,
        name: p.name || `Provider ${p.id}`,
        role: p.role,
        title: p.title,
        active: p.active
      });
    });
    // In case a provider exists on documents but not in active roster
    docs.forEach((d: any) => {
      const pid = d.provider_id || d.service_provider_id;
      if (pid && !map.has(pid)) {
        map.set(pid, {
          id: pid,
          name: d.provider_name || `Provider ${pid}`,
          role: d.provider_role,
          title: d.provider_title,
          active: 1
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [providersList, docs]);

  // Filtered canonical providers supporting name, title, role, id, case-insensitive
  const filteredCanonicalProviders = useMemo(() => {
    const q = providerSearchQuery.trim().toLowerCase();
    if (!q) return allCanonicalProviders;
    return allCanonicalProviders.filter((p) => {
      const nameMatch = p.name.toLowerCase().includes(q);
      const idMatch = p.id.toLowerCase().includes(q);
      const roleMatch = p.role ? p.role.toLowerCase().includes(q) : false;
      const titleMatch = p.title ? p.title.toLowerCase().includes(q) : false;
      return nameMatch || idMatch || roleMatch || titleMatch;
    });
  }, [allCanonicalProviders, providerSearchQuery]);

  const activeProviderRecord = useMemo(() => {
    if (selectedProviderId === 'ALL' || !selectedProviderId) return null;
    return allCanonicalProviders.find((p) => p.id === selectedProviderId) || null;
  }, [selectedProviderId, allCanonicalProviders]);

  const selectedProviderDisplayName = activeProviderRecord ? activeProviderRecord.name : 'All Therapists';

  // Context change effect: reset to Period Documentation Overview for Owner / RC on context change
  const prevContextRef = useRef<{ client: string; provider: string; periodKey: string }>({
    client: selectedClientId,
    provider: selectedProviderId,
    periodKey: `${periodMode}_${selectedPayPeriod.id}_${selectedWeekNumber}_${customStartDate}_${customEndDate}`
  });

  useEffect(() => {
    const currentPeriodKey = `${periodMode}_${selectedPayPeriod.id}_${selectedWeekNumber}_${customStartDate}_${customEndDate}`;
    const changed =
      prevContextRef.current.client !== selectedClientId ||
      prevContextRef.current.provider !== selectedProviderId ||
      prevContextRef.current.periodKey !== currentPeriodKey;

    if (changed) {
      prevContextRef.current = {
        client: selectedClientId,
        provider: selectedProviderId,
        periodKey: currentPeriodKey
      };
      if (isOwnerOrRc) {
        setSelectedDoc(null);
      }
    }
  }, [selectedClientId, selectedProviderId, periodMode, selectedPayPeriod, selectedWeekNumber, customStartDate, customEndDate, isOwnerOrRc]);

  const handlePrevPeriod = () => {
    if (periodMode === 'PAY_PERIOD') {
      setSelectedPayPeriod((prev) => getAdjacentPayPeriod(prev, -1));
    } else if (periodMode === 'WEEK') {
      if (selectedWeekNumber === 2) {
        setSelectedWeekNumber(1);
      } else {
        setSelectedPayPeriod((prev) => getAdjacentPayPeriod(prev, -1));
        setSelectedWeekNumber(2);
      }
    }
  };

  const handleNextPeriod = () => {
    if (periodMode === 'PAY_PERIOD') {
      setSelectedPayPeriod((prev) => getAdjacentPayPeriod(prev, 1));
    } else if (periodMode === 'WEEK') {
      if (selectedWeekNumber === 1) {
        setSelectedWeekNumber(2);
      } else {
        setSelectedPayPeriod((prev) => getAdjacentPayPeriod(prev, 1));
        setSelectedWeekNumber(1);
      }
    }
  };

  // Responsive Container Width and Compaction State (Desktop / Tablet pane-aware)
  const [detailHeaderWidth, setDetailHeaderWidth] = useState<number>(650);
  // Inline Action Swap Mode: 'PRIMARY' vs 'SECONDARY' (replaces More popup/dropdown)
  const [docActionsSecondaryMode, setDocActionsSecondaryMode] = useState<boolean>(false);
  const detailHeaderRef = useRef<HTMLDivElement>(null);

  // Mobile Top Mode Switch: 'DOCUMENTS' (fast record review) vs 'CREATE' (creation actions)
  const [mobileMode, setMobileMode] = useState<'DOCUMENTS' | 'CREATE'>('DOCUMENTS');

  // Responsive Left Column Width and Compaction State for Document Types
  const [listColWidth, setListColWidth] = useState<number>(400);
  const [showDesktopSecondaryFilters, setShowDesktopSecondaryFilters] = useState<boolean>(false);
  const listColRef = useRef<HTMLDivElement>(null);

  // ResizeObserver for list column (responds to left pane width)
  useEffect(() => {
    const el = listColRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width > 0) setListColWidth(rect.width);
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        if (width > 0) setListColWidth(width);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // ResizeObserver for detail header (responds to right pane width)
  useEffect(() => {
    const el = detailHeaderRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width > 0) setDetailHeaderWidth(rect.width);
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        if (width > 0) setDetailHeaderWidth(width);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [selectedDoc]);

  // Reset view mode and secondary actions mode on document selection change
  useEffect(() => {
    setDocActionsSecondaryMode(false);
    setSoapGuardianSignatureData('');
    setSoapGuardianDrawing(false);
    const canvas = soapGuardianCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }, [selectedDoc?.id]);

  // Mobile/PWA popstate back navigation support: returns to docs list without leaving page
  useEffect(() => {
    if (selectedDoc) {
      window.history.pushState({ clinicalDocOpen: true }, '');
      const handlePopState = () => {
        setSelectedDoc(null);
      };
      window.addEventListener('popstate', handlePopState);
      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [selectedDoc]);

  const handleLaunchCreateDoc = (type: 'EVAL' | 'POC' | 'QPR' | 'SOAP') => {
    setNewDocInitialType(type);
    setShowNewDocModal(true);
  };

  // Edit Mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editSubjective, setEditSubjective] = useState('');
  const [editObjective, setEditObjective] = useState('');
  const [editAssessment, setEditAssessment] = useState('');
  const [editPlan, setEditPlan] = useState('');
  const [editRecommendations, setEditRecommendations] = useState('');
  const [editMedicalNecessity, setEditMedicalNecessity] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [dailyNarrative, setDailyNarrative] = useState('');
  const [dailyGoals, setDailyGoals] = useState<DailyNoteGoal[]>([]);
  const [dailyObservations, setDailyObservations] = useState('');

  // Signing inputs
  const [clinicianSigName, setClinicianSigName] = useState(user.name);
  const [guardianSigName, setGuardianSigName] = useState('');
  const [guardianRelationship, setGuardianRelationship] = useState('Parent / Legal Guardian');
  const soapGuardianCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [soapGuardianSignatureData, setSoapGuardianSignatureData] = useState('');
  const [soapGuardianDrawing, setSoapGuardianDrawing] = useState(false);
  const [showFaxModal, setShowFaxModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnComments, setReturnComments] = useState('');
  const [actionInProgress, setActionInProgress] = useState(false);

  // eSign API state (SignWell - Guardian / Clinician / Representative workflows)
  const [showEsignModal, setShowEsignModal] = useState(false);
  const [esignSignerRole, setEsignSignerRole] = useState<'GUARDIAN' | 'THERAPIST' | 'OTHER'>('GUARDIAN');
  const [esignSignerName, setEsignSignerName] = useState('');
  const [esignSignerEmail, setEsignSignerEmail] = useState('');
  const [esignSubject, setEsignSubject] = useState('');
  const [esignMessage, setEsignMessage] = useState('');
  const [esignSending, setEsignSending] = useState(false);
  const [esignFeedback, setEsignFeedback] = useState<{
    success: boolean;
    status?: string;
    message: string;
    failureReason?: string;
    envelopeId?: string;
    provider?: string;
  } | null>(null);

  // Physician Certification state (Faxage Route for POC)
  const [physicianSigName, setPhysicianSigName] = useState('');
  const [physicianSigning, setPhysicianSigning] = useState(false);
  const [showDocAuditTrail, setShowDocAuditTrail] = useState(false);

  // Authenticated PDF Download state
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [recordsExporting, setRecordsExporting] = useState(false);
  const [recordsExportNote, setRecordsExportNote] = useState<string | null>(null);
  const [showRecordsExport, setShowRecordsExport] = useState(false);
  const [recordsExportScope, setRecordsExportScope] = useState<'client' | 'selected' | 'all'>('client');
  const [recordsExportClientIds, setRecordsExportClientIds] = useState<string[]>([]);
  const [includeUndetermined, setIncludeUndetermined] = useState(false);
  const bulkExportAllowed = canBulkExportRecords(user);

  useEffect(() => {
    if (!bulkExportAllowed) setShowRecordsExport(false);
  }, [bulkExportAllowed]);

  // Authenticated PDF Preview state for embedded viewer
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [teletherapySignedOriginal, setTeletherapySignedOriginal] = useState<'included' | 'unavailable' | null>(null);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewFailed, setPreviewFailed] = useState<boolean>(false);
  const [docViewMode, setDocViewMode] = useState<'default' | 'pdf'>('default');

  const formatConciseDateTime = (dateStr?: string | null): string => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ', ' +
             d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  const formatSignatureTimestamp = (value?: string | null, emptyLabel = 'Recorded'): string => {
    const raw = String(value ?? '').trim();
    if (!raw) return emptyLabel;
    return formatArizonaTimestamp(raw);
  };

  const formatDocTypeLabel = (docType?: string): string => {
    return formatClinicalDocTypeLabel(docType);
  };

  /**
   * Format dates into compact MM/DD/YY for desktop roster index rows.
   * Example: 2026-09-19 -> 09/19/26
   */
  const formatCompactDos = (rawDos?: string, rawCreated?: string): string => {
    const d = (rawDos || rawCreated || '').trim();
    if (!d) return '';
    // YYYY-MM-DD or YYYY-MM-DDTHH:MM:SS
    const match = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, yyyy, mm, dd] = match;
      return `${mm}/${dd}/${yyyy.slice(-2)}`;
    }
    // MM/DD/YYYY or M/D/YYYY
    const m2 = d.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (m2) {
      const [, mm, dd, yyyy] = m2;
      return `${mm.padStart(2, '0')}/${dd.padStart(2, '0')}/${yyyy.slice(-2)}`;
    }
    // MM/DD/YY or M/D/YY
    const m3 = d.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/);
    if (m3) {
      const [, mm, dd, yy] = m3;
      return `${mm.padStart(2, '0')}/${dd.padStart(2, '0')}/${yy}`;
    }
    return d.slice(0, 10);
  };

  /**
   * Concise, consistent user-facing clinical document display title resolver.
   * 
   * Preferred rules:
   * - EVAL -> "Speech Evaluation & Plan of Care"
   * - POC -> "Plan of Care" (or "Physician-Signed Plan of Care")
   * - QPR -> "Quarterly Progress Report" (no quarter/year, client name, or DOS)
   * - SOAP -> "Daily Speech Therapy Treatment Note", except an exact generated title on CPT 92526
   * - INTAKE -> "Intake Packet"
   * - DDD / EXTERNAL -> Preserves official form identity (e.g. "Arizona DES/DDD Pre-Service Orientation Packet")
   *   without filename/client name clutter
   * 
   * Does NOT rename canonical stored documents or database records.
   */
  const getCleanDocDisplayTitle = (doc: any): string => {
    if (!doc) return 'Clinical Document';

    const docType = doc.doc_type || '';
    const rawTitle = (doc.title || '').trim();
    const rawFileName = (doc.file_name || '').trim();
    const combined = `${rawTitle} ${rawFileName}`.toLowerCase();

    if (docType === 'FEEDING_EVAL' || docType === 'FEEDING_EVALUATION' || combined.includes('feeding evaluation')) {
      return 'Feeding Evaluation';
    }
    if (docType === 'PHYSICIAN_SIGNED_FEEDING_POC') {
      return 'Feeding CPOC';
    }
    if (docType === 'FEEDING_POC' || (combined.includes('feeding') && combined.includes('plan of care'))) {
      return 'Feeding Plan of Care';
    }
    if (
      docType === 'PHYSICIAN_SIGNED_POC' ||
      combined.includes('physician-signed cpoc') ||
      combined.includes('physician signed cpoc') ||
      combined.includes('physician-signed') ||
      combined.includes('physician signed') ||
      combined.includes('physician_signed')
    ) {
      return 'Speech CPOC';
    }

    // 1. EVAL: Speech Evaluation
    if (docType === 'EVAL' || combined.includes('evaluation') || combined.includes('eval & poc')) {
      return 'Speech Evaluation';
    }

    // 2. QPR: Quarterly Progress Report (strictly no Q1/year, no client name, no DOS)
    if (docType === 'QPR' || combined.includes('quarterly progress report') || combined.includes('qpr')) {
      return 'Quarterly Progress Report';
    }

    // 3. SOAP. Feeding display applies only to an exact generated title on CPT 92526.
    if (docType === 'SOAP' || combined.includes('treatment note') || combined.includes('soap')) {
      const feedingTitle = feedingSoapViewerTitle(rawTitle, doc);
      if (feedingTitle !== null) return feedingTitle;
      return 'Daily Speech Therapy Treatment Note';
    }

    // 4. INTAKE: Allegiant Client Intake Packet & Service Agreement
    if (
      docType === 'INTAKE_PACKET' ||
      docType === 'INTAKE' ||
      combined.includes('intake packet') ||
      combined.includes('intake-agreement') ||
      combined.includes('intake agreement') ||
      (combined.includes('.pdf') && combined.includes('intake'))
    ) {
      return 'Allegiant Client Intake Packet & Service Agreement';
    }

    // 5. POC & Physician-Signed CPOC (physician-signed handled above)
    if (docType === 'POC' || combined.includes('plan of care')) {
      return 'Speech Plan of Care';
    }

    // 6. DDD / EXTERNAL:
    // Arizona DES/DDD Pre-Service Orientation Packet (DDD-0097A)
    if (
      docType === 'DDD_PACKET' ||
      combined.includes('pre-service') ||
      combined.includes('orientation') ||
      combined.includes('ddd-0097a') ||
      combined.includes('ddd packet') ||
      (combined.includes('.pdf') && combined.includes('ddd'))
    ) {
      return 'Arizona DES/DDD Pre-Service Orientation Packet';
    }

    if (combined.includes('agreement to perform direct care') || combined.includes('direct care service')) {
      return 'Direct Care Service Agreement';
    }

    if (combined.includes('prior authorization') || combined.includes('prior auth')) {
      return 'Prior Authorization Document';
    }

    // Fallback cleanup for general external / other documents:
    // Strip file extensions (.pdf, .docx), trailing client names, and dates
    if (rawTitle) {
      let clean = rawTitle
        .replace(/\.pdf$/i, '')
        .replace(/\.docx?$/i, '')
        .replace(/[_-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      const fName = (doc.first_name || '').trim();
      const lName = (doc.last_name || '').trim();
      if (fName && lName) {
        const namePattern1 = new RegExp(`\\s*[—–-]\\s*${fName}\\s+${lName}.*$`, 'i');
        const namePattern2 = new RegExp(`\\s*[—–-]\\s*${lName},?\\s+${fName}.*$`, 'i');
        clean = clean.replace(namePattern1, '').replace(namePattern2, '').trim();
      } else if (lName) {
        const namePattern = new RegExp(`\\s*[—–-]\\s*${lName}.*$`, 'i');
        clean = clean.replace(namePattern, '').trim();
      }

      clean = clean.replace(/\s*\(\d{4}-\d{2}-\d{2}\)\s*$/, '').replace(/\s*[—–-]\s*\d{4}-\d{2}-\d{2}\s*$/, '').trim();
      if (clean) return clean;
    }

    if (rawFileName) {
      let cleanFile = rawFileName
        .replace(/\.pdf$/i, '')
        .replace(/\.docx?$/i, '')
        .replace(/[_-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      if (cleanFile) return cleanFile;
    }

    return formatDocTypeLabel(docType) || 'Clinical Document';
  };

  const getCompactDocDisplay = (doc: any) => {
    return {
      displayTitle: getCleanDocDisplayTitle(doc),
      showClientSubtitle: true,
    };
  };

  /**
   * Cleans official document title for display by removing trailing or duplicate dates
   * (e.g. " - 2026-09-18", " - 09/18/2026") so DOS is not duplicated.
   * Display formatting only — does not mutate stored document data.
   */
  const cleanOfficialTitle = (rawTitle?: string, fallbackTitle?: string, dos?: string): string => {
    let base = (rawTitle || fallbackTitle || 'Clinical Document').trim();
    if (dos && dos !== 'N/A') {
      const cleanDos = dos.trim();
      const escaped = cleanDos.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      base = base.replace(new RegExp(`\\s*[-–—]?\\s*\\(?${escaped}\\)?\\s*`, 'gi'), ' ').trim();
    }
    const stripped = base
      .replace(/\s*[-–—]?\s*\(?\d{4}[-/.]\d{1,2}[-/.]\d{1,2}\)?\s*$/i, '')
      .replace(/\s*[-–—]?\s*\(?\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4}\)?\s*$/i, '')
      .replace(/\s*[-–—]?\s*\(?\d{4}[-/.]\d{1,2}[-/.]\d{1,2}\)?\s*/gi, ' ')
      .replace(/\s*[-–—]?\s*\(?\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4}\)?\s*/gi, ' ')
      .replace(/\s{2,}/g, ' ')
      .trim();
    return stripped || base;
  };

  const resolveClinicianName = (doc?: any) => {
    if (doc?.provider_name && !/admin|administrator/i.test(doc.provider_name)) {
      return doc.provider_name;
    }
    if (user.name && !/admin|administrator/i.test(user.name)) {
      return user.name;
    }
    return 'Justin Segura, MS, CCC-SLP';
  };

  const handleOpenEsign = () => {
    if (!selectedDoc) return;
    if (selectedDoc.doc_type === 'SOAP' && !soapGuardianEsignAccess(selectedDoc).allowed) {
      alert('SignWell SOAP dispatch is unavailable. Routine treatment SOAPs require point-of-care signature in Mobile SOAP. Manual SOAP exception approval is required.');
      return;
    }
    if (isAuthoredPlanDoc(selectedDoc.doc_type) || selectedDoc.doc_type === 'QPR') {
      alert(`Guardian SignWell is not required for ${selectedDoc.doc_type}.`);
      return;
    }
    setEsignFeedback(null);
    setEsignSignerRole('GUARDIAN');
    const clientName = `${selectedDoc.first_name} ${selectedDoc.last_name}`;
    const canonicalGuardianName = selectedDoc.client_guardian_name || selectedDoc.guardian_name || selectedDoc.guardian_signature || '';
    const canonicalGuardianEmail = selectedDoc.client_guardian_email || selectedDoc.guardian_email || '';
    const isIntake = selectedDoc.doc_type === 'INTAKE_PACKET' || selectedDoc.doc_type === 'DDD_PACKET';

    setEsignSignerName(canonicalGuardianName);
    setEsignSignerEmail(canonicalGuardianEmail);

    if (isIntake) {
      setEsignSubject(`Action Required: Electronic Signature for ${formatDocTypeLabel(selectedDoc.doc_type)} - ${clientName}`);
      setEsignMessage(`Dear Parent / Guardian,\n\nPlease review and electronically sign the completed ${formatDocTypeLabel(selectedDoc.doc_type)} (${selectedDoc.title || 'Plan of Care'}) for ${clientName}.\n\nThank you,\nAllegiant Therapy\ninfo@allegianttherapy.com`);
    } else if (selectedDoc.doc_type === 'SOAP') {
      setEsignSubject('Action Required: Guardian acknowledgment is due 24 hours after the session end');
      setEsignMessage(`Dear Parent / Legal Guardian,\n\n${soapSessionRemoteMessage({ date: selectedDoc.dos, timeIn: selectedDoc.start_time, timeOut: selectedDoc.end_time })}\n\n${guardianRemoteDeadlineSentence({ date: selectedDoc.dos, timeOut: selectedDoc.end_time })}\n\nThank you,\nAllegiant Therapy\ninfo@allegianttherapy.com`);
    } else {
      // Clinical document strictly uses no-PHI subject and explains 24h requirement
      setEsignSubject('Action Required: Guardian acknowledgment is due 24 hours after the session end');
      setEsignMessage(`Dear Parent / Legal Guardian,\n\nPlease review and electronically sign this clinical document for Allegiant Therapy. ${guardianRemoteDeadlineSentence({ date: selectedDoc.dos, timeOut: selectedDoc.end_time })}\n\nThank you,\nAllegiant Therapy\ninfo@allegianttherapy.com`);
    }
    setShowEsignModal(true);
  };

  const handleSyncEsign = async (docId: string) => {
    try {
      setActionInProgress(true);
      const res = await fetch(`/api/communications/esign/sync/${docId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        await fetchDocs();
        onRefreshStats();
      }
    } catch (err) {
      console.error('Failed to sync eSign status:', err);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleEsignRoleChange = (role: 'GUARDIAN' | 'THERAPIST' | 'OTHER') => {
    setEsignSignerRole(role);
    if (!selectedDoc) return;
    const clientName = `${selectedDoc.first_name} ${selectedDoc.last_name}`;
    const isIntake = selectedDoc.doc_type === 'INTAKE_PACKET' || selectedDoc.doc_type === 'DDD_PACKET';

    if (role === 'GUARDIAN') {
      const canonicalGuardianName = selectedDoc.client_guardian_name || selectedDoc.guardian_name || selectedDoc.guardian_signature || '';
      const canonicalGuardianEmail = selectedDoc.client_guardian_email || selectedDoc.guardian_email || '';
      setEsignSignerName(canonicalGuardianName);
      setEsignSignerEmail(canonicalGuardianEmail);
      if (isIntake) {
        setEsignSubject(`Action Required: Electronic Signature for ${formatDocTypeLabel(selectedDoc.doc_type)} - ${clientName}`);
        setEsignMessage(`Dear Parent / Guardian,\n\nPlease review and electronically sign the completed ${formatDocTypeLabel(selectedDoc.doc_type)} (${selectedDoc.title || 'Plan of Care'}) for ${clientName}.\n\nThank you,\nAllegiant Therapy\ninfo@allegianttherapy.com`);
      } else if (selectedDoc.doc_type === 'SOAP') {
        setEsignSubject('Action Required: Guardian acknowledgment is due 24 hours after the session end');
        setEsignMessage(`Dear Parent / Legal Guardian,\n\n${soapSessionRemoteMessage({ date: selectedDoc.dos, timeIn: selectedDoc.start_time, timeOut: selectedDoc.end_time })}\n\n${guardianRemoteDeadlineSentence({ date: selectedDoc.dos, timeOut: selectedDoc.end_time })}\n\nThank you,\nAllegiant Therapy\ninfo@allegianttherapy.com`);
      } else {
        setEsignSubject('Action Required: Guardian acknowledgment is due 24 hours after the session end');
        setEsignMessage(`Dear Parent / Legal Guardian,\n\nPlease review and electronically sign this clinical document for Allegiant Therapy. ${guardianRemoteDeadlineSentence({ date: selectedDoc.dos, timeOut: selectedDoc.end_time })}\n\nThank you,\nAllegiant Therapy\ninfo@allegianttherapy.com`);
      }
    } else if (role === 'THERAPIST') {
      const clinicianName = resolveClinicianName(selectedDoc);
      setEsignSignerName(clinicianName);
      setEsignSignerEmail(user.email || '');
      setEsignSubject(`Clinician Electronic Signature Required: ${formatDocTypeLabel(selectedDoc.doc_type)} - ${clientName}`);
      setEsignMessage(`Attention Clinician,\n\nPlease review and electronically sign your clinical documentation for ${clientName}.\n\nAllegiant Therapy Clinical Review`);
    } else {
      setEsignSignerName('');
      setEsignSignerEmail('');
      setEsignSubject(`Signature Request: ${formatDocTypeLabel(selectedDoc.doc_type)} - ${clientName}`);
      setEsignMessage(`Please review and electronically sign this clinical document for ${clientName}.\n\nThank you,\nAllegiant Therapy`);
    }
  };

  const handleRecordPhysicianSignature = async (docId: string) => {
    if (!canManageClientOperations(user.role) || !selectedDoc) return;
    try {
      setPhysicianSigning(true);
      const name = physicianSigName.trim() || selectedDoc.physician_name || 'Ordering Physician (Signed via Fax)';
      const res = await fetch(`/api/clinical/docs/${docId}/sign-physician`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ physicianName: name })
      });
      if (res.ok) {
        setPhysicianSigName('');
        await fetchDocs();
      }
    } catch (err) {
      console.error('Error recording physician signature:', err);
    } finally {
      setPhysicianSigning(false);
    }
  };

  const handleSendEsign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDoc || !esignSignerName.trim() || !esignSignerEmail.trim()) return;

    try {
      setEsignSending(true);
      setEsignFeedback(null);

      const result = await mintedGuardianSend({
        clientId: selectedDoc.client_id,
        documentId: selectedDoc.id,
        recipientName: esignSignerName.trim(),
        recipientEmail: esignSignerEmail.trim(),
        sendUrl: '/api/communications/esign/send',
        sendBody: (intentId) => ({
          intentId,
          document_id: selectedDoc.id,
          client_id: selectedDoc.client_id,
          signer_name: esignSignerName.trim(),
          signer_email: esignSignerEmail.trim(),
          signer_role: esignSignerRole,
          subject: esignSubject,
          message: esignMessage
        })
      });
      if (result.stage === 'intent') {
        throw new Error(result.error);
      }
      const data = result.data;
      if (result.presentedAsSuccess) {
        setEsignFeedback({
          success: true,
          status: data.status || 'SENT',
          message: 'Document successfully dispatched via SignWell. Status is now Sent for Guardian Signature (Waiting on Signer).',
          envelopeId: data.envelopeId,
          provider: data.provider
        });
        await fetchDocs();
        onRefreshStats();
      } else {
        setEsignFeedback({
          success: false,
          status: 'FAILED',
          message: data.failureReason || data.error || 'eSign gateway blocked dispatch.',
          failureReason: data.failureReason,
          provider: data.provider
        });
        await fetchDocs();
      }
    } catch (err: any) {
      setEsignFeedback({
        success: false,
        status: 'FAILED',
        message: err.message || 'Network error requesting eSign dispatch.'
      });
    } finally {
      setEsignSending(false);
    }
  };

  const handleExportDocument = () => {
    if (!selectedDoc) return;
    const content = `ALLEGIANT THERAPY PARTNERS - CLINICAL DOCUMENTATION
=====================================================
Document ID: ${selectedDoc.id}
Document Type: ${selectedDoc.doc_type}
Title: ${selectedDoc.title || `${selectedDoc.doc_type} Documentation`}
Date of Service (DOS): ${selectedDoc.dos || selectedDoc.created_at?.slice(0, 10)}
Client: ${selectedDoc.first_name} ${selectedDoc.last_name} (ID: ${selectedDoc.client_id})
Treating Provider: ${selectedDoc.provider_name}

CLINICAL SECTIONS
-----------------------------------------------------
${isAuthoredEvalDoc(selectedDoc.doc_type) ? 'CASE HISTORY & BACKGROUND' : 'SUBJECTIVE'}:
${selectedDoc.subjective || 'N/A'}

${isAuthoredPlanDoc(selectedDoc.doc_type) ? 'TREATMENT GOALS & MEASURABLE OBJECTIVES' : selectedDoc.doc_type === 'QPR' ? 'GOAL PERFORMANCE DATA' : 'OBJECTIVE'}:
${selectedDoc.objective || 'N/A'}

ASSESSMENT & CLINICAL IMPRESSIONS:
${selectedDoc.assessment || 'N/A'}

PLAN & RECOMMENDATIONS:
${selectedDoc.plan || 'N/A'}

SIGNATURES & VERIFICATION
-----------------------------------------------------
Clinician Signature: ${selectedDoc.clinician_signature ? `${selectedDoc.clinician_signature} [Signed: ${formatSignatureTimestamp(selectedDoc.clinician_signed_at)}]` : 'PENDING'}
Guardian Signature Status: ${selectedDoc.guardian_signature ? `Signed by ${selectedDoc.guardian_signature}` : selectedDoc.guardian_signature_status || 'PENDING'}
`;
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedDoc.doc_type}_${selectedDoc.last_name}_${selectedDoc.id}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrintDocument = () => {
    window.print();
  };

  const openDocument = async (id?: string | null, listHint?: ClinicalDocument | null) => {
    if (!id) {
      setSelectedDoc(null);
      return;
    }
    openingDocIdRef.current = id;
    try {
      const res = await fetch(`/api/clinical/docs/${id}`);
      if (!res.ok) return;
      const full = await res.json();
      if (openingDocIdRef.current !== id) return;
      const listRow = listHint || docsRef.current.find((d: any) => d.id === id);
      setSelectedDoc(listRow ? { ...listRow, ...full } : full);
    } catch (err) {
      console.error('Error fetching clinical document detail:', err);
    }
  };

  const fetchDocs = async (selectId?: string) => {
    try {
      setLoading(true);
      const res = await fetch('/api/clinical/docs');
      if (res.ok) {
        const data = await res.json();
        setDocs(data);
        docsRef.current = data;
        const targetId = selectId || initialSelectedDocId;
        if (targetId) {
          const target = data.find((d: any) => d.id === targetId);
          if (target) {
            if (target.client_id && selectedClientId !== 'ALL' && selectedClientId !== target.client_id) {
              setSelectedClientId(target.client_id);
            }
            const targetDate = getCanonicalDocumentDate(target);
            if (targetDate && resolvedPeriod.startDate && (targetDate < resolvedPeriod.startDate || (resolvedPeriod.endDate && targetDate > resolvedPeriod.endDate))) {
              setPeriodMode('ALL_TIME');
            }
            await openDocument(target.id, target);
            onConsumeInitialDocId?.();
            return;
          }
        }
        if (!isOwnerOrRc) {
          if (data.length > 0 && !selectedDoc) {
            await openDocument(data[0].id, data[0]);
          } else if (selectedDoc) {
            const updated = data.find((d: any) => d.id === selectedDoc.id);
            if (updated) await openDocument(updated.id, updated);
          }
        } else if (selectedDoc) {
          const updated = data.find((d: any) => d.id === selectedDoc.id);
          if (updated) await openDocument(updated.id, updated);
        }
      }
    } catch (err) {
      console.error('Error fetching clinical docs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocs(initialSelectedDocId || undefined);
  }, [initialSelectedDocId]);

  useEffect(() => {
    if (selectedDoc) {
      setEditSubjective(selectedDoc.subjective || '');
      setEditObjective(selectedDoc.objective || '');
      setEditAssessment(selectedDoc.assessment || '');
      setEditPlan(selectedDoc.plan || '');
      setEditRecommendations(selectedDoc.recommendations || '');
      setEditMedicalNecessity(selectedDoc.medical_necessity || '');
      if (selectedDoc.doc_type === 'SOAP') {
        const note = mapDailyTreatmentNote(selectedDoc);
        setDailyNarrative(note.narrative === DAILY_NOTE_NOT_STORED ? '' : note.narrative);
        setDailyGoals(note.goals);
        setDailyObservations(note.observations.join('\n\n'));
      }
      setIsEditing(false);

      // Clinician signature attribution: ensure authentic licensed clinician name
      const clinicianName = resolveClinicianName(selectedDoc);
      setClinicianSigName(clinicianName);

      // Canonical guardian details: document signature first, then client-chart aliases
      if (selectedDoc.guardian_signature) {
        setGuardianSigName(selectedDoc.guardian_signature);
      } else if (selectedDoc.client_guardian_name || selectedDoc.guardian_name) {
        setGuardianSigName(selectedDoc.client_guardian_name || selectedDoc.guardian_name);
      }
      if (selectedDoc.guardian_relationship) {
        setGuardianRelationship(selectedDoc.guardian_relationship);
      } else if (selectedDoc.client_guardian_relationship) {
        setGuardianRelationship(selectedDoc.client_guardian_relationship);
      }
    }
  }, [selectedDoc]);

  const handleSaveEdits = async () => {
    if (!selectedDoc) return;
    try {
      setSavingEdit(true);
      const res = await fetch(`/api/clinical/docs/${selectedDoc.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(selectedDoc.doc_type === 'SOAP'
          ? dailyNoteDraftUpdate(selectedDoc, {
            narrative: dailyNarrative,
            goals: dailyGoals,
            observations: dailyObservations
          })
          : {
            subjective: editSubjective,
            objective: editObjective,
            assessment: editAssessment,
            plan: editPlan,
            recommendations: editRecommendations,
            medical_necessity: editMedicalNecessity
          })
      });

      if (res.ok) {
        setIsEditing(false);
        await fetchDocs();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'The note was not saved.');
      }
    } catch (err) {
      console.error('Save doc edits failed:', err);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleSignClinician = async (docId: string) => {
    try {
      let nameToUse = clinicianSigName.trim();
      if (!nameToUse || /admin|administrator/i.test(nameToUse)) {
        nameToUse = resolveClinicianName(selectedDoc);
      }
      const res = await fetch(`/api/clinical/docs/${docId}/sign-clinician`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signerName: nameToUse })
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        await fetchDocs();
        onRefreshStats();
        if (data.payrollAssignmentMessage) alert(data.payrollAssignmentMessage);
      } else {
        const errData = await res.json().catch(() => ({}));
        alert(errData.error || 'Failed to sign clinical document as clinician.');
      }
    } catch (err) {
      console.error('Clinician sign failed:', err);
    }
  };

  const handleSignGuardian = async (docId: string, waived = false) => {
    try {
      const isSoap = selectedDoc?.id === docId && selectedDoc.doc_type === 'SOAP';
      const isInPersonEval = selectedDoc?.id === docId && isAuthoredEvalDoc(selectedDoc.doc_type) && !waived;
      if (isSoap && waived) {
        return;
      }
      if ((isSoap || isInPersonEval) && (!guardianSigName.trim() || !guardianRelationship.trim() || !soapGuardianSignatureData)) {
        alert(isInPersonEval
          ? 'Guardian name, relationship, and signature are required before this evaluation can be signed.'
          : 'Guardian/designee signature is required before this session note can be submitted.');
        return;
      }
      const res = await fetch(`/api/clinical/docs/${docId}/sign-guardian`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify((isSoap || isInPersonEval)
          ? {
              guardianName: guardianSigName.trim(),
              relationship: guardianRelationship.trim(),
              signatureEvidence: soapGuardianSignatureData
            }
          : {
              guardianName: guardianSigName || 'Parent / Legal Representative',
              relationship: guardianRelationship,
              waived
            })
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        setSoapGuardianSignatureData('');
        await fetchDocs();
        onRefreshStats();
        if (data.payrollAssignmentMessage) alert(data.payrollAssignmentMessage);
      } else if (isSoap || isInPersonEval) {
        const errData = await res.json().catch(() => ({}));
        alert(errData.error || (isInPersonEval
          ? 'Guardian name, relationship, and signature are required before this evaluation can be signed.'
          : 'Guardian/designee signature is required before this session note can be submitted.'));
      }
    } catch (err) {
      console.error('Guardian sign failed:', err);
    }
  };

  const startSoapGuardianDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = soapGuardianCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const { x, y } = signatureCanvasPoint(canvas, e);
    setSoapGuardianDrawing(true);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const drawSoapGuardianSignature = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!soapGuardianDrawing) return;
    const canvas = soapGuardianCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const { x, y } = signatureCanvasPoint(canvas, e);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0f172a';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopSoapGuardianDrawing = () => {
    if (!soapGuardianDrawing) return;
    setSoapGuardianDrawing(false);
    const canvas = soapGuardianCanvasRef.current;
    if (canvas) {
      setSoapGuardianSignatureData(canvas.toDataURL());
    }
  };

  const clearSoapGuardianPad = () => {
    const canvas = soapGuardianCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    setSoapGuardianSignatureData('');
  };

  const handleSignSupervisor = async (docId: string, action: 'APPROVE' | 'RETURN' = 'APPROVE', comments = '') => {
    try {
      setActionInProgress(true);
      const res = await fetch(`/api/clinical/docs/${docId}/sign-supervisor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action,
          comments,
          supervisorName: user.name
        })
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (action === 'APPROVE') {
          playPulseSeal();
        }
        setShowReturnModal(false);
        setReturnComments('');
        await fetchDocs();
        onRefreshStats();
        if (data.payrollAssignmentMessage) alert(data.payrollAssignmentMessage);
      } else {
        const data = await res.json();
        alert(data.error || 'Supervisor action failed');
      }
    } catch (err) {
      console.error('Supervisor sign failed:', err);
    } finally {
      setActionInProgress(false);
    }
  };

  const isUploadedOrExternal = Boolean(
    selectedDoc && (
      selectedDoc.doc_type === 'EXTERNAL' ||
      selectedDoc.doc_type === 'PHYSICIAN_SIGNED_POC' ||
      selectedDoc.doc_type === 'PHYSICIAN_SIGNED_FEEDING_POC' ||
      selectedDoc.doc_type === 'OTHER' ||
      selectedDoc.doc_type === 'INTAKE_PACKET' ||
      selectedDoc.doc_type === 'DDD_PACKET' ||
      selectedDoc.source === 'MANUAL_UPLOAD' ||
      selectedDoc.source === 'INBOUND_FAX' ||
      Boolean(selectedDoc.is_file_only_document) ||
      (selectedDoc.file_name && !selectedDoc.subjective && !selectedDoc.objective)
    )
  );

  const isPdfViewerActive = Boolean(
    selectedDoc && (isUploadedOrExternal || docViewMode === 'pdf')
  );

  const isSlpaDoc = Boolean(
    selectedDoc && (
      selectedDoc.provider_role === 'SLPA' ||
      (selectedDoc.provider_title && selectedDoc.provider_title.includes('SLPA'))
    )
  );

  const getDocDownloadFileName = (doc: any): string => {
    if (!doc) return 'document.pdf';
    const clientName = `${doc.first_name || ''}_${doc.last_name || 'Client'}`.trim().replace(/[^a-zA-Z0-9_-]/g, '_');
    if (doc.doc_type === 'INTAKE_PACKET' || doc.doc_type === 'INTAKE') {
      return `Allegiant_Intake_Agreement_${clientName}.pdf`;
    }
    if (doc.doc_type === 'DDD_PACKET') {
      return `Arizona_DES_DDD_Pre_Service_Orientation_${clientName}.pdf`;
    }
    return doc.file_name || `${doc.doc_type || 'DOC'}_${(doc.last_name || 'Client').replace(/[^a-zA-Z0-9_-]/g, '_')}_${doc.id}.pdf`;
  };

  // Reset view mode on document selection change
  useEffect(() => {
    setDocViewMode('default');
  }, [selectedDoc?.id]);

  // Pre-load authenticated preview blob when an external/uploaded/packet document is opened or PDF mode is active
  useEffect(() => {
    let active = true;
    let currentObjectUrl: string | null = null;

    if (!selectedDoc || !isPdfViewerActive) {
      setPreviewBlobUrl(null);
      setPreviewLoading(false);
      setPreviewError(null);
      setPreviewFailed(false);
      return;
    }

    const loadPreview = async () => {
      setPreviewBlobUrl(null);
      setTeletherapySignedOriginal(null);
      setPreviewLoading(true);
      setPreviewError(null);
      setPreviewFailed(false);
      try {
        const token = (() => {
          try {
            return localStorage.getItem('allegiant_session_token') || sessionStorage.getItem('allegiant_session_token');
          } catch {
            return null;
          }
        })();

        const headers: Record<string, string> = {
          'Accept': 'application/pdf, application/json'
        };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
          headers['X-Session-Token'] = token;
        }

        const res = await fetch(`/api/clinical/docs/${selectedDoc.id}/pdf`, {
          method: 'GET',
          headers,
          credentials: 'include'
        });

        if (!res.ok) {
          let errMsg = `Document preview request returned status ${res.status}`;
          try {
            const errData = await res.json();
            if (errData?.error) errMsg = errData.error;
          } catch {}
          throw new Error(errMsg);
        }

        const contentType = res.headers.get('content-type') || '';
        if (!contentType.includes('application/pdf')) {
          let errMsg = `Expected application/pdf but received ${contentType}`;
          try {
            const errData = await res.json();
            if (errData?.error) errMsg = errData.error;
          } catch {}
          throw new Error(errMsg);
        }

        const signedOriginal = res.headers.get('X-Teletherapy-Signed-Original');
        const blob = await res.blob();
        if (!active) return;
        setTeletherapySignedOriginal(signedOriginal === 'included' || signedOriginal === 'unavailable' ? signedOriginal : null);
        const pdfBlob = new Blob([blob], { type: 'application/pdf' });
        currentObjectUrl = URL.createObjectURL(pdfBlob);
        setPreviewBlobUrl(currentObjectUrl);
      } catch (err: any) {
        if (!active) return;
        console.error('Error loading clinical doc preview:', err);
        setPreviewError(err?.message || 'Failed to load document preview');
      } finally {
        if (active) setPreviewLoading(false);
      }
    };

    loadPreview();

    return () => {
      active = false;
      if (currentObjectUrl) {
        URL.revokeObjectURL(currentObjectUrl);
      }
    };
  }, [
    selectedDoc?.id,
    selectedDoc?.status,
    selectedDoc?.updated_at,
    selectedDoc?.clinician_signed_at,
    selectedDoc?.guardian_signed_at,
    selectedDoc?.supervisor_signed_at,
    isPdfViewerActive
  ]);

  // Authenticated PDF Download via Pulse API session
  const handleDownloadPdf = async (doc: ClinicalDocument, openInNewTab = false) => {
    if (downloadingPdf) return;
    setDownloadingPdf(true);
    setPdfError(null);

    try {
      // Re-use authoritative Pulse session token mechanism
      const token = (() => {
        try {
          return localStorage.getItem('allegiant_session_token') || sessionStorage.getItem('allegiant_session_token');
        } catch {
          return null;
        }
      })();

      const headers: Record<string, string> = {
        'Accept': 'application/pdf, application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        headers['X-Session-Token'] = token;
      }

      // Relative same-origin API request (resolves to https://emr.allegianttherapy.com in production)
      const res = await fetch(`/api/clinical/docs/${doc.id}/pdf`, {
        method: 'GET',
        headers,
        credentials: 'include'
      });

      if (!res.ok) {
        let errMsg = `Document PDF request returned status ${res.status}`;
        try {
          const errData = await res.json();
          if (errData?.error) errMsg = errData.error;
        } catch {
          // not JSON
        }
        setPdfError(errMsg);
        return;
      }

      const blob = await res.blob();
      const pdfBlob = new Blob([blob], { type: 'application/pdf' });
      const objectUrl = URL.createObjectURL(pdfBlob);
      const filename = getDocDownloadFileName(doc);

      if (openInNewTab) {
        const opened = window.open(objectUrl, '_blank');
        if (!opened) {
          const tempLink = document.createElement('a');
          tempLink.href = objectUrl;
          tempLink.download = filename;
          document.body.appendChild(tempLink);
          tempLink.click();
          document.body.removeChild(tempLink);
        }
      } else {
        const tempLink = document.createElement('a');
        tempLink.href = objectUrl;
        tempLink.download = filename;
        document.body.appendChild(tempLink);
        tempLink.click();
        document.body.removeChild(tempLink);
      }

      // Revoke temporary browser object URL after use
      setTimeout(() => {
        URL.revokeObjectURL(objectUrl);
      }, 30000);
    } catch (err: any) {
      console.error('Error downloading clinical doc PDF:', err);
      setPdfError(err?.message || 'Failed to download clinical document PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const openRecordsExport = () => {
    setShowRecordsExport((open) => !open);
    setRecordsExportClientIds((current) => {
      if (current.length || selectedClientId === 'ALL') return current;
      return [selectedClientId];
    });
  };

  const handleRecordsExport = async () => {
    if (recordsExporting) return;
    const clientIds = recordsExportScope === 'client'
      ? (selectedClientId === 'ALL' ? [] : [selectedClientId])
      : recordsExportScope === 'selected'
        ? recordsExportClientIds
        : [];
    if (recordsExportScope !== 'all' && clientIds.length === 0) {
      setRecordsExportNote('Select one client, choose clients, or export everyone.');
      return;
    }
    setRecordsExporting(true);
    setRecordsExportNote(null);
    try {
      const token = (() => {
        try {
          return localStorage.getItem('allegiant_session_token') || sessionStorage.getItem('allegiant_session_token');
        } catch {
          return null;
        }
      })();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/zip, application/json'
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
        headers['X-Session-Token'] = token;
      }
      const res = await fetch('/api/clinical/records-export', {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          scope: recordsExportScope,
          clientIds,
          startDate: resolvedPeriod.startDate || '',
          endDate: resolvedPeriod.endDate || '',
          includeUndetermined
        })
      });
      if (!res.ok) {
        let errMsg = `Records export returned status ${res.status}`;
        try {
          const errData = await res.json();
          if (errData?.error) errMsg = errData.error;
        } catch {
          // response was not JSON
        }
        setRecordsExportNote(errMsg);
        return;
      }
      const included = Number(res.headers.get('X-Records-Export-Included') || 0);
      const notExported = Number(res.headers.get('X-Records-Export-Not-Exported') || 0);
      const exportedWithIssues = Number(res.headers.get('X-Records-Export-With-Issues') || 0);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      const disposition = res.headers.get('Content-Disposition') || '';
      const named = disposition.match(/filename="([^"]+)"/);
      link.download = named?.[1] || 'records_export.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
      setRecordsExportNote(recordsExportCompletionNote(included, notExported, exportedWithIssues));
    } catch (err: any) {
      setRecordsExportNote(err?.message || 'Records export failed.');
    } finally {
      setRecordsExporting(false);
    }
  };

  const isFinalizedOrCleared = Boolean(
    selectedDoc && (
      selectedDoc.status === 'FINALIZED' ||
      selectedDoc.status === 'COMPLETED' ||
      (selectedDoc as any).is_cleared ||
      (selectedDoc as any).clearance_status === 'CLEARED' ||
      (selectedDoc.clinician_signature && (
        selectedDoc.provider_role !== 'SLPA' ||
        selectedDoc.supervisor_approval_status === 'APPROVED'
      ))
    )
  );

  const evalGuardianMetadataSuggestsCompletion = Boolean(
    selectedDoc && (
      String(selectedDoc.guardian_signature || '').trim() ||
      String(selectedDoc.guardian_signature_status || '').toUpperCase() === 'SIGNED' ||
      String(selectedDoc.esign_status || '').toUpperCase() === 'COMPLETED'
    )
  );
  const soapGuardianEvidenceIncomplete = Boolean(
    selectedDoc &&
    (selectedDoc as any).guardian_evidence_verified !== true &&
    (
      (selectedDoc.doc_type === 'SOAP' && selectedDoc.guardian_signature) ||
      (isAuthoredEvalDoc(selectedDoc.doc_type) && evalGuardianMetadataSuggestsCompletion)
    )
  );

  const isSupervisorReview = Boolean(
    selectedDoc &&
    (user.role === 'SUPERVISOR' || user.role === 'ADMIN') &&
    isSlpaDoc &&
    selectedDoc.supervisor_approval_status !== 'APPROVED'
  );

  const isOperationalUser = user.role === 'OWNER' || user.role === 'ADMIN' || user.role === 'BILLING' || user.role === 'OFFICE' || user.role === 'REFERRAL_COORDINATOR';

  const userProviderId = user.providerId || (user as any).provider_id;
  const isAuthor = !selectedDoc || !selectedDoc.provider_id || !userProviderId || selectedDoc.provider_id === userProviderId;
  const isAssistantUser = user.role === 'ASSISTANT' || (user as any).provider_role === 'SLPA';
  const isSlpaProhibitedDoc = Boolean(selectedDoc && isAssistantUser && (isAuthoredEvalDoc(selectedDoc.doc_type) || isAuthoredPlanDoc(selectedDoc.doc_type)));

  const canAuthorClinicalDocs = user.role === 'OWNER' || user.role === 'ADMIN' || user.role === 'SUPERVISOR' || user.role === 'ASSISTANT';
  const canCreateEval = canAuthorClinicalDocs && !isAssistantUser;
  const canCreatePoc = canAuthorClinicalDocs && !isAssistantUser;
  const canCreateQpr = canAuthorClinicalDocs;

  const canEditDraft = Boolean(
    selectedDoc &&
    !isFinalizedOrCleared &&
    !isUploadedOrExternal &&
    !isSupervisorReview &&
    !isSlpaProhibitedDoc &&
    (
      user.role === 'OWNER' ||
      user.role === 'ADMIN' ||
      ((user.role === 'SUPERVISOR' || user.role === 'ASSISTANT') && isAuthor)
    )
  );

  const canFaxClient = canManageClientOperations(user.role);
  const canOperationalFax = Boolean(canFaxClient && isFinalizedOrCleared);

  const filteredDocs = useMemo(() => {
    return docs.filter((d: any) => {
      // 1. Client filter (WHO)
      if (selectedClientId && selectedClientId !== 'ALL') {
        if (d.client_id !== selectedClientId) return false;
      }

      // 2. Therapist filter (WHO - Canonical Provider)
      if (selectedProviderId && selectedProviderId !== 'ALL') {
        const docProviderId = d.provider_id || d.service_provider_id;
        if (docProviderId !== selectedProviderId) return false;
      }

      // 3. Period filter (WHEN)
      if (resolvedPeriod.startDate && resolvedPeriod.endDate) {
        const docDate = getCanonicalDocumentDate(d);
        if (!docDate || docDate < resolvedPeriod.startDate || docDate > resolvedPeriod.endDate) {
          return false;
        }
      } else if (resolvedPeriod.startDate) {
        const docDate = getCanonicalDocumentDate(d);
        if (!docDate || docDate < resolvedPeriod.startDate) return false;
      } else if (resolvedPeriod.endDate) {
        const docDate = getCanonicalDocumentDate(d);
        if (!docDate || docDate > resolvedPeriod.endDate) return false;
      }

      // 4. Document Type filter (WHAT TYPE)
      const isIntake = d.doc_type === 'INTAKE_PACKET' || d.doc_type === 'DDD_PACKET' || d.doc_type === 'INTAKE';
      const isExt = !isIntake && (d.doc_type === 'EXTERNAL' || d.doc_type === 'PHYSICIAN_SIGNED_POC' || d.doc_type === 'PHYSICIAN_SIGNED_FEEDING_POC' || d.doc_type === 'OTHER' || d.source === 'MANUAL_UPLOAD' || d.source === 'INBOUND_FAX' || Boolean(d.is_file_only_document));
      const matchesType = docTypeFilter === 'ALL'
        ? true
        : docTypeFilter === 'INTAKE'
        ? isIntake
        : docTypeFilter === 'EXTERNAL'
        ? isExt
        : docTypeFilter === 'POC'
        ? (d.doc_type === 'POC' || d.doc_type === 'FEEDING_POC' || d.doc_type === 'PHYSICIAN_SIGNED_POC' || d.doc_type === 'PHYSICIAN_SIGNED_FEEDING_POC') && !isIntake
        : docTypeFilter === 'EVAL'
        ? (d.doc_type === 'EVAL' || d.doc_type === 'FEEDING_EVAL' || d.doc_type === 'FEEDING_EVALUATION')
        : d.doc_type === docTypeFilter;

      if (!matchesType) return false;

      // 5. Search query filter (WHICH DOCUMENT)
      if (search.trim()) {
        const cleanTitle = getCleanDocDisplayTitle(d);
        const matchesSearch = `${d.id || ''} ${d.client_id || ''} ${d.first_name || ''} ${d.last_name || ''} ${d.client_name || ''} ${d.doc_type || ''} ${d.provider_name || ''} ${d.dos || ''} ${d.document_date || ''} ${d.title || ''} ${d.file_name || ''} ${cleanTitle}`
          .toLowerCase()
          .includes(search.toLowerCase());
        if (!matchesSearch) return false;
      }

      return true;
    }).sort((a: any, b: any) => {
      const dateA = getCanonicalDocumentDate(a);
      const dateB = getCanonicalDocumentDate(b);
      if (dateA !== dateB) {
        return dateB.localeCompare(dateA);
      }
      const createdA = a.created_at || '';
      const createdB = b.created_at || '';
      return createdB.localeCompare(createdA);
    });
  }, [docs, selectedClientId, selectedProviderId, resolvedPeriod, docTypeFilter, search]);

  // Keep selectedDoc synchronized with filtered documents
  useEffect(() => {
    if (filteredDocs.length === 0) {
      if (selectedDoc) setSelectedDoc(null);
    } else if (selectedDoc) {
      if (!filteredDocs.some((d: any) => d.id === selectedDoc.id)) {
        if (isOwnerOrRc) {
          setSelectedDoc(null);
        } else {
          void openDocument(filteredDocs[0].id, filteredDocs[0]);
        }
      }
    } else if (!isOwnerOrRc) {
      void openDocument(filteredDocs[0].id, filteredDocs[0]);
    }
  }, [filteredDocs, selectedDoc, isOwnerOrRc]);

  // Period Documentation Overview Metrics & Needs Attention list (Live derived from canonical filtered set)
  const periodOverview = useMemo(() => {
    const totalDocs = filteredDocs.length;
    const clientIds = new Set<string>();
    const therapistIds = new Set<string>();

    let countSoap = 0;
    let countEval = 0;
    let countPoc = 0;
    let countQpr = 0;
    let countIntake = 0;
    let countExternal = 0;

    let countCleared = 0;
    let countPendingClinician = 0;
    let countPendingGuardian = 0;
    let countPendingSupervisor = 0;
    let countNeedsAttention = 0;

    const needsAttentionList: Array<{
      doc: any;
      clientName: string;
      docType: string;
      dos: string;
      therapistName: string;
      statusLabel: string;
      severity: 'rose' | 'amber' | 'indigo' | 'sky';
    }> = [];

    filteredDocs.forEach((d: any) => {
      if (d.client_id) clientIds.add(d.client_id);
      const provId = d.provider_id || d.service_provider_id;
      if (provId) therapistIds.add(provId);

      const isIntake = d.doc_type === 'INTAKE_PACKET' || d.doc_type === 'DDD_PACKET' || d.doc_type === 'INTAKE';
      const isExt = !isIntake && (
        d.doc_type === 'EXTERNAL' ||
        d.doc_type === 'PHYSICIAN_SIGNED_POC' ||
        d.doc_type === 'PHYSICIAN_SIGNED_FEEDING_POC' ||
        d.doc_type === 'OTHER' ||
        d.source === 'MANUAL_UPLOAD' ||
        d.source === 'INBOUND_FAX' ||
        Boolean(d.is_file_only_document)
      );

      if (isIntake) {
        countIntake++;
      } else if (isExt) {
        countExternal++;
      } else if (d.doc_type === 'SOAP') {
        countSoap++;
      } else if (d.doc_type === 'EVAL' || d.doc_type === 'FEEDING_EVAL' || d.doc_type === 'FEEDING_EVALUATION') {
        countEval++;
      } else if (d.doc_type === 'POC' || d.doc_type === 'FEEDING_POC') {
        countPoc++;
      } else if (d.doc_type === 'QPR') {
        countQpr++;
      }

      // Canonical status derivations matching list item
      const isSlpa = d.provider_role === 'SLPA' || (d.provider_title && d.provider_title.includes('SLPA'));
      const isCleared = documentFullySignedAndCleared(d);
      const isReturned = Boolean(d.clearance_status === 'NEEDS_CORRECTION' || d.supervisor_approval_status === 'RETURNED');
      const isClinicianSigned = Boolean(d.clinician_signature);
      const isPendingClinician = !isClinicianSigned || d.clearance_status === 'PENDING_CLINICIAN';
      const isPendingGuardian = (d.clearance_status === 'PENDING_GUARDIAN' && !isAuthoredPlanDoc(d.doc_type)) ||
        ((isAuthoredEvalDoc(d.doc_type) || d.doc_type === 'SOAP') && !d.guardian_signature && d.guardian_signature_status !== 'WAIVED_DOCUMENTED' && d.doc_type !== 'QPR');
      const isPendingSupervisor = isSlpa && d.clearance_status === 'PENDING_SUPERVISOR';
      const isOverdue = !isAuthoredPlanDoc(d.doc_type) && (d.is_guardian_signature_overdue || d.guardian_signature_status === 'OVERDUE');
      const isSentForGuardian = !isAuthoredPlanDoc(d.doc_type) && (d.esign_status === 'SENT' || d.esign_status === 'PENDING' || d.guardian_signature_status === 'SENT_FOR_SIGNATURE') && !d.guardian_signature;

      if (isCleared) {
        countCleared++;
      } else {
        if (isOverdue || isReturned) {
          countNeedsAttention++;
        }
        if (isPendingClinician) {
          countPendingClinician++;
        }
        if (isPendingGuardian) {
          countPendingGuardian++;
        }
        if (isPendingSupervisor) {
          countPendingSupervisor++;
        }

        let statusLabel = 'Pending Review';
        let severity: 'rose' | 'amber' | 'indigo' | 'sky' = 'amber';

        if (isReturned) {
          statusLabel = 'Supervisor Returned Revision';
          severity = 'rose';
        } else if (isOverdue) {
          statusLabel = d.late_pay_consequence
            ? GUARDIAN_OVERDUE_PAY_MESSAGE
            : (d.guardian_signature_display_label || 'Guardian Signature — OVERDUE');
          severity = 'rose';
        } else if (isPendingSupervisor) {
          statusLabel = 'Supervisor Co-Sign Pending';
          severity = 'indigo';
        } else if (isPendingClinician) {
          statusLabel = 'Clinician Signature Pending';
          severity = 'sky';
        } else if (isPendingGuardian) {
          statusLabel = isSentForGuardian ? 'Guardian Signature Sent' : 'Guardian Signature Pending';
          severity = 'amber';
        } else if (isAuthoredPlanDoc(d.doc_type) && !d.physician_signature) {
          statusLabel = 'Physician Signature Pending';
          severity = 'amber';
        } else {
          statusLabel = d.clearance_status ? d.clearance_status.replace(/_/g, ' ') : 'Attention Required';
          severity = 'amber';
        }

        const clientName = d.client_name
          ? d.client_name
          : (d.first_name && d.last_name
              ? `${d.first_name} ${d.last_name}`
              : (d.last_name && d.first_name
                  ? `${d.last_name}, ${d.first_name}`
                  : (d.first_name || d.last_name || `Client ${d.client_id}`)));

        const therapistName = d.provider_name || 'Unassigned';
        const dos = d.dos || d.document_date || (d.created_at ? d.created_at.slice(0, 10) : '—');

        needsAttentionList.push({
          doc: d,
          clientName,
          docType: d.doc_type,
          dos,
          therapistName,
          statusLabel,
          severity
        });
      }
    });

    return {
      totalDocs,
      uniqueClients: clientIds.size,
      uniqueTherapists: therapistIds.size,
      byType: {
        SOAP: countSoap,
        EVAL: countEval,
        POC: countPoc,
        QPR: countQpr,
        INTAKE: countIntake,
        EXTERNAL: countExternal
      },
      status: {
        cleared: countCleared,
        pendingClinician: countPendingClinician,
        pendingGuardian: countPendingGuardian,
        pendingSupervisor: countPendingSupervisor,
        needsAttention: countNeedsAttention
      },
      needsAttentionList
    };
  }, [filteredDocs]);

  const sectionData = useMemo(() => {
    if (!selectedDoc?.section_data_json) return null;
    try {
      return JSON.parse(selectedDoc.section_data_json);
    } catch {
      return null;
    }
  }, [selectedDoc?.section_data_json]);

  const renderRecordsExportPanel = () => showRecordsExport && bulkExportAllowed ? (
    <div className="clinical-records-export-panel absolute left-1/2 -translate-x-1/2 top-full mt-1.5 w-80 max-w-[92vw] rounded-xl border border-slate-200 bg-white p-3 shadow-xl z-50 text-left space-y-2">
      <p className="text-[11px] text-slate-600">
        Sessions and plans use {resolvedPeriod.periodLabel}. All Time exports all history.
      </p>
      <p className="text-[11px] text-slate-600">
        EOBs are included by client association and are not limited to this date range, when this account can already open EOB files. Everyone includes inactive and discharged clients.
      </p>
      <div className="grid grid-cols-3 gap-1 text-[11px] font-semibold">
        {([
          ['client', 'This client'],
          ['selected', 'Choose'],
          ['all', 'Everyone']
        ] as const).map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => setRecordsExportScope(value)}
            className={`py-1 rounded-md border cursor-pointer ${recordsExportScope === value ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-700 border-slate-200'}`}
          >
            {label}
          </button>
        ))}
      </div>
      {recordsExportScope === 'selected' && (
        <div className="max-h-36 overflow-y-auto space-y-1">
          {allCanonicalClients.map((client) => {
            const status = clientsList.find((row: any) => row.id === client.id)?.status;
            const checked = recordsExportClientIds.includes(client.id);
            return (
              <label key={client.id} className="flex items-center gap-2 text-[11px] text-slate-700">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => {
                    setRecordsExportClientIds((current) => checked
                      ? current.filter((id) => id !== client.id)
                      : [...current, client.id]);
                  }}
                />
                <span className="truncate">{client.name}{status && status !== 'ACTIVE' ? ` (${status})` : ''}</span>
              </label>
            );
          })}
        </div>
      )}
      <label className="flex items-start gap-2 text-[11px] text-slate-700">
        <input
          type="checkbox"
          checked={includeUndetermined}
          onChange={(event) => setIncludeUndetermined(event.target.checked)}
        />
        <span>Include records without a complete date range.</span>
      </label>
      <button
        type="button"
        onClick={handleRecordsExport}
        disabled={recordsExporting}
        className="w-full h-8 rounded-lg bg-slate-800 text-white text-xs font-bold cursor-pointer disabled:opacity-50"
      >
        {recordsExporting ? 'Exporting…' : 'Download ZIP'}
      </button>
      {recordsExportNote && <p className="text-[10px] text-slate-600">{recordsExportNote}</p>}
    </div>
  ) : null;

  return (
    <div className="space-y-4">
      {/* Header Shell: Mobile unified framed shell matching Client Charts, Desktop/Tablet preserved */}
      {/* Mobile Shell (< md) */}
      {/* Mobile Header Shell */}
      <div className="md:hidden relative rounded-2xl border border-slate-700/80 mobile-header-shell bg-gradient-to-br from-[#2a394d] via-[#1a2a42] to-[#0d1727] px-4 py-3 space-y-2.5 no-print z-20 shadow-xl">
        <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-full bg-gradient-to-l from-sky-500/5 via-indigo-500/5 to-transparent pointer-events-none" />
        </div>
        <div className="relative z-10 flex flex-col items-center justify-center text-center w-full space-y-2.5">
          {/* Centered Title */}
          <h1 className="text-xl font-bold tracking-tight text-white">
            Clinical Documents
          </h1>

          {/* Mobile Top Mode Switch: DOCUMENTS vs CREATE (Bloom Light Segmented Control) */}
          <div
            id="clinical-mobile-mode-switch"
            className="inline-flex items-center rounded-xl p-1 gap-1 w-full max-w-[280px] bg-slate-100 border border-slate-200/90 shadow-2xs"
          >
            <button
              type="button"
              id="clinical-mobile-mode-documents-btn"
              onClick={() => setMobileMode('DOCUMENTS')}
              className={`flex-1 h-8 px-2.5 text-xs font-semibold rounded-lg transition-all inline-flex items-center justify-center space-x-1.5 cursor-pointer ${
                mobileMode === 'DOCUMENTS'
                  ? 'bg-white text-slate-900 font-bold border border-slate-300 shadow-xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <FileText className="w-3.5 h-3.5 shrink-0 text-slate-600" />
              <span>DOCUMENTS</span>
            </button>
            <button
              type="button"
              id="clinical-mobile-mode-create-btn"
              onClick={() => setMobileMode('CREATE')}
              className={`flex-1 h-8 px-2.5 text-xs font-semibold rounded-lg transition-all inline-flex items-center justify-center space-x-1.5 cursor-pointer ${
                mobileMode === 'CREATE'
                  ? 'bg-white text-slate-900 font-bold border border-slate-300 shadow-xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Plus className="w-3.5 h-3.5 shrink-0 text-slate-600" />
              <span>CREATE</span>
            </button>
          </div>

          {/* DOCUMENTS MODE: Primary record-review workspace controls */}
          {mobileMode === 'DOCUMENTS' && (
            <>
              {/* COMPACT CONTEXT ROW: WHO (Client) & WHEN (Period) */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2.5 w-full py-0.5">
            {/* WHO: CLIENT SELECTOR */}
            <div className="relative inline-flex flex-col items-center w-full max-w-[260px]" ref={mobileClientDropdownRef}>
              <div className="relative w-full">
                <button
                  type="button"
                  id="clinical-client-selector-btn-mobile"
                  onClick={() => {
                    setShowClientDropdown((prev) => !prev);
                    setShowPeriodDropdown(false);
                  }}
                  className={`w-full relative inline-flex items-center justify-center px-7 py-1.5 rounded-lg border text-xs font-semibold cursor-pointer transition-all ${
                    selectedClientId !== 'ALL'
                      ? 'bg-slate-100 text-slate-900 border-slate-300 font-bold shadow-xs'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                  title="Filter by Client (WHO)"
                >
                  <div className="flex items-center justify-center gap-1.5 truncate min-w-0 text-center">
                    <User className="w-3.5 h-3.5 shrink-0 text-slate-600" />
                    <span className="truncate">{selectedClientDisplayName}</span>
                  </div>
                  <ChevronDown className={`w-3.5 h-3.5 shrink-0 absolute right-2.5 transition-transform ${
                    showClientDropdown ? 'rotate-180 text-slate-700' : 'text-slate-400'
                  }`} />
                </button>

                {/* Client Dropdown Popover */}
                {showClientDropdown && (
                  <div
                    id="clinical-client-dropdown-menu-mobile"
                    className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 w-72 max-w-[92vw] rounded-xl border border-slate-200 p-2 z-50 space-y-2 bg-white text-slate-800 shadow-xl text-left"
                  >
                    <div className="relative">
                      <input
                        type="text"
                        id="clinical-client-search-input-mobile"
                        value={clientSearchQuery}
                        onChange={(e) => setClientSearchQuery(e.target.value)}
                        placeholder="Search client name or ID..."
                        className="w-full rounded-lg px-2.5 py-1.5 text-xs focus:outline-none bg-slate-50 border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-slate-400"
                        autoFocus
                      />
                      {clientSearchQuery && (
                        <button
                          type="button"
                          onClick={() => setClientSearchQuery('')}
                          className="absolute right-2 top-2 text-slate-400 hover:text-slate-700 text-xs p-0.5"
                        >
                          ✕
                        </button>
                      )}
                    </div>

                    <div className="max-h-56 overflow-y-auto space-y-1 pr-1 custom-scrollbar">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedClientId('ALL');
                          setShowClientDropdown(false);
                          setClientSearchQuery('');
                        }}
                        className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-medium flex items-center justify-between transition-colors cursor-pointer ${
                          selectedClientId === 'ALL'
                            ? 'bg-slate-100 text-slate-900 border border-slate-300 font-bold'
                            : 'hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full shrink-0 bg-slate-700" />
                          <span>All Clients (Office-wide)</span>
                        </div>
                        <span className="text-[10px] font-mono text-slate-500">All</span>
                      </button>

                      {filteredCanonicalClients.map((client) => {
                        const isCurrent = selectedClientId === client.id;
                        const docCount = docs.filter((d: any) => d.client_id === client.id).length;
                        return (
                          <button
                            key={client.id}
                            type="button"
                            onClick={() => {
                              setSelectedClientId(client.id);
                              setShowClientDropdown(false);
                              setClientSearchQuery('');
                            }}
                            className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-medium flex items-center justify-between transition-colors cursor-pointer ${
                              isCurrent
                                ? 'bg-slate-100 text-slate-900 border border-slate-300 font-bold'
                                : 'hover:bg-slate-50 text-slate-700'
                            }`}
                          >
                            <div className="truncate min-w-0 pr-2">
                              <div className="truncate font-medium text-slate-900">{client.name}</div>
                              <div className="text-[10px] font-mono truncate text-slate-500">
                                ID: {client.id}{client.ahcccs_id ? ` • AHCCCS: ${client.ahcccs_id}` : ''}
                              </div>
                            </div>
                            {docCount > 0 && (
                              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded shrink-0 bg-slate-100 text-slate-700 border border-slate-200">
                                {docCount}
                              </span>
                            )}
                          </button>
                        );
                      })}

                      {filteredCanonicalClients.length === 0 && (
                        <div className="text-center py-4 text-xs text-slate-500">
                          No clients match &ldquo;{clientSearchQuery}&rdquo;
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* WHEN: PERIOD SELECTOR */}
            <div className="relative inline-flex flex-col items-center w-full max-w-[280px]" ref={mobilePeriodDropdownRef}>
              <div className="inline-flex items-center gap-1 w-full justify-center">
                {(periodMode === 'PAY_PERIOD' || periodMode === 'WEEK') && (
                  <button
                    type="button"
                    onClick={handlePrevPeriod}
                    className="h-8 px-2 rounded-lg text-xs font-bold cursor-pointer bg-white hover:bg-slate-50 text-slate-700 border border-slate-200/90 shadow-xs"
                    title="Previous Period"
                  >
                    ‹
                  </button>
                )}

                <button
                  type="button"
                  id="clinical-period-selector-btn-mobile"
                  onClick={() => {
                    setShowPeriodDropdown((prev) => !prev);
                    setShowClientDropdown(false);
                  }}
                  className="flex-1 inline-flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg border text-xs font-semibold cursor-pointer transition-all truncate bg-white hover:bg-slate-50 text-slate-800 border-slate-200/90 shadow-xs"
                  title="Filter by Canonical Period (WHEN)"
                >
                  <div className="flex items-center gap-1.5 truncate min-w-0">
                    <Calendar className="w-3.5 h-3.5 shrink-0 text-slate-500" />
                    <span className="truncate">{resolvedPeriod.periodLabel}</span>
                  </div>
                  <ChevronDown className={`w-3.5 h-3.5 shrink-0 ml-1 ${isClinicalLightMobile ? 'text-slate-500' : 'text-slate-400'}`} />
                </button>

                {(periodMode === 'PAY_PERIOD' || periodMode === 'WEEK') && (
                  <button
                    type="button"
                    onClick={handleNextPeriod}
                    className="h-8 px-2 rounded-lg text-xs font-bold cursor-pointer bg-white hover:bg-slate-50 text-slate-700 border border-slate-200/90 shadow-xs"
                    title="Next Period"
                  >
                    ›
                  </button>
                )}
              </div>
              {bulkExportAllowed && (
                <button
                  type="button"
                  id="clinical-records-export-btn-mobile"
                  onClick={openRecordsExport}
                  title="Export session notes, plans of care, and associated EOB files"
                  className="mt-1 h-8 px-2.5 rounded-lg text-xs font-bold cursor-pointer bg-white hover:bg-slate-50 text-slate-700 border border-slate-200/90 shadow-xs disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {recordsExporting ? 'Exporting…' : 'Export records'}
                </button>
              )}
              {renderRecordsExportPanel()}

              {/* Period Dropdown Popover */}
              {showPeriodDropdown && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 w-80 max-w-[92vw] rounded-xl border p-3 z-50 space-y-3 text-left bg-white border-slate-200/95 text-slate-800 shadow-xl">
                  <div className="grid grid-cols-4 gap-1 p-1 rounded-lg text-[11px] font-semibold text-center bg-slate-100 border border-slate-200/90">
                    {(['PAY_PERIOD', 'WEEK', 'CUSTOM', 'ALL_TIME'] as const).map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => setPeriodMode(mode)}
                        className={`py-1 rounded-md transition-all cursor-pointer ${
                          periodMode === mode
                            ? 'bg-slate-800 text-white font-bold shadow-xs'
                            : 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
                        }`}
                      >
                        {mode === 'PAY_PERIOD' ? 'Period' : mode === 'WEEK' ? 'Week' : mode === 'CUSTOM' ? 'Custom' : 'All'}
                      </button>
                    ))}
                  </div>

                  {periodMode === 'PAY_PERIOD' && (
                    <div className="space-y-2">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedPayPeriod(getCurrentPayPeriod());
                          setShowPeriodDropdown(false);
                        }}
                        className="w-full text-left p-2 rounded-lg text-xs font-semibold flex items-center justify-between transition-colors cursor-pointer bg-white border border-slate-200 hover:bg-slate-50 text-slate-800"
                      >
                        <span>Current Pay Period</span>
                        <span className="text-[10px] font-mono">Today</span>
                      </button>

                      <div className="text-[10px] font-mono uppercase tracking-wider font-bold px-1 text-slate-600">
                        Recent Pay Periods
                      </div>
                      <div className="max-h-48 overflow-y-auto space-y-1 pr-1 custom-scrollbar">
                        {recentPayPeriods.map((pp) => {
                          const isSelected = selectedPayPeriod.id === pp.id;
                          return (
                            <button
                              key={pp.id}
                              type="button"
                              onClick={() => {
                                setSelectedPayPeriod(pp);
                                setShowPeriodDropdown(false);
                              }}
                              className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-medium flex items-center justify-between transition-colors cursor-pointer ${
                                isSelected
                                  ? 'bg-slate-100 text-slate-900 border border-slate-300 font-bold'
                                  : 'hover:bg-slate-50 text-slate-700'
                              }`}
                            >
                              <span>{pp.label}</span>
                              <span className="text-[10px] font-mono text-slate-500">
                                {formatDisplayDate(pp.startDate)} – {formatDisplayDate(pp.endDate)}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {periodMode === 'WEEK' && (
                    <div className="space-y-2">
                      <div className="text-xs font-medium text-slate-700">
                        Pay Period: <span className="font-mono text-slate-900 font-bold">{selectedPayPeriod.label}</span>
                      </div>
                      {(() => {
                        const weeks = getPayPeriodWeeks(selectedPayPeriod);
                        return (
                          <div className="grid grid-cols-2 gap-2 pt-1">
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedWeekNumber(1);
                                setShowPeriodDropdown(false);
                              }}
                              className={`p-2 rounded-lg border text-left cursor-pointer transition-colors ${
                                selectedWeekNumber === 1
                                  ? 'bg-slate-800 text-white border-slate-700 font-bold'
                                  : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                              }`}
                            >
                              <div className="text-xs font-bold">Week 1</div>
                              <div className="text-[10px] font-mono mt-0.5 text-slate-500">
                                {formatDisplayDate(weeks.week1.startDate)} – {formatDisplayDate(weeks.week1.endDate)}
                              </div>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedWeekNumber(2);
                                setShowPeriodDropdown(false);
                              }}
                              className={`p-2 rounded-lg border text-left cursor-pointer transition-colors ${
                                selectedWeekNumber === 2
                                  ? 'bg-slate-800 text-white border-slate-700 font-bold'
                                  : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                              }`}
                            >
                              <div className="text-xs font-bold">Week 2</div>
                              <div className="text-[10px] font-mono mt-0.5 text-slate-500">
                                {formatDisplayDate(weeks.week2.startDate)} – {formatDisplayDate(weeks.week2.endDate)}
                              </div>
                            </button>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {periodMode === 'CUSTOM' && (
                    <div className="space-y-2 text-xs">
                      <div>
                        <label className="text-[11px] block mb-1 text-slate-600">Start Date</label>
                        <input
                          type="date"
                          value={customStartDate}
                          onChange={(e) => setCustomStartDate(e.target.value)}
                          className="w-full rounded-lg px-2 py-1 text-xs bg-white border border-slate-200 text-slate-900"
                        />
                      </div>
                      <div>
                        <label className="text-[11px] block mb-1 text-slate-600">End Date</label>
                        <input
                          type="date"
                          value={customEndDate}
                          onChange={(e) => setCustomEndDate(e.target.value)}
                          className="w-full rounded-lg px-2 py-1 text-xs bg-white border border-slate-200 text-slate-900"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowPeriodDropdown(false)}
                        className="w-full mt-2 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs cursor-pointer"
                      >
                        Apply Range
                      </button>
                    </div>
                  )}

                  {periodMode === 'ALL_TIME' && (
                    <div className="p-3 rounded-lg border text-xs space-y-2 bg-slate-50 border-slate-200 text-slate-700">
                      <p>Displaying all clinical records across all time, with no date range filtering.</p>
                      <button
                        type="button"
                        onClick={() => setShowPeriodDropdown(false)}
                        className="w-full py-1.5 rounded-lg font-medium text-xs cursor-pointer bg-white hover:bg-slate-50 text-slate-800 border border-slate-200"
                      >
                        Done
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Centered Document-Type Bar: Primary row (EVAL, POC, QPR, SOAP, MORE) or In-Place Secondary row (BACK, INTAKE, EXTERNAL) */}
          <div className={`inline-flex items-center justify-between gap-1 p-1 rounded-xl w-full max-w-[390px] min-h-[38px] ${
            isClinicalLightMobile
              ? 'bg-slate-100 border border-slate-200/90 shadow-inner'
              : 'bg-gradient-to-b from-[#0a0e16] to-[#06080e] border border-slate-800/90 shadow-[inset_0_2px_4px_rgba(0,0,0,0.6),0_1px_2px_rgba(255,255,255,0.05)]'
          }`}>
            {!showMobileSecondaryFilters ? (
              <>
                {(['EVAL', 'POC', 'QPR', 'SOAP'] as const).map((tag) => {
                  const isSelected = docTypeFilter === tag;
                  const accentDot =
                    tag === 'EVAL' ? (isClinicalLightMobile ? 'bg-blue-600' : 'bg-blue-400') :
                    tag === 'POC' ? (isClinicalLightMobile ? 'bg-indigo-600' : 'bg-indigo-400') :
                    tag === 'QPR' ? (isClinicalLightMobile ? 'bg-emerald-600' : 'bg-emerald-400') : (isAssistantMobile ? 'bg-cyan-600' : isSupervisorMobile ? 'bg-sky-600' : 'bg-sky-600');
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => setDocTypeFilter((prev) => (prev === tag ? 'ALL' : tag))}
                      className={`flex-1 min-w-0 py-1.5 px-1 rounded-lg text-[10.5px] font-mono font-bold tracking-wider transition-all cursor-pointer border flex items-center justify-center gap-1 whitespace-nowrap ${
                        isSelected
                          ? isAssistantMobile
                            ? 'bg-cyan-50 text-cyan-900 border-cyan-300 font-bold shadow-xs'
                            : isSupervisorMobile
                            ? 'bg-purple-50 text-purple-900 border-purple-300 font-bold shadow-xs'
                            : 'bg-white text-slate-900 border-slate-300 font-bold shadow-xs'
                          : isClinicalLightMobile
                          ? 'bg-transparent hover:bg-slate-200/60 text-slate-600 hover:text-slate-900 border-transparent shadow-none'
                          : 'bg-transparent hover:bg-slate-800/60 text-slate-300 hover:text-white border-transparent shadow-none'
                      }`}
                      title={`Filter by ${tag}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${accentDot} ${isSelected ? (isAssistantMobile ? 'opacity-100 ring-1 ring-cyan-300/60' : isSupervisorMobile ? 'opacity-100 ring-1 ring-purple-300/60' : 'opacity-100 ring-1 ring-slate-300') : 'opacity-75'}`} />
                      <span>{tag}</span>
                    </button>
                  );
                })}

                {/* MORE Trigger -> Swaps contents of the same filter strip in place */}
                {(() => {
                  const isSecondaryActive = docTypeFilter === 'INTAKE' || docTypeFilter === 'EXTERNAL';
                  return (
                    <button
                      type="button"
                      id="clinical-doc-type-more-btn-mobile"
                      onClick={() => setShowMobileSecondaryFilters(true)}
                      className={`flex-1 min-w-0 py-1.5 px-1 rounded-lg text-[10.5px] font-mono font-bold tracking-wider transition-all cursor-pointer border flex items-center justify-center gap-0.5 whitespace-nowrap ${
                        isSecondaryActive
                          ? isAssistantMobile
                            ? 'bg-cyan-50 text-cyan-900 border-cyan-300 font-bold shadow-xs'
                            : isSupervisorMobile
                            ? 'bg-purple-50 text-purple-900 border-purple-300 font-bold shadow-xs'
                            : 'bg-white text-slate-900 border-slate-300 font-bold shadow-xs'
                          : isClinicalLightMobile
                          ? 'bg-transparent hover:bg-slate-200/60 text-slate-600 hover:text-slate-900 border-transparent shadow-none'
                          : 'bg-transparent hover:bg-slate-800/60 text-slate-300 hover:text-white border-transparent shadow-none'
                      }`}
                      title="More document types (Intake, External)"
                    >
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                        docTypeFilter === 'INTAKE'
                          ? isAssistantMobile ? 'bg-teal-600 ring-1 ring-cyan-300/60' : isSupervisorMobile ? 'bg-teal-600 ring-1 ring-purple-300/60' : 'bg-teal-600 ring-1 ring-slate-300'
                          : docTypeFilter === 'EXTERNAL'
                          ? isAssistantMobile ? 'bg-violet-600 ring-1 ring-cyan-300/60' : isSupervisorMobile ? 'bg-violet-600 ring-1 ring-purple-300/60' : 'bg-violet-600 ring-1 ring-slate-300'
                          : isClinicalLightMobile ? 'bg-slate-400' : 'bg-slate-500'
                      }`} />
                      <span>MORE</span>
                      <ChevronRight className="w-2.5 h-2.5 shrink-0 opacity-75" />
                    </button>
                  );
                })()}
              </>
            ) : (
              <>
                {/* Secondary In-Place Row: [ BACK ] [ INTAKE ] [ EXTERNAL ] */}
                <button
                  type="button"
                  id="clinical-doc-type-back-btn-mobile"
                  onClick={() => setShowMobileSecondaryFilters(false)}
                  className={`py-1.5 px-2.5 rounded-lg text-[10.5px] font-mono font-bold tracking-wider border transition-all flex items-center justify-center gap-1 cursor-pointer shrink-0 ${
                    isClinicalLightMobile
                      ? 'text-slate-700 hover:text-slate-900 bg-white hover:bg-slate-50 border-slate-200 shadow-xs'
                      : 'text-slate-300 hover:text-white bg-slate-800/80 hover:bg-slate-700/80 border-slate-700/80'
                  }`}
                  title="Back to primary document types"
                >
                  <ChevronLeft className={`w-3 h-3 ${isAssistantMobile ? 'text-cyan-600' : isSupervisorMobile ? 'text-purple-600' : 'text-slate-600'}`} />
                  <span>BACK</span>
                </button>

                <button
                  type="button"
                  id="clinical-doc-type-intake-btn-mobile"
                  onClick={() => setDocTypeFilter((prev) => (prev === 'INTAKE' ? 'ALL' : 'INTAKE'))}
                  className={`flex-1 min-w-0 py-1.5 px-2 rounded-lg text-[10.5px] font-mono font-bold tracking-wider transition-all cursor-pointer border flex items-center justify-center gap-1.5 whitespace-nowrap ${
                    docTypeFilter === 'INTAKE'
                      ? isAssistantMobile
                        ? 'bg-cyan-50 text-cyan-900 border-cyan-300 font-bold shadow-xs'
                        : isSupervisorMobile
                        ? 'bg-purple-50 text-purple-900 border-purple-300 font-bold shadow-xs'
                        : 'bg-white text-slate-900 border-slate-300 font-bold shadow-xs'
                      : isClinicalLightMobile
                      ? 'bg-transparent hover:bg-slate-200/60 text-slate-600 hover:text-slate-900 border-transparent shadow-none'
                      : 'bg-transparent hover:bg-slate-800/60 text-slate-300 hover:text-white border-transparent shadow-none'
                  }`}
                  title="Filter by Intake Packets"
                >
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isClinicalLightMobile ? 'bg-teal-600' : 'bg-teal-400'} ${docTypeFilter === 'INTAKE' ? (isAssistantMobile ? 'opacity-100 ring-1 ring-cyan-300/60' : isSupervisorMobile ? 'opacity-100 ring-1 ring-purple-300/60' : 'opacity-100 ring-1 ring-slate-300') : 'opacity-75'}`} />
                  <span>INTAKE</span>
                  {docTypeFilter === 'INTAKE' && <Check className={`w-3 h-3 ml-0.5 ${isAssistantMobile ? 'text-cyan-700' : isSupervisorMobile ? 'text-purple-700' : 'text-slate-700'}`} />}
                </button>

                <button
                  type="button"
                  id="clinical-doc-type-external-btn-mobile"
                  onClick={() => setDocTypeFilter((prev) => (prev === 'EXTERNAL' ? 'ALL' : 'EXTERNAL'))}
                  className={`flex-1 min-w-0 py-1.5 px-2 rounded-lg text-[10.5px] font-mono font-bold tracking-wider transition-all cursor-pointer border flex items-center justify-center gap-1.5 whitespace-nowrap ${
                    docTypeFilter === 'EXTERNAL'
                      ? isAssistantMobile
                        ? 'bg-cyan-50 text-cyan-900 border-cyan-300 font-bold shadow-xs'
                        : isSupervisorMobile
                        ? 'bg-purple-50 text-purple-900 border-purple-300 font-bold shadow-xs'
                        : 'bg-white text-slate-900 border-slate-300 font-bold shadow-xs'
                      : isClinicalLightMobile
                      ? 'bg-transparent hover:bg-slate-200/60 text-slate-600 hover:text-slate-900 border-transparent shadow-none'
                      : 'bg-transparent hover:bg-slate-800/60 text-slate-300 hover:text-white border-transparent shadow-none'
                  }`}
                  title="Filter by External Documents"
                >
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isClinicalLightMobile ? 'bg-violet-600' : 'bg-violet-400'} ${docTypeFilter === 'EXTERNAL' ? (isAssistantMobile ? 'opacity-100 ring-1 ring-cyan-300/60' : isSupervisorMobile ? 'opacity-100 ring-1 ring-purple-300/60' : 'opacity-100 ring-1 ring-slate-300') : 'opacity-75'}`} />
                  <span>EXTERNAL</span>
                  {docTypeFilter === 'EXTERNAL' && <Check className={`w-3.5 h-3.5 ml-0.5 ${isAssistantMobile ? 'text-cyan-700' : isSupervisorMobile ? 'text-purple-700' : 'text-slate-700'}`} />}
                </button>
              </>
            )}
          </div>
          </>
          )}
        </div>
      </div>

      {/* Desktop/Tablet Header Shell: Canonical rich navy / blue-grey shell */}
      <div className="hidden md:block relative rounded-2xl border border-slate-700/80 desktop-header-shell px-6 py-4 shadow-[0_4px_16px_rgba(0,0,0,0.4),inset_0_1px_0_0_rgba(255,255,255,0.12)] no-print z-20">
        {/* Subtle specular highlights clipped to rounded border */}
        <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
          <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-slate-400/30 to-transparent pointer-events-none" />
          <div className="absolute top-0 right-0 w-96 h-full bg-gradient-to-l from-sky-500/5 via-indigo-500/5 to-transparent pointer-events-none" />
        </div>

        <div className="relative z-10 flex flex-col items-center justify-center w-full space-y-3 text-center">
          {/* ROW 1: Centered Title */}
          <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight leading-none">
            Clinical Documents
          </h1>

          {/* ROW 2: Compact Context Row — WHO (Client, Therapist) & WHEN (Period) */}
          <div className="flex flex-wrap items-center justify-center gap-4 w-full py-0.5">
            {/* WHO: CLIENT SELECTOR */}
            <div className="relative inline-flex flex-col items-center" ref={clientDropdownRef}>
              {isOwnerOrRc && (
                <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-semibold mb-1">
                  CLIENT
                </span>
              )}
              <div className="relative">
                <button
                  type="button"
                  id="clinical-client-selector-btn"
                  onClick={() => {
                    setShowClientDropdown((prev) => !prev);
                    setShowProviderDropdown(false);
                    setShowPeriodDropdown(false);
                  }}
                  className={`relative inline-flex items-center justify-center pl-8 pr-8 py-1.5 rounded-lg border text-xs font-semibold cursor-pointer transition-all min-w-[180px] max-w-[280px] shadow-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/70 ${
                    selectedClientId !== 'ALL'
                      ? 'bg-white text-slate-900 border-slate-300'
                      : 'bg-white hover:bg-slate-50 text-slate-800 border-slate-200/90 hover:border-slate-300'
                  }`}
                  title="Filter by Client (WHO)"
                >
                  <User
                    className={`w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 shrink-0 pointer-events-none ${
                      selectedClientId !== 'ALL' ? 'text-slate-600' : 'text-slate-500'
                    }`}
                  />
                  <span className="truncate text-center w-full block">
                    {selectedClientDisplayName}
                  </span>
                  <ChevronDown
                    className={`w-3.5 h-3.5 text-slate-400 shrink-0 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none transition-transform ${
                      showClientDropdown ? 'rotate-180 text-slate-600' : ''
                    }`}
                  />
                </button>

                {/* Client Dropdown Popover */}
                {showClientDropdown && (
                  <div
                    id="clinical-client-dropdown-menu"
                    className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 w-72 sm:w-80 max-w-[92vw] rounded-xl border border-slate-200 bg-white p-2 shadow-xl z-50 space-y-2 text-left"
                  >
                    <div className="relative">
                      <input
                        type="text"
                        id="clinical-client-search-input"
                        value={clientSearchQuery}
                        onChange={(e) => setClientSearchQuery(e.target.value)}
                        placeholder="Search client name or ID..."
                        className="w-full bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-slate-400"
                        autoFocus
                      />
                      {clientSearchQuery && (
                        <button
                          type="button"
                          onClick={() => setClientSearchQuery('')}
                          className="absolute right-2 top-2 text-slate-400 hover:text-slate-800 text-xs p-0.5"
                        >
                          ✕
                        </button>
                      )}
                    </div>

                    <div className="max-h-60 overflow-y-auto space-y-1 pr-1 custom-scrollbar">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedClientId('ALL');
                          setShowClientDropdown(false);
                          setClientSearchQuery('');
                        }}
                        className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-medium flex items-center justify-between transition-colors cursor-pointer ${
                          selectedClientId === 'ALL'
                            ? 'bg-slate-100 text-slate-900 border border-slate-300 font-bold'
                            : 'hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-slate-600 shrink-0" />
                          <span>All Clients (Office-wide)</span>
                        </div>
                        <span className="text-[10px] font-mono text-slate-400">All</span>
                      </button>

                      {filteredCanonicalClients.map((client) => {
                        const isCurrent = selectedClientId === client.id;
                        const docCount = docs.filter((d: any) => d.client_id === client.id).length;
                        return (
                          <button
                            key={client.id}
                            type="button"
                            onClick={() => {
                              setSelectedClientId(client.id);
                              setShowClientDropdown(false);
                              setClientSearchQuery('');
                            }}
                            className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-medium flex items-center justify-between transition-colors cursor-pointer ${
                              isCurrent
                                ? 'bg-slate-100 text-slate-900 border border-slate-300 font-bold'
                                : 'hover:bg-slate-50 text-slate-700'
                            }`}
                          >
                            <div className="truncate min-w-0 pr-2">
                              <div className="truncate text-slate-900 font-medium">{client.name}</div>
                              <div className="text-[10px] font-mono text-slate-400 truncate">
                                ID: {client.id}{client.ahcccs_id ? ` • AHCCCS: ${client.ahcccs_id}` : ''}
                              </div>
                            </div>
                            {docCount > 0 && (
                              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200 shrink-0">
                                {docCount}
                              </span>
                            )}
                          </button>
                        );
                      })}

                      {filteredCanonicalClients.length === 0 && (
                        <div className="text-center py-4 text-xs text-slate-400">
                          No clients match &ldquo;{clientSearchQuery}&rdquo;
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* WHO: THERAPIST SELECTOR (Owner / Referral Coordinator Desktop Only) */}
            {isOwnerOrRc && (
              <div className="relative inline-flex flex-col items-center" ref={providerDropdownRef}>
                <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-semibold mb-1">
                  THERAPIST
                </span>
                <div className="relative">
                  <button
                    type="button"
                    id="clinical-therapist-selector-btn"
                    onClick={() => {
                      setShowProviderDropdown((prev) => !prev);
                      setShowClientDropdown(false);
                      setShowPeriodDropdown(false);
                    }}
                    className={`relative inline-flex items-center justify-center pl-8 pr-8 py-1.5 rounded-lg border text-xs font-semibold cursor-pointer transition-all min-w-[180px] max-w-[280px] shadow-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/70 ${
                      selectedProviderId !== 'ALL'
                        ? 'bg-white text-slate-900 border-slate-300'
                        : 'bg-white hover:bg-slate-50 text-slate-800 border-slate-200/90 hover:border-slate-300'
                    }`}
                    title="Filter by Therapist (PROVIDER)"
                  >
                    <Stethoscope
                      className={`w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 shrink-0 pointer-events-none ${
                        selectedProviderId !== 'ALL' ? 'text-slate-600' : 'text-slate-500'
                      }`}
                    />
                    <span className="truncate text-center w-full block">
                      {selectedProviderDisplayName}
                    </span>
                    <ChevronDown
                      className={`w-3.5 h-3.5 text-slate-400 shrink-0 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none transition-transform ${
                        showProviderDropdown ? 'rotate-180 text-slate-600' : ''
                      }`}
                    />
                  </button>

                  {/* Therapist Dropdown Popover */}
                  {showProviderDropdown && (
                    <div
                      id="clinical-therapist-dropdown-menu"
                      className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 w-72 sm:w-80 max-w-[92vw] rounded-xl border border-slate-200 bg-white p-2 shadow-xl z-50 space-y-2 text-left"
                    >
                      <div className="relative">
                        <input
                          type="text"
                          id="clinical-therapist-search-input"
                          value={providerSearchQuery}
                          onChange={(e) => setProviderSearchQuery(e.target.value)}
                          placeholder="Search therapist name or credential..."
                          className="w-full bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-slate-400"
                          autoFocus
                        />
                        {providerSearchQuery && (
                          <button
                            type="button"
                            onClick={() => setProviderSearchQuery('')}
                            className="absolute right-2 top-2 text-slate-400 hover:text-slate-800 text-xs p-0.5"
                          >
                            ✕
                          </button>
                        )}
                      </div>

                      <div className="max-h-60 overflow-y-auto space-y-1 pr-1 custom-scrollbar">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedProviderId('ALL');
                            setShowProviderDropdown(false);
                            setProviderSearchQuery('');
                          }}
                          className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-medium flex items-center justify-between transition-colors cursor-pointer ${
                            selectedProviderId === 'ALL'
                              ? 'bg-slate-100 text-slate-900 border border-slate-300 font-bold'
                              : 'hover:bg-slate-50 text-slate-700'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-slate-600 shrink-0" />
                            <span>All Therapists (All Active Treating Clinicians)</span>
                          </div>
                          <span className="text-[10px] font-mono text-slate-400">All</span>
                        </button>

                        {filteredCanonicalProviders.map((provider) => {
                          const isCurrent = selectedProviderId === provider.id;
                          const docCount = docs.filter(
                            (d: any) => d.provider_id === provider.id || d.service_provider_id === provider.id
                          ).length;
                          return (
                            <button
                              key={provider.id}
                              type="button"
                              onClick={() => {
                                setSelectedProviderId(provider.id);
                                setShowProviderDropdown(false);
                                setProviderSearchQuery('');
                              }}
                              className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-medium flex items-center justify-between transition-colors cursor-pointer ${
                                isCurrent
                                  ? 'bg-slate-100 text-slate-900 border border-slate-300 font-bold'
                                  : 'hover:bg-slate-50 text-slate-700'
                              }`}
                            >
                              <div className="truncate min-w-0 pr-2">
                                <div className="truncate text-slate-900 font-medium">{provider.name}</div>
                                <div className="text-[10px] font-mono text-slate-400 truncate">
                                  ID: {provider.id}{provider.role ? ` • ${provider.role}` : ''}
                                </div>
                              </div>
                              {docCount > 0 && (
                                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200 shrink-0">
                                  {docCount}
                                </span>
                              )}
                            </button>
                          );
                        })}

                        {filteredCanonicalProviders.length === 0 && (
                          <div className="text-center py-4 text-xs text-slate-400">
                            No therapists match &ldquo;{providerSearchQuery}&rdquo;
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* WHEN: PERIOD SELECTOR */}
            <div className="relative inline-flex flex-col items-center" ref={periodDropdownRef}>
              {isOwnerOrRc && (
                <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-semibold mb-1">
                  PERIOD
                </span>
              )}
              <div className="inline-flex items-center gap-1">
                {(periodMode === 'PAY_PERIOD' || periodMode === 'WEEK') && (
                  <button
                    type="button"
                    onClick={handlePrevPeriod}
                    className="h-8 px-2 rounded-lg bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border border-slate-200/90 hover:border-slate-300 shadow-xs text-xs font-bold cursor-pointer transition-all"
                    title="Previous Period"
                  >
                    ‹
                  </button>
                )}

                <button
                  type="button"
                  id="clinical-period-selector-btn"
                  onClick={() => {
                    setShowPeriodDropdown((prev) => !prev);
                    setShowClientDropdown(false);
                  }}
                  className="inline-flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg border text-xs font-semibold cursor-pointer transition-all min-w-[180px] max-w-[280px] shadow-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/70 bg-white hover:bg-slate-50 text-slate-800 border-slate-200/90 hover:border-slate-300"
                  title="Filter by Canonical Period (WHEN)"
                >
                  <div className="flex items-center gap-1.5 truncate min-w-0">
                    <Calendar className="w-3.5 h-3.5 shrink-0 text-slate-500" />
                    <span className="truncate">{resolvedPeriod.periodLabel}</span>
                  </div>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-500 shrink-0 ml-1" />
                </button>

                {(periodMode === 'PAY_PERIOD' || periodMode === 'WEEK') && (
                  <button
                    type="button"
                    onClick={handleNextPeriod}
                    className="h-8 px-2 rounded-lg bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border border-slate-200/90 hover:border-slate-300 shadow-xs text-xs font-bold cursor-pointer transition-all"
                    title="Next Period"
                  >
                    ›
                  </button>
                )}
                {bulkExportAllowed && (
                  <button
                    type="button"
                    id="clinical-records-export-btn"
                    onClick={openRecordsExport}
                    title="Export session notes, plans of care, and associated EOB files"
                    className="h-8 px-2.5 rounded-lg bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border border-slate-200/90 hover:border-slate-300 shadow-xs text-xs font-bold cursor-pointer transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {recordsExporting ? 'Exporting…' : 'Export records'}
                  </button>
                )}
              </div>
              {renderRecordsExportPanel()}

              {/* Period Dropdown Popover */}
              {showPeriodDropdown && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 w-80 max-w-[92vw] rounded-xl border border-slate-200 bg-white p-3 shadow-xl z-50 space-y-3 text-left">
                  <div className="grid grid-cols-4 gap-1 p-1 rounded-lg bg-slate-100 border border-slate-200/90 text-[11px] font-semibold text-center">
                    {(['PAY_PERIOD', 'WEEK', 'CUSTOM', 'ALL_TIME'] as const).map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => setPeriodMode(mode)}
                        className={`py-1 rounded-md transition-all cursor-pointer ${
                          periodMode === mode
                            ? 'bg-slate-800 text-white font-bold shadow-xs'
                            : 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
                        }`}
                      >
                        {mode === 'PAY_PERIOD' ? 'Period' : mode === 'WEEK' ? 'Week' : mode === 'CUSTOM' ? 'Custom' : 'All Time'}
                      </button>
                    ))}
                  </div>

                  {periodMode === 'PAY_PERIOD' && (
                    <div className="space-y-2">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedPayPeriod(getCurrentPayPeriod());
                          setShowPeriodDropdown(false);
                        }}
                        className="w-full text-left p-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-800 text-xs font-semibold flex items-center justify-between transition-colors cursor-pointer"
                      >
                        <span>Current Pay Period</span>
                        <span className="text-[10px] font-mono">Today</span>
                      </button>

                      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 font-bold px-1">
                        Recent Pay Periods
                      </div>
                      <div className="max-h-48 overflow-y-auto space-y-1 pr-1 custom-scrollbar">
                        {recentPayPeriods.map((pp) => {
                          const isSelected = selectedPayPeriod.id === pp.id;
                          return (
                            <button
                              key={pp.id}
                              type="button"
                              onClick={() => {
                                setSelectedPayPeriod(pp);
                                setShowPeriodDropdown(false);
                              }}
                              className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-medium flex items-center justify-between transition-colors cursor-pointer ${
                                isSelected
                                  ? 'bg-slate-100 text-slate-900 border border-slate-300 font-bold'
                                  : 'hover:bg-slate-50 text-slate-700'
                              }`}
                            >
                              <span>{pp.label}</span>
                              <span className="text-[10px] font-mono text-slate-500">
                                {formatDisplayDate(pp.startDate)} – {formatDisplayDate(pp.endDate)}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {periodMode === 'WEEK' && (
                    <div className="space-y-2">
                      <div className="text-xs text-slate-700 font-medium">
                        Pay Period: <span className="font-mono text-slate-900">{selectedPayPeriod.label}</span>
                      </div>
                      {(() => {
                        const weeks = getPayPeriodWeeks(selectedPayPeriod);
                        return (
                          <div className="grid grid-cols-2 gap-2 pt-1">
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedWeekNumber(1);
                                setShowPeriodDropdown(false);
                              }}
                              className={`p-2 rounded-lg border text-left cursor-pointer transition-colors ${
                                selectedWeekNumber === 1
                                  ? 'bg-slate-800 text-white border-slate-700 font-bold'
                                  : 'bg-white border-slate-200 hover:bg-slate-50 text-slate-700'
                              }`}
                            >
                              <div className="text-xs font-bold">Week 1</div>
                              <div className={`text-[10px] font-mono mt-0.5 ${selectedWeekNumber === 1 ? 'text-slate-200' : 'text-slate-500'}`}>
                                {formatDisplayDate(weeks.week1.startDate)} – {formatDisplayDate(weeks.week1.endDate)}
                              </div>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedWeekNumber(2);
                                setShowPeriodDropdown(false);
                              }}
                              className={`p-2 rounded-lg border text-left cursor-pointer transition-colors ${
                                selectedWeekNumber === 2
                                  ? 'bg-slate-800 text-white border-slate-700 font-bold'
                                  : 'bg-white border-slate-200 hover:bg-slate-50 text-slate-700'
                              }`}
                            >
                              <div className="text-xs font-bold">Week 2</div>
                              <div className={`text-[10px] font-mono mt-0.5 ${selectedWeekNumber === 2 ? 'text-slate-200' : 'text-slate-500'}`}>
                                {formatDisplayDate(weeks.week2.startDate)} – {formatDisplayDate(weeks.week2.endDate)}
                              </div>
                            </button>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {periodMode === 'CUSTOM' && (
                    <div className="space-y-2 text-xs">
                      <div>
                        <label className="text-slate-500 text-[11px] block mb-1">Start Date</label>
                        <input
                          type="date"
                          value={customStartDate}
                          onChange={(e) => setCustomStartDate(e.target.value)}
                          className="w-full bg-white border border-slate-200 rounded-lg px-2 py-1 text-slate-800 text-xs"
                        />
                      </div>
                      <div>
                        <label className="text-slate-500 text-[11px] block mb-1">End Date</label>
                        <input
                          type="date"
                          value={customEndDate}
                          onChange={(e) => setCustomEndDate(e.target.value)}
                          className="w-full bg-white border border-slate-200 rounded-lg px-2 py-1 text-slate-800 text-xs"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowPeriodDropdown(false)}
                        className="w-full mt-2 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs cursor-pointer"
                      >
                        Apply Range
                      </button>
                    </div>
                  )}

                  {periodMode === 'ALL_TIME' && (
                    <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-700 space-y-2">
                      <p>Displaying all clinical records across all time, with no date range filtering.</p>
                      <button
                        type="button"
                        onClick={() => setShowPeriodDropdown(false)}
                        className="w-full py-1.5 rounded-lg bg-white hover:bg-slate-50 text-slate-800 border border-slate-200 font-medium text-xs cursor-pointer"
                      >
                        Done
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Scoped Document Search Surface (Mobile only — sits cleanly below header shell, matching Clients view search position) */}
      <div className={`${isAssistantMobile ? 'md:hidden' : 'sm:hidden'} relative group w-full min-w-0 no-print`}>
        <div className="relative flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-600/90 hover:border-slate-500 bg-gradient-to-b from-[#1a293e] to-[#142032] shadow-[0_2px_8px_rgba(0,0,0,0.3),inset_0_1px_0_0_rgba(255,255,255,0.1)] transition-all focus-within:border-sky-400/80 focus-within:ring-1 focus-within:ring-sky-500/30">
          <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-slate-300/20 to-transparent pointer-events-none" />
          <div className="flex items-center pl-0.5 shrink-0 select-none">
            <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" className="stroke-sky-400" />
              <path d="M21 21l-4.35-4.35" className="stroke-white" />
            </svg>
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search documents..."
            className="w-full bg-transparent text-xs text-white placeholder:text-slate-300 focus:outline-none tracking-tight font-medium"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="p-1 rounded text-slate-500 hover:text-slate-300 transition-colors shrink-0"
              title="Clear search"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Clinical Creation Actions: Compact Toolbar for Desktop (Canonical Canon) */}
      {canAuthorClinicalDocs && (
        <div
          id="clinical-doc-desktop-toolbar"
          className="hidden sm:flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl bg-white border border-slate-200/90 shadow-2xs no-print"
        >
          <div className="flex items-center space-x-2 text-xs font-semibold text-slate-700 shrink-0">
            <span className="w-2 h-2 rounded-full bg-slate-400" />
            <span className="font-bold text-slate-900 tracking-tight">Create Document:</span>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* 1. Create Evaluation (SLP/Authoring clinicians only — hidden for Assistant/SLPA) */}
            {!isAssistantUser && (
              <button
                type="button"
                id="create-eval-btn"
                disabled={!canCreateEval}
                onClick={canCreateEval ? () => handleLaunchCreateDoc('EVAL') : undefined}
                title={canCreateEval ? 'Create Evaluation (Initial Evaluation & Plan of Care)' : 'Clinical note authoring is restricted.'}
                className={`h-8 px-3 rounded-lg border text-xs font-semibold inline-flex items-center space-x-1.5 transition-all cursor-pointer ${
                  canCreateEval
                    ? 'bg-blue-50/80 hover:bg-blue-100/90 text-blue-900 border-blue-200/90 shadow-2xs active:scale-[0.98]'
                    : 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                }`}
              >
                <Award className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                <span>Evaluation</span>
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-bold">EVAL</span>
              </button>
            )}

            {/* 2. Create Plan of Care (POC) (SLP/Authoring clinicians only — hidden for Assistant/SLPA) */}
            {!isAssistantUser && (
              <button
                type="button"
                id="create-poc-btn"
                disabled={!canCreatePoc}
                onClick={canCreatePoc ? () => handleLaunchCreateDoc('POC') : undefined}
                title={canCreatePoc ? 'Create Plan of Care (POC Recertification & Goals)' : 'Clinical note authoring is restricted.'}
                className={`h-8 px-3 rounded-lg border text-xs font-semibold inline-flex items-center space-x-1.5 transition-all cursor-pointer ${
                  canCreatePoc
                    ? 'bg-indigo-50/80 hover:bg-indigo-100/90 text-indigo-900 border-indigo-200/90 shadow-2xs active:scale-[0.98]'
                    : 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                }`}
              >
                <Calendar className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                <span>Plan of Care</span>
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-bold">POC</span>
              </button>
            )}

            {/* 3. Create Quarterly Progress Report (QPR) */}
            <button
              type="button"
              id="create-qpr-btn"
              disabled={!canCreateQpr}
              onClick={canCreateQpr ? () => handleLaunchCreateDoc('QPR') : undefined}
              title={canCreateQpr ? 'Create Quarterly Progress Report (QPR Summary & Outcomes)' : 'Clinical note authoring is restricted.'}
              className={`h-8 px-3 rounded-lg border text-xs font-semibold inline-flex items-center space-x-1.5 transition-all cursor-pointer ${
                canCreateQpr
                  ? 'bg-emerald-50/80 hover:bg-emerald-100/90 text-emerald-900 border-emerald-200/90 shadow-2xs active:scale-[0.98]'
                  : 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
              }`}
            >
              <Target className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              <span>Progress Report</span>
              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold">QPR</span>
            </button>

          </div>
        </div>
      )}

      {/* Mobile Only: CREATE mode full view */}
      {canAuthorClinicalDocs && mobileMode === 'CREATE' && (
      <div
        id="clinical-doc-launch-cards"
        className="grid sm:hidden gap-3 w-full no-print"
      >
        {/* 1. Create Evaluation (SLP/Authoring clinicians only — hidden for Assistant/SLPA) */}
        {!isAssistantUser && (
        <button
          type="button"
          id="create-eval-btn-mobile"
          disabled={!canCreateEval}
          onClick={canCreateEval ? () => handleLaunchCreateDoc('EVAL') : undefined}
          title={
            canCreateEval
              ? 'Create Evaluation (Initial Evaluation & Plan of Care)'
              : 'Clinical note authoring is restricted to authorized clinicians.'
          }
          className={`relative flex items-center justify-between p-3.5 rounded-xl text-left overflow-hidden transition-all duration-200 ${
            canCreateEval
              ? 'group cursor-pointer bg-white border border-slate-200/90 border-l-[3px] border-l-blue-500 shadow-xs active:scale-[0.99]'
              : 'bg-slate-50 border border-slate-200/90 opacity-70 cursor-not-allowed select-none'
          }`}
        >
          <div className="flex items-center space-x-3 min-w-0 flex-1 pr-2 z-10">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border ${canCreateEval ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-slate-100 border-slate-200 text-slate-400'}`}>
              <Award className="w-5 h-5 stroke-[2.2]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-bold text-slate-800 text-sm tracking-tight truncate">Create Evaluation</div>
              <div className="text-[11px] text-slate-600 truncate">Initial Evaluation &amp; Plan of Care</div>
            </div>
          </div>
          <div className="shrink-0 z-10">
            {canCreateEval ? (
              <div className="w-7 h-7 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-center">
                <Plus className="w-4 h-4 text-blue-700 stroke-[2.5]" />
              </div>
            ) : (
              <span className="flex items-center space-x-1 px-2 py-0.5 rounded-md bg-slate-100 border border-slate-200 text-slate-500 text-[10px] font-semibold">
                <Lock className="w-3 h-3 text-slate-500" />
                <span>Locked</span>
              </span>
            )}
          </div>
        </button>
        )}

        {/* 2. Create Plan of Care (POC) */}
        {!isAssistantUser && (
        <button
          type="button"
          id="create-poc-btn-mobile"
          disabled={!canCreatePoc}
          onClick={canCreatePoc ? () => handleLaunchCreateDoc('POC') : undefined}
          title={
            canCreatePoc
              ? 'Create Plan of Care (POC Recertification & Goals)'
              : 'Clinical note authoring is restricted to authorized clinicians.'
          }
          className={`relative flex items-center justify-between p-3.5 rounded-xl text-left overflow-hidden transition-all duration-200 ${
            canCreatePoc
              ? 'group cursor-pointer bg-white border border-slate-200/90 border-l-[3px] border-l-indigo-500 shadow-xs active:scale-[0.99]'
              : 'bg-slate-50 border border-slate-200/90 opacity-70 cursor-not-allowed select-none'
          }`}
        >
          <div className="flex items-center space-x-3 min-w-0 flex-1 pr-2 z-10">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border ${canCreatePoc ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-slate-100 border-slate-200 text-slate-400'}`}>
              <Calendar className="w-5 h-5 stroke-[2.2]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-bold text-slate-800 text-sm tracking-tight truncate">Create Plan of Care</div>
              <div className="text-[11px] text-slate-600 truncate">POC Recertification &amp; Goals</div>
            </div>
          </div>
          <div className="shrink-0 z-10">
            {canCreatePoc ? (
              <div className="w-7 h-7 rounded-lg bg-indigo-50 border border-indigo-200 flex items-center justify-center">
                <Plus className="w-4 h-4 text-indigo-700 stroke-[2.5]" />
              </div>
            ) : (
              <span className="flex items-center space-x-1 px-2 py-0.5 rounded-md bg-slate-100 border border-slate-200 text-slate-500 text-[10px] font-semibold">
                <Lock className="w-3 h-3 text-slate-500" />
                <span>Locked</span>
              </span>
            )}
          </div>
        </button>
        )}

        {/* 3. Create Quarterly Progress Report (QPR) */}
        <button
          type="button"
          id="create-qpr-btn-mobile"
          disabled={!canCreateQpr}
          onClick={canCreateQpr ? () => handleLaunchCreateDoc('QPR') : undefined}
          title={
            canCreateQpr
              ? 'Create Quarterly Progress Report (QPR Summary & Outcomes)'
              : 'Clinical note authoring is restricted to authorized clinicians.'
          }
          className={`relative flex items-center justify-between p-3.5 rounded-xl text-left overflow-hidden transition-all duration-200 ${
            canCreateQpr
              ? 'group cursor-pointer bg-white border border-slate-200/90 border-l-[3px] border-l-emerald-500 shadow-xs active:scale-[0.99]'
              : 'bg-slate-50 border border-slate-200/90 opacity-70 cursor-not-allowed select-none'
          }`}
        >
          <div className="flex items-center space-x-3 min-w-0 flex-1 pr-2 z-10">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border ${canCreateQpr ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-slate-100 border-slate-200 text-slate-400'}`}>
              <Target className="w-5 h-5 stroke-[2.2]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-bold text-slate-800 text-sm tracking-tight truncate">Create Quarterly Progress Report</div>
              <div className="text-[11px] text-slate-600 truncate">QPR Summary &amp; Outcomes</div>
            </div>
          </div>
          <div className="shrink-0 z-10">
            {canCreateQpr ? (
              <div className="w-7 h-7 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                <Plus className="w-4 h-4 text-emerald-700 stroke-[2.5]" />
              </div>
            ) : (
              <span className="flex items-center space-x-1 px-2 py-0.5 rounded-md bg-slate-100 border border-slate-200 text-slate-500 text-[10px] font-semibold">
                <Lock className="w-3 h-3 text-slate-500" />
                <span>Locked</span>
              </span>
            )}
          </div>
        </button>

      </div>
      )}

      {/* Non-authoring role notice in Mobile CREATE mode */}
      {!canAuthorClinicalDocs && mobileMode === 'CREATE' && (
        <div className="sm:hidden p-8 rounded-xl bg-[#0c1424]/80 border border-slate-700/70 text-center text-xs text-slate-400 shadow-[0_4px_16px_rgba(0,0,0,0.3)]">
          <Lock className="w-8 h-8 text-slate-500 mx-auto mb-2" />
          <p className="font-semibold text-slate-300">Clinical Note Authoring Restricted</p>
          <p className="text-[11px] text-slate-400 mt-1">
            Clinical document creation is restricted to authorized clinicians.
          </p>
        </div>
      )}

      {/* Main Grid: List on Left, Active Document Editor on Right */}
      <div className={`${mobileMode === 'CREATE' ? 'hidden sm:grid' : 'grid'} grid-cols-1 lg:grid-cols-12 gap-6 print:block`}>
        {/* Left: Documents List - On mobile, hidden when document detail is open */}
        <div ref={listColRef} className={`${selectedDoc ? 'hidden lg:block' : 'block'} lg:col-span-4 space-y-3 no-print`}>
          {/* Document-Type Filter Row (Desktop / Tablet) - Placed directly above Search documents */}
          <div className="hidden sm:block bg-white p-1 rounded-xl border border-slate-200/90 shadow-2xs">
            {listColWidth < 360 ? (
              // Compact mode when list column is narrow: Primary types that fit + MORE / In-place BACK + EXTERNAL
              !showDesktopSecondaryFilters ? (
                <div className="flex items-center justify-between gap-1 w-full">
                  {(['EVAL', 'POC', 'QPR', 'SOAP', 'INTAKE'] as const).map((tag) => {
                    const isSelected = docTypeFilter === tag;
                    const accentDot =
                      tag === 'EVAL' ? 'bg-blue-400' :
                      tag === 'POC' ? 'bg-indigo-400' :
                      tag === 'QPR' ? 'bg-emerald-400' :
                      tag === 'SOAP' ? 'bg-cyan-400' : 'bg-teal-400';
                    return (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => setDocTypeFilter((prev) => (prev === tag ? 'ALL' : tag))}
                        className={`flex-1 min-w-0 py-1.5 px-0.5 rounded-lg text-[10px] font-mono font-bold tracking-tight transition-all cursor-pointer border flex items-center justify-center gap-1 whitespace-nowrap ${
                          isSelected
                            ? 'bg-slate-800 text-white border-slate-800 shadow-2xs'
                            : 'bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border-slate-200 shadow-2xs'
                        }`}
                        title={`Filter by ${tag}`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${accentDot} ${isSelected ? 'opacity-100 ring-1 ring-sky-300/60' : 'opacity-75'}`} />
                        <span className="truncate">{tag}</span>
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    id="clinical-doc-type-more-btn-desktop"
                    onClick={() => setShowDesktopSecondaryFilters(true)}
                    className={`flex-1 min-w-0 py-1.5 px-0.5 rounded-lg text-[10px] font-mono font-bold tracking-tight transition-all cursor-pointer border flex items-center justify-center gap-0.5 whitespace-nowrap ${
                      docTypeFilter === 'EXTERNAL'
                        ? 'bg-slate-800 text-white border-slate-800 shadow-2xs'
                        : 'bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border-slate-200 shadow-2xs'
                    }`}
                    title="More document types"
                  >
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${docTypeFilter === 'EXTERNAL' ? 'bg-violet-400 ring-1 ring-sky-300/60' : 'bg-slate-500'}`} />
                    <span>MORE</span>
                    <ChevronRight className="w-2.5 h-2.5 shrink-0 opacity-75" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 w-full">
                  <button
                    type="button"
                    id="clinical-doc-type-back-btn-desktop"
                    onClick={() => setShowDesktopSecondaryFilters(false)}
                    className="py-1.5 px-2.5 rounded-lg text-[10px] font-mono font-bold tracking-tight text-slate-700 hover:text-slate-900 bg-white hover:bg-slate-50 border border-slate-200 transition-all flex items-center justify-center gap-1 cursor-pointer shrink-0"
                    title="Back to primary document types"
                  >
                    <ChevronLeft className="w-3 h-3 text-slate-600" />
                    <span>BACK</span>
                  </button>
                  <button
                    type="button"
                    id="clinical-doc-type-external-btn-desktop"
                    onClick={() => setDocTypeFilter((prev) => (prev === 'EXTERNAL' ? 'ALL' : 'EXTERNAL'))}
                    className={`flex-1 min-w-0 py-1.5 px-2 rounded-lg text-[10.5px] font-mono font-bold tracking-tight transition-all cursor-pointer border flex items-center justify-center gap-1.5 whitespace-nowrap ${
                      docTypeFilter === 'EXTERNAL'
                        ? 'bg-slate-800 text-white border-slate-800 shadow-2xs'
                        : 'bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border-slate-200 shadow-2xs'
                    }`}
                    title="Filter by External Documents"
                  >
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 bg-violet-400 ${docTypeFilter === 'EXTERNAL' ? 'opacity-100 ring-1 ring-sky-300/60' : 'opacity-75'}`} />
                    <span>EXTERNAL</span>
                    {docTypeFilter === 'EXTERNAL' && <Check className="w-3.5 h-3.5 text-white ml-0.5" />}
                  </button>
                </div>
              )
            ) : (
              // Direct visible types: EVAL, POC, QPR, SOAP, INTAKE, EXTERNAL
              <div className="flex items-center justify-between gap-1 w-full">
                {(['EVAL', 'POC', 'QPR', 'SOAP', 'INTAKE', 'EXTERNAL'] as const).map((tag) => {
                  const isSelected = docTypeFilter === tag;
                  const accentDot =
                    tag === 'EVAL' ? 'bg-blue-400' :
                    tag === 'POC' ? 'bg-indigo-400' :
                    tag === 'QPR' ? 'bg-emerald-400' :
                    tag === 'SOAP' ? 'bg-cyan-400' :
                    tag === 'INTAKE' ? 'bg-teal-400' : 'bg-violet-400';
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => setDocTypeFilter((prev) => (prev === tag ? 'ALL' : tag))}
                      className={`flex-1 min-w-0 py-1.5 px-1 rounded-lg text-[10.5px] font-mono font-bold tracking-tight transition-all cursor-pointer border flex items-center justify-center gap-1 whitespace-nowrap ${
                        isSelected
                          ? 'bg-slate-800 text-white border-slate-800 shadow-2xs'
                          : 'bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border-slate-200 shadow-2xs'
                      }`}
                      title={`Filter by ${tag}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${accentDot} ${isSelected ? 'opacity-100 ring-1 ring-sky-300/60' : 'opacity-75'}`} />
                      <span className="truncate">{tag}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Document Search Shell (Desktop / Tablet) */}
          <div className="hidden sm:block bg-white p-1.5 rounded-xl border border-slate-200/90 text-[11px] shadow-2xs">
            <div className="relative flex items-center gap-1.5 px-2.5 py-1.5 w-full rounded-lg border border-slate-200 hover:border-slate-300 bg-slate-50/80 focus-within:border-slate-400 focus-within:ring-1 focus-within:ring-slate-300 transition-all">
              <div className="flex items-center shrink-0 select-none">
                <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="11" cy="11" r="7" className="stroke-slate-500" />
                  <path d="M21 21l-4.35-4.35" className="stroke-slate-500" />
                </svg>
              </div>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search documents..."
                className="w-full bg-transparent text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none tracking-tight font-medium min-w-0"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="p-0.5 rounded text-slate-400 hover:text-slate-700 transition-colors shrink-0 cursor-pointer"
                  title="Clear search"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          <div className={`relative rounded-xl overflow-hidden border max-h-[720px] overflow-y-auto ${
            isClinicalStaff
              ? 'bg-white border-slate-200/90 divide-y divide-slate-100 shadow-2xs'
              : isClinicalLightMobile
              ? 'bg-white border-slate-200/90 divide-y divide-slate-100 shadow-[0_1px_3px_rgba(15,23,42,0.06)]'
              : 'border-slate-700/80 bg-gradient-to-b from-[#141f32] via-[#101726] to-[#0c121e] shadow-xl shadow-black/30 divide-y divide-slate-700/60'
          }`}>
            {/* Cool Silver / Blue Top Hairline Shimmer */}
            {!isClinicalStaff && !isClinicalLightMobile && (
              <div className="sticky top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-slate-400/20 to-transparent pointer-events-none z-10" />
            )}
            {loading ? (
              <div className={`p-8 text-center text-xs ${isClinicalStaff || isClinicalLightMobile ? 'text-slate-600' : 'text-slate-300'}`}>Loading clinical documents...</div>
            ) : filteredDocs.length === 0 ? (
              <div className={`p-8 text-center text-xs ${isClinicalStaff || isClinicalLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
                <FileText className={`w-8 h-8 mx-auto mb-2 ${isClinicalStaff || isClinicalLightMobile ? 'text-slate-400' : 'text-slate-500'}`} />
                <p className={`font-semibold ${isClinicalStaff || isClinicalLightMobile ? 'text-slate-800' : 'text-slate-300'}`}>No clinical documents found.</p>
                <p className={`text-[11px] mt-1 ${isClinicalStaff || isClinicalLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
                  Click &ldquo;Create Document&rdquo; to draft an Evaluation, POC, or QPR.
                </p>
              </div>
            ) : (
              filteredDocs.map((doc: any, idx: number) => {
                const isSelected = selectedDoc?.id === doc.id;
                const isIntakeDoc = doc.doc_type === 'INTAKE_PACKET' || doc.doc_type === 'DDD_PACKET';
                const isSlpa = doc.provider_role === 'SLPA' || (doc.provider_title && doc.provider_title.includes('SLPA'));
                
                const isCleared = documentFullySignedAndCleared(doc);
                const isReturned = doc.clearance_status === 'NEEDS_CORRECTION' || doc.supervisor_approval_status === 'RETURNED';
                const isPendingGuardian = (doc.clearance_status === 'PENDING_GUARDIAN' && !isAuthoredPlanDoc(doc.doc_type)) || ((isAuthoredEvalDoc(doc.doc_type) || doc.doc_type === 'SOAP') && !doc.guardian_signature && doc.guardian_signature_status !== 'WAIVED_DOCUMENTED' && doc.doc_type !== 'QPR');
                const isPendingSupervisor = isSlpa && doc.clearance_status === 'PENDING_SUPERVISOR';
                const isOverdue = !isAuthoredPlanDoc(doc.doc_type) && (doc.is_guardian_signature_overdue || doc.guardian_signature_status === 'OVERDUE');
                const isSentForGuardian = !isAuthoredPlanDoc(doc.doc_type) && (doc.esign_status === 'SENT' || doc.esign_status === 'PENDING' || doc.guardian_signature_status === 'SENT_FOR_SIGNATURE') && !doc.guardian_signature;

                const displayTitle = getCleanDocDisplayTitle(doc);
                const clientDisplayName = doc.client_name
                  ? doc.client_name
                  : (doc.first_name && doc.last_name
                      ? `${doc.first_name} ${doc.last_name}`
                      : (doc.last_name && doc.first_name
                          ? `${doc.last_name}, ${doc.first_name}`
                          : (doc.first_name || doc.last_name || 'Client Record')));

                return (
                  <div
                    key={doc.id}
                    onClick={() => { void openDocument(doc.id, doc); }}
                    className={`group relative overflow-hidden px-3 py-2 sm:px-3.5 sm:py-2.5 cursor-pointer transition-colors duration-150 border-l-[3px] select-none ${
                      isSelected
                        ? isClinicalStaff || isClinicalLightMobile
                          ? 'bg-slate-100 border-l-slate-800 shadow-2xs'
                          : isAssistantMobile
                          ? 'bg-cyan-50/70 border-l-cyan-600 shadow-xs sm:ops-row-active sm:border-l-cyan-400 sm:shadow-[inset_0_1px_0_0_rgba(103,232,249,0.3)]'
                          : isSupervisorMobile
                          ? 'bg-purple-50/70 border-l-purple-600 shadow-xs sm:ops-row-active sm:border-l-cyan-400 sm:shadow-[inset_0_1px_0_0_rgba(103,232,249,0.3)]'
                          : 'ops-row-active border-l-cyan-400 shadow-[inset_0_1px_0_0_rgba(103,232,249,0.3)]'
                        : isClinicalStaff || isClinicalLightMobile
                        ? 'bg-white hover:bg-slate-50/80 border-l-transparent text-slate-800'
                        : idx % 2 === 0
                        ? 'ops-row-a border-l-transparent hover:border-l-cyan-400/80 hover:bg-[#20314a] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]'
                        : 'ops-row-b border-l-transparent hover:border-l-cyan-400/80 hover:bg-[#20314a] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]'
                    }`}
                  >
                    {/* Subtle chrome/silver sheen across the row on hover */}
                    {!isClinicalStaff && !isClinicalLightMobile && (
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.03] to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out pointer-events-none" />
                    )}

                    {/* Desktop / Tablet (sm+): Simplified Roster Index Row */}
                    <div className="hidden sm:block space-y-1">
                      {/* Top Row: Client Name on left, ONE meaningful overall status on far-right */}
                      <div className="flex items-center justify-between gap-2 min-w-0">
                        <span
                          className={`font-semibold text-xs truncate min-w-0 flex-1 ${
                            isSelected
                              ? isClinicalStaff || isClinicalLightMobile
                                ? 'text-slate-900 font-bold'
                                : 'text-cyan-200 font-bold'
                              : isClinicalStaff || isClinicalLightMobile
                              ? 'text-slate-800 group-hover:text-slate-950'
                              : 'text-slate-100 group-hover:text-cyan-300'
                          }`}
                          title={clientDisplayName}
                        >
                          {clientDisplayName}
                        </span>

                        {/* Far Right: ONE meaningful overall status badge */}
                        <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded font-bold uppercase shrink-0 whitespace-nowrap ${
                          isCleared
                            ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                            : isReturned
                            ? 'bg-rose-50 text-rose-800 border border-rose-200'
                            : isOverdue
                            ? 'bg-rose-50 text-rose-800 border border-rose-200'
                            : isPendingSupervisor
                            ? 'bg-indigo-50 text-indigo-800 border border-indigo-200'
                            : isPendingGuardian
                            ? 'bg-amber-50 text-amber-800 border border-amber-200'
                            : 'bg-slate-50 text-slate-700 border border-slate-200'
                        }`}>
                          {isCleared
                            ? 'CLEARED'
                            : isReturned
                            ? 'NEEDS REVISION'
                            : isOverdue
                            ? 'GUARDIAN OVERDUE'
                            : isPendingSupervisor
                            ? 'PENDING CO-SIGN'
                            : isPendingGuardian
                            ? (isSentForGuardian ? 'WAITING GUARDIAN' : 'GUARDIAN SIG REQ')
                            : 'PENDING PROVIDER'}
                        </span>
                      </div>

                      {/* Second Row: [ TYPE TAG ]   DOS: date */}
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded font-bold uppercase shrink-0 ${
                          isIntakeDoc
                            ? 'bg-teal-50 text-teal-800 border border-teal-200'
                            : 'bg-slate-50 text-slate-700 border border-slate-200'
                        }`}>
                          {formatDocTypeLabel(doc.doc_type).toUpperCase()}
                        </span>
                        <span className="text-[10px] font-mono text-slate-500 leading-tight shrink-0">
                          DOS: {formatCompactDos(doc.dos, doc.created_at) || 'N/A'}
                        </span>
                      </div>
                    </div>

                    {/* Mobile (< sm): Ultra-Compact 1-Row List Item (Client Name, Type, DOS) */}
                    <div className="sm:hidden flex items-center justify-between gap-2 min-w-0">
                      {/* 1. Client Name with priority width */}
                      <span
                        className={`font-semibold text-xs truncate min-w-0 flex-1 ${
                          isSelected
                            ? isAssistantMobile
                              ? 'text-cyan-950 font-bold'
                              : isSupervisorMobile
                              ? 'text-purple-950 font-bold'
                              : 'text-cyan-200 font-bold'
                            : isAssistantMobile
                            ? 'text-slate-900 group-hover:text-cyan-900'
                            : isSupervisorMobile
                            ? 'text-slate-900 group-hover:text-purple-900'
                            : 'text-slate-100 group-hover:text-cyan-300'
                        }`}
                        title={clientDisplayName}
                      >
                        {clientDisplayName}
                      </span>

                      {/* 2. Document Type Tag */}
                      <span
                        className={`text-[9px] font-mono px-1.5 py-0.5 rounded font-bold uppercase shrink-0 ${
                          isClinicalLightMobile
                            ? isIntakeDoc
                              ? 'bg-teal-50 text-teal-800 border border-teal-200'
                              : isAuthoredEvalDoc(doc.doc_type)
                              ? 'bg-blue-50 text-blue-800 border border-blue-200'
                              : isAuthoredPlanDoc(doc.doc_type)
                              ? 'bg-indigo-50 text-indigo-800 border border-indigo-200'
                              : doc.doc_type === 'QPR'
                              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                              : 'bg-sky-50 text-sky-800 border border-sky-200'
                            : isIntakeDoc
                            ? 'bg-teal-500/10 text-teal-300 border border-teal-500/25'
                            : 'bg-sky-500/10 text-sky-300 border border-sky-500/25'
                        }`}
                      >
                        {formatDocTypeLabel(doc.doc_type).toUpperCase()}
                      </span>

                      {/* 3. DOS */}
                      <span className={`text-[10px] font-mono shrink-0 tabular-nums ${
                        isClinicalLightMobile ? 'text-slate-500' : 'text-slate-400'
                      }`}>
                        {formatDisplayDate((doc.dos || doc.document_date || doc.created_at || '').slice(0, 10)) || 'N/A'}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right: Selected Document Review, Inline Edit & Signing Panel */}
        <div className={`${selectedDoc ? 'block' : 'hidden lg:block'} lg:col-span-8 print:w-full print:block`}>
          {selectedDoc ? (
            <div className={`relative rounded-xl border overflow-hidden space-y-4 print:border-none print:bg-white print:shadow-none print:p-0 print:space-y-3 ${
              isClinicalStaff || isClinicalLightMobile
                ? 'bg-white border-slate-200/90 shadow-2xs'
                : 'border-slate-700/80 bg-gradient-to-b from-[#18263c] via-[#141f32] to-[#101929] shadow-[0_12px_36px_rgba(0,0,0,0.55),0_0_0_1px_rgba(148,163,184,0.12),inset_0_1px_0_0_rgba(255,255,255,0.12)]'
            }`}>
              {/* Mobile Back Button: List → Detail → Back */}
              <div className={`lg:hidden no-print p-3 border-b flex items-center justify-between ${
                isClinicalLightMobile
                  ? 'bg-slate-50 border-slate-200/90'
                  : 'border-slate-700/80 bg-gradient-to-b from-[#1e2e48] to-[#152338]'
              }`}>
                <button
                  type="button"
                  onClick={() => setSelectedDoc(null)}
                  className={`flex items-center space-x-2 text-xs font-semibold active:scale-95 transition-all cursor-pointer ${
                    isAssistantMobile
                      ? 'text-cyan-700 hover:text-cyan-900'
                      : isSupervisorMobile
                      ? 'text-purple-700 hover:text-purple-900'
                      : 'text-sky-400 hover:text-sky-300'
                  }`}
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Back to Documents</span>
                </button>
                <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded uppercase ${
                  isAssistantMobile
                    ? 'bg-cyan-50 text-cyan-800 border border-cyan-200'
                    : isSupervisorMobile
                    ? 'bg-purple-100/70 text-purple-800 border border-purple-200'
                    : 'bg-sky-500/10 text-sky-300 border border-sky-500/30'
                }`}>
                  {selectedDoc.doc_type}
                </span>
              </div>

              {/* Desktop Admin Return Action: Back to Period Overview (Owner / RC) */}
              {isOwnerOrRc && (
                <div className="hidden lg:flex no-print px-5 py-2.5 border-b border-slate-700/80 bg-[#1a2a42] items-center justify-between">
                  <button
                    type="button"
                    id="clinical-back-to-period-overview-btn"
                    onClick={() => setSelectedDoc(null)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-white border border-white/20 hover:border-white/30 text-xs font-bold transition-all cursor-pointer shadow-2xs active:scale-95"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    <span>Back to Period Overview</span>
                  </button>
                  <div className="flex items-center gap-2 text-[11px] font-mono text-slate-300">
                    <span>{resolvedPeriod.periodLabel}</span>
                    {selectedProviderDisplayName !== 'All Therapists' && (
                      <span className="text-white font-semibold">• {selectedProviderDisplayName}</span>
                    )}
                    {selectedClientDisplayName !== 'All Clients' && (
                      <span className="text-white font-semibold">• {selectedClientDisplayName}</span>
                    )}
                  </div>
                </div>
              )}

              {/* Cool Silver-Blue / Chrome Top Hairline Specular Reflection */}
              {!isClinicalStaff && !isClinicalLightMobile && (
                <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-slate-300/35 to-transparent pointer-events-none z-10 no-print" />
              )}

              {/* Clean Clinical Encounter Letterhead for Print/PDF (SOAP Notes) */}
              {selectedDoc.doc_type === 'SOAP' && (
                <div className="hidden print:block border-b-2 border-slate-800 pb-3 mb-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-xl font-black text-slate-900 tracking-tight">ALLEGIANT THERAPY</div>
                      <div className="text-xs font-bold text-slate-700 uppercase tracking-widest mt-0.5">
                        Daily treatment note
                      </div>
                    </div>
                    <div className="text-right text-[11px] text-slate-700 leading-tight">
                      <div><strong>Document ID:</strong> {selectedDoc.id}</div>
                      <div><strong>Date of service:</strong> {mapDailyTreatmentNote(selectedDoc).session.dateOfService}</div>
                      <div><strong>Duration:</strong> {mapDailyTreatmentNote(selectedDoc).session.durationMinutes} min</div>
                      <div><strong>Place of service:</strong> {mapDailyTreatmentNote(selectedDoc).session.placeOfService}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Doc Header (On-Screen Interactive Viewer) */}
              <div
                ref={detailHeaderRef}
                className={`p-4 sm:p-5 border-b no-print min-w-0 ${
                  isClinicalStaff || isClinicalLightMobile
                    ? 'bg-white border-slate-200/90'
                    : 'bg-gradient-to-b from-[#18263c] to-[#121d2e] border-slate-700/80'
                }`}
              >
                {(() => {
                  const displayTitle = getCleanDocDisplayTitle(selectedDoc);
                  const clientName = selectedDoc.client_name
                    || (selectedDoc.first_name && selectedDoc.last_name ? `${selectedDoc.first_name} ${selectedDoc.last_name}` : (selectedDoc.first_name || selectedDoc.last_name || 'Client Record'));
                  const providerDisplay = selectedDoc.provider_name
                    ? (selectedDoc.provider_role && !selectedDoc.provider_name.includes(selectedDoc.provider_role)
                        ? `${selectedDoc.provider_name}, ${selectedDoc.provider_role}`
                        : selectedDoc.provider_name)
                    : 'Allegiant Clinical Team';
                  const dosDisplay = selectedDoc.dos || selectedDoc.document_date || selectedDoc.created_at?.slice(0, 10) || 'N/A';
                  const hasPdfToggle = Boolean(
                    selectedDoc.file_content ||
                    selectedDoc.file_name ||
                    isAuthoredEvalDoc(selectedDoc.doc_type) ||
                    isAuthoredPlanDoc(selectedDoc.doc_type) ||
                    selectedDoc.doc_type === 'QPR'
                  ) && !isUploadedOrExternal;

                  // Mobile intake display alias: for INTAKE_PACKET on mobile only, show "Allegiant Intake Packet"
                  const mobileDisplayTitle = selectedDoc.doc_type === 'INTAKE_PACKET' ? 'Allegiant Intake Packet' : displayTitle;

                  // Desktop action rail dynamic responsiveness:
                  // Primary actions: Download PDF, View Chart, Edit (if draft)
                  // Secondary actions: Export (if operational), Print (if operational), Viewer/PDF (if toggleable), Fax (if authorized)
                  const hasAuthorizedSecondaryActions = Boolean(
                    isOperationalUser ||
                    hasPdfToggle ||
                    canOperationalFax
                  );

                  // Calculate estimated width required to display all authorized actions inline:
                  // Base padding and gap allowances:
                  // - Download PDF (~125px) + View Chart (~105px) = 230px
                  // - Edit draft button (~70px or ~135px when editing)
                  // - Secondary actions: Export (~75px), Print (~70px), Viewer toggle (~110px), Fax (~65px)
                  // - Plus gap-2 (8px) between each action
                  const estActionWidth =
                    125 + // Download PDF
                    105 + // View Chart
                    (canEditDraft ? (isEditing ? 140 : 70) : 0) +
                    (isOperationalUser ? 75 : 0) + // Export
                    (isOperationalUser ? 70 : 0) + // Print
                    (hasPdfToggle ? 110 : 0) + // Viewer toggle
                    (canOperationalFax ? 65 : 0) + // Fax
                    24; // safety margin for padding/gaps

                  // Constrained width threshold: only active when authorized secondary actions actually exist
                  // AND the container width cannot fit all authorized actions inline without wrapping
                  const isActionRailConstrained = hasAuthorizedSecondaryActions && (detailHeaderWidth < estActionWidth);

                  return (
                    <div className="space-y-3 min-w-0">
                      {/* ================================================== */}
                      {/* B. MOBILE SELECTED DOCUMENT HEADER (sm:hidden) */}
                      {/* ================================================== */}
                      <div className={`sm:hidden p-3.5 rounded-xl space-y-2.5 text-left min-w-0 ${
                        isClinicalLightMobile
                          ? 'bg-slate-50 border border-slate-200/90 shadow-xs'
                          : 'bg-gradient-to-b from-[#0b121e] to-[#070b13] border border-slate-700/80 shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_2px_8px_rgba(0,0,0,0.4)]'
                      }`}>
                        {/* 1. TOP LINE: CLIENT NAME (Strongest) */}
                        <h2
                          className={`text-base font-black tracking-wide uppercase leading-tight truncate ${
                            isClinicalLightMobile ? 'text-slate-900' : 'text-white'
                          }`}
                          title={`Client: ${clientName}`}
                        >
                          {clientName.toUpperCase()}
                        </h2>

                        {/* 2. SECOND LINE: FULL DOCUMENT TITLE */}
                        <div className={`text-sm font-semibold leading-snug break-words ${
                          isClinicalLightMobile ? 'text-slate-800' : 'text-slate-200'
                        }`}>
                          {mobileDisplayTitle}
                        </div>

                        {/* 3. THIRD LINE: DOS • TREATING THERAPIST */}
                        <div className={`text-xs font-medium flex items-center justify-start gap-1.5 flex-wrap ${
                          isClinicalLightMobile ? 'text-slate-500' : 'text-slate-400'
                        }`}>
                          <span className={isClinicalLightMobile ? 'text-slate-800 font-semibold' : 'text-slate-300 font-semibold'}>
                            DOS {dosDisplay}
                          </span>
                          <span className={isClinicalLightMobile ? 'text-slate-400' : 'text-slate-600'}>•</span>
                          <span className={isClinicalLightMobile ? 'text-slate-600' : 'text-slate-400'}>{providerDisplay}</span>
                        </div>

                        {/* 4. MOBILE ACTION RAIL: 3-BUTTON RAIL WITH INLINE SWAP */}
                        <div className="flex items-center justify-between gap-1.5 pt-0.5 w-full">
                          {!docActionsSecondaryMode ? (
                            /* State 1 (Default / Canonical): [Download PDF] [View Chart] [More] */
                            <>
                              {/* 1. Download PDF */}
                              <button
                                type="button"
                                onClick={() => handleDownloadPdf(selectedDoc)}
                                disabled={downloadingPdf}
                                className={`flex-1 min-w-0 h-8 px-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1 border transition-all focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap ${
                                  isClinicalLightMobile
                                    ? 'bg-white hover:bg-slate-100 text-slate-800 border-slate-200 shadow-xs'
                                    : 'bg-gradient-to-b from-slate-800 via-slate-850 to-slate-900 hover:from-slate-750 hover:to-slate-850 text-slate-200 hover:text-white border-slate-600/80 shadow-[0_2px_8px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.1)] hover:border-slate-500 focus:ring-1 focus:ring-slate-400/50'
                                }`}
                                title="Download official Clinical Document PDF"
                              >
                                {downloadingPdf ? (
                                  <Loader2 className="w-3.5 h-3.5 text-rose-500 animate-spin shrink-0" />
                                ) : (
                                  <FileText className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                                )}
                                <span className="truncate">{downloadingPdf ? 'Downloading...' : 'Download PDF'}</span>
                              </button>

                              {/* 2. View Chart */}
                              <button
                                type="button"
                                onClick={() => onSelectClient(selectedDoc.client_id)}
                                className={`flex-1 min-w-0 h-8 px-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1 border transition-all focus:outline-none cursor-pointer whitespace-nowrap ${
                                  isAssistantMobile
                                    ? 'bg-cyan-50 hover:bg-cyan-100 text-cyan-900 border-cyan-200 shadow-xs'
                                    : isSupervisorMobile
                                    ? 'bg-purple-50 hover:bg-purple-100 text-purple-900 border-purple-200 shadow-xs'
                                    : 'bg-gradient-to-b from-[#0c1828] via-[#091422] to-[#060c16] hover:from-[#112238] hover:to-[#0c1828] text-cyan-300 hover:text-cyan-200 border-cyan-500/40 hover:border-cyan-400/80 shadow-[0_2px_8px_rgba(6,182,212,0.18),inset_0_1px_0_rgba(34,211,238,0.15)] focus:ring-1 focus:ring-cyan-400/50'
                                }`}
                                title="View client electronic health record chart"
                              >
                                <User className={`w-3.5 h-3.5 shrink-0 ${isAssistantMobile ? 'text-cyan-600' : isSupervisorMobile ? 'text-purple-600' : 'text-cyan-400'}`} />
                                <span className="truncate">View Chart</span>
                              </button>

                              {/* 3. More (Inline Swap trigger) */}
                              <button
                                type="button"
                                onClick={() => setDocActionsSecondaryMode(true)}
                                className={`shrink-0 h-8 px-2.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1 border transition-all cursor-pointer whitespace-nowrap ${
                                  isClinicalLightMobile
                                    ? 'bg-white hover:bg-slate-100 text-slate-700 border-slate-200 shadow-xs'
                                    : 'bg-gradient-to-b from-slate-800 to-slate-900 hover:from-slate-750 hover:to-slate-850 text-slate-300 hover:text-white border-slate-700/80 hover:border-slate-600 shadow-[0_2px_6px_rgba(0,0,0,0.25)]'
                                }`}
                                title="Show additional actions (Export, Print)"
                              >
                                <MoreHorizontal className={`w-3.5 h-3.5 shrink-0 ${isClinicalLightMobile ? 'text-slate-600' : 'text-slate-400'}`} />
                                <span>More</span>
                              </button>
                            </>
                          ) : (
                            /* State 2 (Swapped in-place): [Back] [Export] [Print] (with optional Fax/Viewer) */
                            <>
                              {/* Back (Inline Swap back trigger) */}
                              <button
                                type="button"
                                onClick={() => setDocActionsSecondaryMode(false)}
                                className={`h-8 px-3 rounded-lg text-xs font-semibold flex items-center justify-center space-x-1 border shadow-xs transition-all shrink-0 cursor-pointer whitespace-nowrap ${
                                  isClinicalLightMobile
                                    ? 'bg-white hover:bg-slate-100 text-slate-700 border-slate-200'
                                    : 'bg-slate-800 hover:bg-slate-750 text-slate-200 hover:text-white border-slate-600/80'
                                }`}
                                title="Return to primary actions"
                              >
                                <ArrowLeft className={`w-3.5 h-3.5 ${isClinicalLightMobile ? 'text-slate-600' : 'text-slate-400'}`} />
                                <span>Back</span>
                              </button>

                              {/* Export */}
                              {isOperationalUser && (
                                <button
                                  type="button"
                                  onClick={handleExportDocument}
                                  className={`flex-1 h-8 px-2.5 rounded-lg text-xs font-semibold flex items-center justify-center space-x-1.5 border shadow-xs transition-all shrink-0 cursor-pointer whitespace-nowrap ${
                                    isClinicalLightMobile
                                      ? 'bg-white hover:bg-slate-100 text-slate-700 border-slate-200'
                                      : 'bg-slate-800/90 hover:bg-slate-750 text-slate-200 hover:text-white border-slate-700 hover:border-slate-600'
                                  }`}
                                  title="Export document summary"
                                >
                                  <Download className={`w-3.5 h-3.5 ${isAssistantMobile ? 'text-cyan-600' : isSupervisorMobile ? 'text-indigo-600' : 'text-indigo-400'}`} />
                                  <span>Export</span>
                                </button>
                              )}

                              {/* Print */}
                              {isOperationalUser && (
                                <button
                                  type="button"
                                  onClick={handlePrintDocument}
                                  className={`flex-1 h-8 px-2.5 rounded-lg text-xs font-semibold flex items-center justify-center space-x-1.5 border shadow-xs transition-all shrink-0 cursor-pointer whitespace-nowrap ${
                                    isClinicalLightMobile
                                      ? 'bg-white hover:bg-slate-100 text-slate-700 border-slate-200'
                                      : 'bg-slate-800/90 hover:bg-slate-750 text-slate-200 hover:text-white border-slate-700 hover:border-slate-600'
                                  }`}
                                  title="Print document"
                                >
                                  <Printer className={`w-3.5 h-3.5 ${isClinicalLightMobile ? 'text-slate-600' : 'text-slate-300'}`} />
                                  <span>Print</span>
                                </button>
                              )}

                              {/* Optional: Fax Doc if operational fax enabled */}
                              {canOperationalFax && (
                                <button
                                  type="button"
                                  onClick={() => setShowFaxModal(true)}
                                  className={`h-8 px-2.5 rounded-lg text-xs font-semibold flex items-center justify-center space-x-1 border shadow-xs transition-all shrink-0 cursor-pointer whitespace-nowrap ${
                                    isClinicalLightMobile
                                      ? 'bg-white hover:bg-slate-100 text-slate-700 border-slate-200'
                                      : 'bg-slate-800/90 hover:bg-slate-750 text-slate-200 hover:text-white border-slate-700 hover:border-slate-600'
                                  }`}
                                  title="Fax clinical document"
                                >
                                  <Share2 className={`w-3.5 h-3.5 ${isAssistantMobile ? 'text-teal-600' : isSupervisorMobile ? 'text-purple-600' : 'text-sky-400'}`} />
                                  <span>Fax</span>
                                </button>
                              )}
                            </>
                          )}
                        </div>

                        {/* Authorized Draft Editor on mobile (retained) */}
                        {canEditDraft && (
                          <div className="pt-1 border-t border-slate-800/80">
                            {!isEditing ? (
                              <button
                                type="button"
                                onClick={() => setIsEditing(true)}
                                className="h-7 px-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold flex items-center space-x-1 border border-slate-700 transition-colors cursor-pointer shrink-0 whitespace-nowrap"
                                title="Edit clinical draft content"
                              >
                                <Edit2 className="w-3 h-3 text-sky-400" />
                                <span>Edit Draft Document</span>
                              </button>
                            ) : (
                              <div className="flex space-x-1.5 shrink-0">
                                <button
                                  type="button"
                                  onClick={() => setIsEditing(false)}
                                  className="h-7 px-2.5 rounded-lg bg-slate-800 text-slate-400 hover:text-white text-xs font-semibold cursor-pointer"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={handleSaveEdits}
                                  disabled={savingEdit}
                                  className="h-7 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold flex items-center space-x-1 shadow transition-colors cursor-pointer whitespace-nowrap"
                                >
                                  <Save className="w-3 h-3" />
                                  <span>{savingEdit ? 'Saving...' : 'Save Changes'}</span>
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* ================================================== */}
                      {/* A. DESKTOP / TABLET SELECTED DOCUMENT HEADER (hidden sm:block) */}
                      {/* ONE SINGLE LEFT-JUSTIFIED HIERARCHY */}
                      {/* ================================================== */}
                      <div className="hidden sm:flex flex-col items-start text-left space-y-2 min-w-0">
                        {/* 1. TOP LINE: CLIENT NAME • DOS • TREATING THERAPIST */}
                        {/* Priority: 1. CLIENT NAME (strongest) 2. DOS (next) 3. Treating Therapist (muted) */}
                        <div className="flex items-center justify-start flex-wrap gap-x-2 gap-y-1 text-left min-w-0">
                          {/* CLIENT NAME — Strongest */}
                          <span
                            className="text-base lg:text-lg font-black text-slate-900 tracking-wide uppercase truncate"
                            title={`Client: ${clientName}`}
                          >
                            {clientName.toUpperCase()}
                          </span>

                          <span className="text-slate-400 font-bold">•</span>

                          {/* DOS — Next priority */}
                          <span className="text-sm lg:text-base font-semibold text-slate-700 whitespace-nowrap">
                            DOS {dosDisplay}
                          </span>

                          <span className="text-slate-300">•</span>

                          {/* Treating Therapist — Smaller / Muted */}
                          <span className="text-xs lg:text-sm text-slate-500 font-medium truncate">
                            {providerDisplay}
                          </span>
                        </div>

                        {/* 2. DIRECTLY UNDERNEATH: FULL DOCUMENT TITLE */}
                        <div className="text-sm lg:text-base font-bold text-slate-800 leading-snug break-words">
                          {displayTitle}
                        </div>

                        {/* 3. DIRECTLY UNDERNEATH: ACTION RAIL (shares same left edge) */}
                        <div className="flex items-center justify-start flex-wrap gap-2 pt-1 max-w-full">
                          {/* If desktop has ample room (or no secondary actions hidden), show full inline suite: */}
                          {/* [Download PDF] [View Chart] [Edit (if draft)] [Export] [Print] */}
                          {!isActionRailConstrained ? (
                            <>
                              {/* 1. Download PDF */}
                              <button
                                type="button"
                                onClick={() => handleDownloadPdf(selectedDoc)}
                                disabled={downloadingPdf}
                                className="h-8 px-3 rounded-lg bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold flex items-center space-x-1.5 border border-slate-200 shadow-2xs transition-all shrink-0 hover:border-slate-300 focus:outline-none focus:ring-1 focus:ring-slate-300 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                                title="Download official Clinical Document PDF"
                              >
                                {downloadingPdf ? (
                                  <Loader2 className="w-3.5 h-3.5 text-slate-500 animate-spin" />
                                ) : (
                                  <FileText className="w-3.5 h-3.5 text-slate-500" />
                                )}
                                <span>{downloadingPdf ? 'Downloading PDF...' : 'Download PDF'}</span>
                              </button>

                              {/* 2. View Chart */}
                              <button
                                type="button"
                                onClick={() => onSelectClient(selectedDoc.client_id)}
                                className="h-8 px-3 rounded-lg bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold flex items-center space-x-1.5 border border-slate-200 shadow-2xs transition-all shrink-0 hover:border-slate-300 focus:outline-none focus:ring-1 focus:ring-slate-300 cursor-pointer whitespace-nowrap"
                                title="View client electronic health record chart"
                              >
                                <User className="w-3.5 h-3.5 text-slate-500" />
                                <span>View Chart</span>
                              </button>

                              {/* 3. Edit Document where authorized */}
                              {canEditDraft && (
                                !isEditing ? (
                                  <button
                                    type="button"
                                    onClick={() => setIsEditing(true)}
                                    className="h-8 px-2.5 rounded-lg bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold flex items-center space-x-1 border border-slate-200 shadow-2xs transition-colors cursor-pointer shrink-0 whitespace-nowrap"
                                    title="Edit clinical draft content"
                                  >
                                    <Edit2 className="w-3.5 h-3.5 text-slate-500" />
                                    <span>Edit</span>
                                  </button>
                                ) : (
                                  <div className="flex space-x-1.5 shrink-0">
                                    <button
                                      type="button"
                                      onClick={() => setIsEditing(false)}
                                      className="h-8 px-2.5 rounded-lg bg-white hover:bg-slate-50 text-slate-600 text-xs font-semibold cursor-pointer border border-slate-200"
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      type="button"
                                      onClick={handleSaveEdits}
                                      disabled={savingEdit}
                                      className="h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold flex items-center space-x-1 shadow transition-colors cursor-pointer whitespace-nowrap"
                                    >
                                      <Save className="w-3.5 h-3.5" />
                                      <span>{savingEdit ? 'Saving...' : 'Save'}</span>
                                    </button>
                                  </div>
                                )
                              )}

                              {/* 4. Export */}
                              {isOperationalUser && (
                                <button
                                  type="button"
                                  onClick={handleExportDocument}
                                  className="h-8 px-2.5 rounded-lg bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold flex items-center space-x-1.5 border border-slate-200 shadow-2xs transition-all shrink-0 hover:border-slate-300 cursor-pointer whitespace-nowrap"
                                  title="Export clinical document summary"
                                >
                                  <Download className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                                  <span>Export</span>
                                </button>
                              )}

                              {/* 5. Print */}
                              {isOperationalUser && (
                                <button
                                  type="button"
                                  onClick={handlePrintDocument}
                                  className="h-8 px-2.5 rounded-lg bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold flex items-center space-x-1.5 border border-slate-200 shadow-2xs transition-all shrink-0 hover:border-slate-300 cursor-pointer whitespace-nowrap"
                                  title="Print clinical document"
                                >
                                  <Printer className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                                  <span>Print</span>
                                </button>
                              )}

                              {/* Document Viewer (PDF) Toggle */}
                              {hasPdfToggle && (
                                <button
                                  type="button"
                                  onClick={() => setDocViewMode(prev => prev === 'pdf' ? 'default' : 'pdf')}
                                  className={`h-8 px-2.5 rounded-lg text-xs font-semibold flex items-center space-x-1.5 border transition-all shrink-0 cursor-pointer whitespace-nowrap ${
                                    docViewMode === 'pdf'
                                      ? 'bg-slate-800 border-slate-800 text-white shadow-2xs'
                                      : 'bg-white hover:bg-slate-50 text-slate-700 border-slate-200'
                                  }`}
                                  title={docViewMode === 'pdf' ? 'Switch back to Clinical Record View' : 'View Embedded Document PDF'}
                                >
                                  <FileText className="w-3.5 h-3.5 text-slate-500" />
                                  <span>{docViewMode === 'pdf' ? 'Clinical Note' : 'Viewer (PDF)'}</span>
                                </button>
                              )}

                              {/* Fax Doc */}
                              {canOperationalFax && (
                                <button
                                  type="button"
                                  onClick={() => setShowFaxModal(true)}
                                  className="h-8 px-2.5 rounded-lg bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold flex items-center space-x-1.5 border border-slate-200 shadow-2xs transition-all shrink-0 hover:border-slate-300 cursor-pointer whitespace-nowrap"
                                  title="Fax clinical document"
                                >
                                  <Share2 className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                                  <span>Fax</span>
                                </button>
                              )}
                            </>
                          ) : (
                            /* Constrained Desktop Width: Inline Swap Mode (More <-> Back | Secondary Actions) */
                            !docActionsSecondaryMode ? (
                              /* State 1: [Download PDF] [View Chart] [Edit] [More] */
                              <>
                                <button
                                  type="button"
                                  onClick={() => handleDownloadPdf(selectedDoc)}
                                  disabled={downloadingPdf}
                                  className="h-8 px-3 rounded-lg bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold flex items-center space-x-1.5 border border-slate-200 shadow-2xs transition-all shrink-0 hover:border-slate-300 focus:outline-none focus:ring-1 focus:ring-slate-300 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                                  title="Download official Clinical Document PDF"
                                >
                                  {downloadingPdf ? (
                                  <Loader2 className="w-3.5 h-3.5 text-slate-500 animate-spin" />
                                ) : (
                                  <FileText className="w-3.5 h-3.5 text-slate-500" />
                                )}
                                <span>{downloadingPdf ? 'Downloading...' : 'Download PDF'}</span>
                                </button>

                                <button
                                  type="button"
                                  onClick={() => onSelectClient(selectedDoc.client_id)}
                                  className="h-8 px-3 rounded-lg bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold flex items-center space-x-1.5 border border-slate-200 shadow-2xs transition-all shrink-0 hover:border-slate-300 focus:outline-none focus:ring-1 focus:ring-slate-300 cursor-pointer whitespace-nowrap"
                                  title="View client electronic health record chart"
                                >
                                  <User className="w-3.5 h-3.5 text-slate-500" />
                                  <span>View Chart</span>
                                </button>

                                {canEditDraft && (
                                  !isEditing ? (
                                    <button
                                      type="button"
                                      onClick={() => setIsEditing(true)}
                                      className="h-8 px-2.5 rounded-lg bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold flex items-center space-x-1 border border-slate-200 shadow-2xs transition-colors cursor-pointer shrink-0 whitespace-nowrap"
                                      title="Edit clinical draft content"
                                    >
                                      <Edit2 className="w-3.5 h-3.5 text-slate-500" />
                                      <span>Edit</span>
                                    </button>
                                  ) : (
                                    <div className="flex space-x-1.5 shrink-0">
                                      <button
                                        type="button"
                                        onClick={() => setIsEditing(false)}
                                        className="h-8 px-2.5 rounded-lg bg-white hover:bg-slate-50 text-slate-600 text-xs font-semibold cursor-pointer border border-slate-200"
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        type="button"
                                        onClick={handleSaveEdits}
                                        disabled={savingEdit}
                                        className="h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold flex items-center space-x-1 shadow transition-colors cursor-pointer whitespace-nowrap"
                                      >
                                        <Save className="w-3.5 h-3.5" />
                                        <span>{savingEdit ? 'Saving...' : 'Save'}</span>
                                      </button>
                                    </div>
                                  )
                                )}

                                {/* Inline swap trigger button: More */}
                                <button
                                  type="button"
                                  onClick={() => setDocActionsSecondaryMode(true)}
                                  className="h-8 px-3 rounded-lg bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold flex items-center space-x-1.5 border border-slate-200 shadow-2xs transition-all shrink-0 hover:border-slate-300 cursor-pointer whitespace-nowrap"
                                  title="Show additional actions"
                                >
                                  <MoreHorizontal className="w-3.5 h-3.5 text-slate-400" />
                                  <span>More</span>
                                </button>
                              </>
                            ) : (
                              /* State 2: Swapped in-place to [Back] [Export] [Print] (and optional items) */
                              <>
                                <button
                                  type="button"
                                  onClick={() => setDocActionsSecondaryMode(false)}
                                  className="h-8 px-3 rounded-lg bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold flex items-center space-x-1 border border-slate-200 shadow-2xs transition-all shrink-0 cursor-pointer whitespace-nowrap"
                                  title="Return to primary actions"
                                >
                                  <ArrowLeft className="w-3.5 h-3.5 text-slate-400" />
                                  <span>Back</span>
                                </button>

                                {isOperationalUser && (
                                  <button
                                    type="button"
                                    onClick={handleExportDocument}
                                    className="h-8 px-2.5 rounded-lg bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold flex items-center space-x-1.5 border border-slate-200 shadow-2xs transition-all shrink-0 hover:border-slate-300 cursor-pointer whitespace-nowrap"
                                    title="Export clinical document summary"
                                  >
                                    <Download className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                                    <span>Export</span>
                                  </button>
                                )}

                                {isOperationalUser && (
                                  <button
                                    type="button"
                                    onClick={handlePrintDocument}
                                    className="h-8 px-2.5 rounded-lg bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold flex items-center space-x-1.5 border border-slate-200 shadow-2xs transition-all shrink-0 hover:border-slate-300 cursor-pointer whitespace-nowrap"
                                    title="Print clinical document"
                                  >
                                    <Printer className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                                    <span>Print</span>
                                  </button>
                                )}

                                {hasPdfToggle && (
                                  <button
                                    type="button"
                                    onClick={() => setDocViewMode(prev => prev === 'pdf' ? 'default' : 'pdf')}
                                    className={`h-8 px-2.5 rounded-lg text-xs font-semibold flex items-center space-x-1.5 border transition-all shrink-0 cursor-pointer whitespace-nowrap ${
                                      docViewMode === 'pdf'
                                        ? 'bg-slate-800 border-slate-800 text-white shadow-2xs'
                                        : 'bg-white hover:bg-slate-50 text-slate-700 border-slate-200'
                                    }`}
                                    title={docViewMode === 'pdf' ? 'Switch back to Clinical Record View' : 'View Embedded Document PDF'}
                                  >
                                    <FileText className="w-3.5 h-3.5 text-slate-500" />
                                    <span>{docViewMode === 'pdf' ? 'Clinical Note' : 'Viewer (PDF)'}</span>
                                  </button>
                                )}

                                {canOperationalFax && (
                                  <button
                                    type="button"
                                    onClick={() => setShowFaxModal(true)}
                                    className="h-8 px-2.5 rounded-lg bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold flex items-center space-x-1.5 border border-slate-200 shadow-2xs transition-all shrink-0 hover:border-slate-300 cursor-pointer whitespace-nowrap"
                                    title="Fax clinical document"
                                  >
                                    <Share2 className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                                    <span>Fax</span>
                                  </button>
                                )}
                              </>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* PDF Request Error Banner */}
              {pdfError && (
                <div className="mx-6 p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center justify-between gap-2 no-print">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="w-4 h-4 shrink-0 text-rose-600" />
                    <span>{pdfError}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPdfError(null)}
                    className="text-rose-600 hover:text-rose-800 font-bold px-1.5 py-0.5 rounded hover:bg-rose-100 transition-colors"
                    title="Dismiss"
                  >
                    ✕
                  </button>
                </div>
              )}

              {/* Medical Record Verification & Signatures Status Panel */}
              <div className="mx-6 p-4 rounded-xl bg-slate-50 border border-slate-200/90 space-y-3 no-print">
                {/* Mobile Header: Clean 3-Row Hierarchy (sm:hidden) */}
                <div className="sm:hidden flex flex-col space-y-2 border-b border-slate-200 pb-3">
                  {/* Row 1: Section title/icon treatment only (clean, full-width) */}
                  <div className="flex items-center space-x-2">
                    <ShieldCheck className="w-4 h-4 text-slate-600 shrink-0" />
                    <span className="text-xs font-semibold text-slate-900">
                      Document Verification & Compliance
                    </span>
                  </div>

                  {/* Row 2: Status pill on its own row directly beneath title */}
                  <div className="flex items-center">
                    <span className={`text-[10px] font-mono px-2.5 py-0.5 rounded-full font-bold uppercase ${
                      documentFullySignedAndCleared(selectedDoc)
                        ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                        : (selectedDoc as any).clearance_status === 'NEEDS_CORRECTION' || selectedDoc.supervisor_approval_status === 'RETURNED'
                        ? 'bg-rose-50 text-rose-800 border border-rose-200'
                        : 'bg-amber-50 text-amber-800 border border-amber-200'
                    }`}>
                      {documentFullySignedAndCleared(selectedDoc)
                        ? '✓ Fully Signed & Cleared'
                        : (selectedDoc as any).clearance_status === 'NEEDS_CORRECTION' || selectedDoc.supervisor_approval_status === 'RETURNED'
                        ? 'Action Required: Revision Needed'
                        : 'Pending Signatures'}
                    </span>
                  </div>

                  {/* Row 3: Period label + date range together on ONE row */}
                  {(selectedDoc.pay_period || selectedDoc.is_manual_soap) && (
                    <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                      {selectedDoc.pay_period && (
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-white text-slate-700 border border-slate-200 whitespace-nowrap">
                          Period: {selectedDoc.pay_period.label}
                        </span>
                      )}
                      {selectedDoc.is_manual_soap && (
                        <span
                          title={selectedDoc.manual_soap_reason ? `Manual exception: ${selectedDoc.manual_soap_reason}` : 'Manual exception'}
                          className="text-[10px] font-mono px-2 py-0.5 rounded bg-white text-slate-600 border border-slate-200 cursor-help whitespace-nowrap"
                        >
                          Manual exception
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Desktop/Tablet Header: Original Single Row Layout (hidden sm:flex) */}
                <div className="hidden sm:flex sm:items-center justify-between gap-2 border-b border-slate-200 pb-3">
                  <div className="flex items-center space-x-2">
                    <ShieldCheck className="w-4 h-4 text-slate-600" />
                    <span className="text-xs font-semibold text-slate-900">
                      Document Verification & Compliance
                    </span>
                    {selectedDoc.pay_period && (
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-white text-slate-700 border border-slate-200">
                        Period: {selectedDoc.pay_period.label}
                      </span>
                    )}
                    {selectedDoc.is_manual_soap && (
                      <span
                        title={selectedDoc.manual_soap_reason ? `Manual exception: ${selectedDoc.manual_soap_reason}` : 'Manual exception'}
                        className="text-[10px] font-mono px-2 py-0.5 rounded bg-white text-slate-600 border border-slate-200 cursor-help"
                      >
                        Manual exception
                      </span>
                    )}
                  </div>

                  <span className={`text-[10px] font-mono px-2.5 py-0.5 rounded-full font-bold uppercase self-start sm:self-auto ${
                    documentFullySignedAndCleared(selectedDoc)
                      ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                      : (selectedDoc as any).clearance_status === 'NEEDS_CORRECTION' || selectedDoc.supervisor_approval_status === 'RETURNED'
                      ? 'bg-rose-50 text-rose-800 border border-rose-200'
                      : 'bg-amber-50 text-amber-800 border border-amber-200'
                  }`}>
                    {documentFullySignedAndCleared(selectedDoc)
                      ? '✓ Fully Signed & Cleared'
                      : (selectedDoc as any).clearance_status === 'NEEDS_CORRECTION' || selectedDoc.supervisor_approval_status === 'RETURNED'
                      ? 'Action Required: Revision Needed'
                      : 'Pending Signatures'}
                  </span>
                </div>

                {/* Pre-Cutoff Checklist Items - Mobile (Hard Frozen - 100% Unchanged) */}
                <div className="grid grid-cols-1 gap-2 text-[11px] font-mono sm:hidden">
                  {/* 1. Clinician */}
                  <div className={`p-2 rounded-lg border flex items-center justify-between ${
                    selectedDoc.clinician_signature
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                      : 'bg-white border-slate-200 text-slate-700'
                  }`}>
                    <span>1. Clinician Signature:</span>
                    <strong className="font-semibold">
                      {selectedDoc.clinician_signature ? '✓ Signed' : 'Pending'}
                    </strong>
                  </div>

                  {/* 2. Guardian / Physician */}
                  {isAuthoredPlanDoc(selectedDoc.doc_type) ? (
                    <div className={`p-2 rounded-lg border flex items-center justify-between ${
                      selectedDoc.physician_signature
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                        : 'bg-amber-50 border-amber-200 text-amber-800'
                    }`}>
                      <span>2. Physician Cert (Faxage):</span>
                      <strong className="font-semibold">
                        {selectedDoc.physician_signature ? '✓ Certified' : 'Pending Fax'}
                      </strong>
                    </div>
                  ) : (
                    <div className={`p-2 rounded-lg border flex items-center justify-between ${
                      guardianSignatureShownSigned(selectedDoc) || selectedDoc.guardian_signature_status === 'WAIVED_DOCUMENTED' || selectedDoc.doc_type === 'QPR'
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                        : (selectedDoc as any).is_guardian_signature_overdue || selectedDoc.guardian_signature_status === 'OVERDUE'
                        ? 'bg-rose-50 border-rose-200 text-rose-800'
                        : 'bg-amber-50 border-amber-200 text-amber-800'
                    }`}>
                      <span>2. Guardian Signature:</span>
                      <strong className="font-semibold">
                        {guardianSignatureShownSigned(selectedDoc)
                          ? '✓ Signed'
                          : selectedDoc.guardian_signature_status === 'WAIVED_DOCUMENTED'
                          ? '✓ Waived'
                          : selectedDoc.doc_type === 'QPR'
                          ? '✓ Not Req'
                          : (selectedDoc as any).is_guardian_signature_overdue || selectedDoc.guardian_signature_status === 'OVERDUE'
                          ? 'Overdue'
                          : 'Pending'}
                      </strong>
                    </div>
                  )}

                  {/* 3. Supervisor */}
                  <div className={`p-2 rounded-lg border flex items-center justify-between ${
                    selectedDoc.supervisor_approval_status === 'RETURNED'
                      ? 'bg-rose-50 border-rose-200 text-rose-800'
                      : 'bg-white border-slate-200 text-slate-700'
                  }`}>
                    <span>3. Supervisor Co-Sign:</span>
                    <strong className="font-semibold text-slate-800">
                      {!isSlpaDoc
                        ? 'Not Required (SLP)'
                        : selectedDoc.supervisor_approval_status === 'APPROVED'
                        ? '✓ Approved'
                        : selectedDoc.supervisor_approval_status === 'RETURNED'
                        ? 'Returned'
                        : 'Pending'}
                    </strong>
                  </div>
                </div>

                {/* Pre-Cutoff Checklist Items - Desktop (Clean Single Row) */}
                <div className="hidden sm:grid sm:grid-cols-3 gap-2 font-mono">
                  {/* 1. Clinician */}
                  <div className={`p-2.5 rounded-lg border flex flex-col justify-between ${
                    selectedDoc.clinician_signature
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                      : 'bg-white border-slate-200 text-slate-700'
                  }`}>
                    <span className="text-[11px] text-slate-500 font-medium">1. Clinician Signature</span>
                    <div className="mt-1">
                      <strong className={`text-xs font-semibold block ${selectedDoc.clinician_signature ? 'text-emerald-800' : 'text-slate-800'}`}>
                        {selectedDoc.clinician_signature ? '✓ Signed' : 'Pending'}
                      </strong>
                    </div>
                  </div>

                  {/* 2. Guardian / Physician */}
                  {isAuthoredPlanDoc(selectedDoc.doc_type) ? (
                    <div className={`p-2.5 rounded-lg border flex flex-col justify-between ${
                      selectedDoc.physician_signature
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                        : 'bg-amber-50 border-amber-200 text-amber-800'
                    }`}>
                      <span className="text-[11px] text-slate-500 font-medium">2. Physician Cert</span>
                      <div className="mt-1">
                        <strong className="text-xs font-semibold block">
                          {selectedDoc.physician_signature ? '✓ Certified' : 'Pending Fax'}
                        </strong>
                        <span className="text-[10px] text-slate-500 font-normal block leading-tight">Faxage</span>
                      </div>
                    </div>
                  ) : (
                    <div className={`p-2.5 rounded-lg border flex flex-col justify-between ${
                      guardianSignatureShownSigned(selectedDoc) || selectedDoc.guardian_signature_status === 'WAIVED_DOCUMENTED' || selectedDoc.doc_type === 'QPR'
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                        : (selectedDoc as any).is_guardian_signature_overdue || selectedDoc.guardian_signature_status === 'OVERDUE'
                        ? 'bg-rose-50 border-rose-200 text-rose-800'
                        : 'bg-amber-50 border-amber-200 text-amber-800'
                    }`}>
                      <span className="text-[11px] text-slate-500 font-medium">2. Guardian Signature</span>
                      <div className="mt-1">
                        <strong className="text-xs font-semibold block">
                          {guardianSignatureShownSigned(selectedDoc)
                            ? '✓ Signed'
                            : selectedDoc.guardian_signature_status === 'WAIVED_DOCUMENTED'
                            ? '✓ Waived'
                            : selectedDoc.doc_type === 'QPR'
                            ? '✓ Not Req'
                            : (selectedDoc as any).is_guardian_signature_overdue || selectedDoc.guardian_signature_status === 'OVERDUE'
                            ? ((selectedDoc as any).late_pay_consequence
                              ? GUARDIAN_OVERDUE_PAY_MESSAGE
                              : ((selectedDoc as any).guardian_signature_display_label || 'Guardian Signature — OVERDUE'))
                            : (selectedDoc as any).guardian_deadline_label
                            ? `Due by ${(selectedDoc as any).guardian_deadline_label}`
                            : 'Pending'}
                        </strong>
                      </div>
                    </div>
                  )}

                  {/* 3. Supervisor */}
                  <div className={`p-2.5 rounded-lg border flex flex-col justify-between ${
                    selectedDoc.supervisor_approval_status === 'RETURNED'
                      ? 'bg-rose-50 border-rose-200 text-rose-800'
                      : selectedDoc.supervisor_approval_status === 'APPROVED'
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                      : isSlpaDoc
                      ? 'bg-amber-50 border-amber-200 text-amber-800'
                      : 'bg-white border-slate-200 text-slate-700'
                  }`}>
                    <span className="text-[11px] text-slate-500 font-medium">3. Supervisor Signature</span>
                    <div className="mt-1">
                      <strong className={`text-xs font-semibold block ${
                        selectedDoc.supervisor_approval_status === 'RETURNED'
                          ? 'text-rose-800'
                          : selectedDoc.supervisor_approval_status === 'APPROVED'
                          ? 'text-emerald-800'
                          : isSlpaDoc
                          ? 'text-amber-800'
                          : 'text-slate-800'
                      }`}>
                        {!isSlpaDoc
                          ? 'Not Required'
                          : selectedDoc.supervisor_approval_status === 'APPROVED'
                          ? '✓ Approved'
                          : selectedDoc.supervisor_approval_status === 'RETURNED'
                          ? 'Returned'
                          : 'Pending'}
                      </strong>
                      {!isSlpaDoc && (
                        <span className="text-[10px] text-slate-500 font-normal block leading-tight">
                          SLP
                        </span>
                      )}
                      {isSlpaDoc && (
                        <span className="text-[10px] text-amber-800/80 font-normal block leading-tight">
                          SLPA
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {selectedDoc.pay_period && (
                  <div className="flex flex-wrap items-center justify-between gap-2 text-[10px] text-slate-500 pt-1 border-t border-slate-200 font-mono">
                    <span>Cutoff Date: <strong className="text-slate-800">{formatDisplayDate(selectedDoc.pay_period.cutoffDate)} (Fri)</strong></span>
                    <span>Pay Date: <strong className="text-slate-800">{formatDisplayDate(selectedDoc.pay_period.payDate)} (Wed)</strong></span>
                  </div>
                )}

                {selectedDoc.is_manual_soap && (
                  <div className="flex flex-wrap items-center justify-between gap-2 text-[10px] text-slate-500 pt-1 border-t border-slate-200 font-mono">
                    <span>Audit Flag: <strong className="text-slate-800">Manual exception</strong></span>
                    {selectedDoc.manual_soap_reason && (
                      <span className="text-slate-500">
                        Rationale: <span className="text-slate-700 font-mono">{selectedDoc.manual_soap_reason}</span>
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Prominent Supervisor Returned Revision Callout */}
              {selectedDoc.supervisor_approval_status === 'RETURNED' && (
                <div className="mx-6 p-4 rounded-xl bg-rose-50 border border-rose-200 space-y-2 no-print">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 text-rose-800 font-bold text-xs">
                      <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
                      <span>Document Returned by Supervisor for Revision</span>
                    </div>
                    {!isEditing && selectedDoc.status !== 'FINALIZED' && (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="px-3 py-1 rounded bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs flex items-center space-x-1 shadow-2xs transition-colors"
                      >
                        <Edit2 className="w-3 h-3" />
                        <span>Edit Note Now</span>
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-rose-900 bg-white p-2.5 rounded-lg border border-rose-200">
                    "{selectedDoc.supervisor_return_comments || 'Please review and revise documentation before pay period cutoff.'}"
                  </p>
                  <p className="text-[10px] text-rose-700">
                    Revise the required clinical sections below and save changes to resubmit for supervisor co-signature before the Friday cutoff.
                  </p>
                </div>
              )}

              {/* Supervisory Review Mode Banner */}
              {isSupervisorReview && (
                <div className="mx-4 sm:mx-6 p-2.5 sm:p-3 rounded-xl bg-slate-50 border border-slate-200/90 text-xs text-slate-700 no-print">
                  <div className="flex flex-col gap-1">
                    {/* ROW 1: Title & Badge */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center space-x-2 min-w-0">
                        <ShieldCheck className="w-4 h-4 text-slate-600 shrink-0" />
                        <span className="font-bold text-slate-900 text-xs whitespace-nowrap">Supervisory Review Mode</span>
                      </div>
                      <span className={`px-2 py-0.5 rounded font-mono text-[10px] font-bold uppercase shrink-0 ${
                        selectedDoc.supervisor_approval_status === 'RETURNED'
                          ? 'bg-rose-50 text-rose-800 border border-rose-200'
                          : 'bg-amber-50 text-amber-800 border border-amber-200'
                      }`}>
                        {selectedDoc.supervisor_approval_status === 'RETURNED' ? 'Returned' : 'Pending Co-Sign'}
                      </span>
                    </div>

                    {/* ROW 2: Author dynamic identity & Read-only review note */}
                    <div className="pl-6 text-[11px] text-slate-600 truncate">
                      <strong className="text-slate-900 font-medium">{selectedDoc.provider_name}</strong>
                      <span className="text-slate-500"> · Read-only review</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Next Step Guidance Banner for Eval */}
              {isAuthoredEvalDoc(selectedDoc.doc_type) &&
                !selectedDoc.guardian_signature &&
                selectedDoc.guardian_signature_status !== 'WAIVED_DOCUMENTED' &&
                selectedDoc.esign_status !== 'SENT' &&
                selectedDoc.esign_status !== 'PENDING' && (
                <div className="mx-6 p-3.5 bg-amber-50 border border-amber-200 rounded-xl flex flex-wrap items-center justify-between gap-2 text-xs no-print">
                  <div className="flex items-center space-x-2 text-slate-700">
                    <PenTool className="w-4 h-4 text-amber-700 shrink-0" />
                    <div>
                      <span className="font-bold text-slate-900">Next Step:</span> Dispatch to parent/guardian for electronic signature or complete in-person at point of care.
                    </div>
                  </div>
                  <button
                    onClick={handleOpenEsign}
                    className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs flex items-center space-x-1.5 shadow transition-colors"
                  >
                    <PenTool className="w-3.5 h-3.5" />
                    <span>Send to Guardian for eSign</span>
                  </button>
                </div>
              )}

              {/* Next Step Guidance Banner for POC (Physician Certification / Faxage) */}
              {canFaxClient &&
                isAuthoredPlanDoc(selectedDoc.doc_type) &&
                selectedDoc.clinician_signature &&
                !selectedDoc.physician_signature && (
                <div className="mx-6 p-3.5 bg-slate-50 border border-slate-200/90 rounded-xl flex flex-wrap items-center justify-between gap-2 text-xs no-print">
                  <div className="flex items-center space-x-2 text-slate-700">
                    <Printer className="w-4 h-4 text-slate-600 shrink-0" />
                    <div>
                      <span className="font-bold text-slate-900">Next Step:</span> Transmit finalized Plan of Care to ordering physician via Faxage for medical certification.
                    </div>
                  </div>
                  <button
                    onClick={() => setShowFaxModal(true)}
                    className="px-3 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-500 text-white font-bold text-xs flex items-center space-x-1.5 shadow transition-colors"
                  >
                    <Printer className="w-3.5 h-3.5" />
                    <span>Dispatch POC via Faxage</span>
                  </button>
                </div>
              )}

              {/* Document Content: Uploaded/External Document View vs. Structured Clinical Record */}
              {isPdfViewerActive ? (
                <div className="px-4 sm:px-6 py-4 space-y-4">
                  <div className="p-4 sm:p-6 rounded-2xl bg-white border border-slate-200/90 shadow-2xs space-y-5">
                    {/* Header Panel */}
                    {(selectedDoc.doc_type === 'INTAKE_PACKET' || selectedDoc.doc_type === 'DDD_PACKET' || selectedDoc.doc_type === 'INTAKE') ? (
                      <>
                        {/* Mobile Only: Intake Packet Panel Layout (sm:hidden) */}
                        <div className="sm:hidden space-y-2 pb-4 border-b border-slate-200">
                          {/* 1. Document Title - Full Available Width on its Own Row */}
                          <h3 className="text-sm font-bold text-slate-900 leading-snug break-words w-full">
                            {getCleanDocDisplayTitle(selectedDoc)}
                          </h3>

                          {/* 2. Source / Workflow and DOS / Date - Below Title, Full Width with Natural Wrapping */}
                          <p className="text-xs text-slate-600 leading-relaxed break-words w-full">
                            Source: <strong className="text-slate-800">
                              {selectedDoc.doc_type === 'INTAKE_PACKET' || selectedDoc.doc_type === 'DDD_PACKET'
                                ? 'Digital Client Intake / Dual eSign Workflow'
                                : selectedDoc.source === 'INBOUND_FAX'
                                ? 'Electronic Inbound Fax (Faxage)'
                                : 'Manual Upload / Digital Intake'}
                            </strong>
                            {' '}• DOS / Date: <span className="font-mono text-slate-700">{selectedDoc.dos || selectedDoc.document_date || selectedDoc.created_at?.slice(0, 10)}</span>
                          </p>

                          {/* 3. Document Tag - Visible Below Text */}
                          <div className="pt-0.5">
                            <span className="inline-block px-3 py-1 rounded-md text-xs font-mono font-bold uppercase bg-slate-50 text-slate-700 border border-slate-200">
                              {formatDocTypeLabel(selectedDoc.doc_type)}
                            </span>
                          </div>
                        </div>

                        {/* Desktop: Unchanged Desktop Layout (hidden sm:flex) */}
                        <div className="hidden sm:flex sm:flex-row sm:items-start justify-between gap-4 pb-4 border-b border-slate-200">
                          <div className="flex items-start space-x-3 min-w-0">
                            <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-600 shrink-0">
                              <FileText className="w-7 h-7" />
                            </div>
                            <div className="min-w-0 space-y-1.5">
                              <h3 className="text-sm font-bold text-slate-900">
                                {getCleanDocDisplayTitle(selectedDoc)}
                              </h3>
                              <p className="text-xs text-slate-600">
                                Source: <strong className="text-slate-800">
                                  {selectedDoc.doc_type === 'INTAKE_PACKET' || selectedDoc.doc_type === 'DDD_PACKET'
                                    ? 'Digital Client Intake / Dual eSign Workflow'
                                    : selectedDoc.source === 'INBOUND_FAX'
                                    ? 'Electronic Inbound Fax (Faxage)'
                                    : 'Manual Upload / Digital Intake'}
                                </strong>
                                {' '}• DOS / Date: <span className="font-mono text-slate-700">{selectedDoc.dos || selectedDoc.document_date || selectedDoc.created_at?.slice(0, 10)}</span>
                              </p>
                            </div>
                          </div>
                          <span className="px-3 py-1 rounded-md text-xs font-mono font-bold uppercase bg-slate-50 text-slate-700 border border-slate-200 self-start shrink-0">
                            {formatDocTypeLabel(selectedDoc.doc_type)}
                          </span>
                        </div>
                      </>
                    ) : (
                      /* Other Document Panels (External, Phys Signed POC, etc.) - Unchanged on All Screen Sizes */
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 pb-4 border-b border-slate-200">
                        <div className="flex items-start space-x-3 min-w-0">
                          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-600 shrink-0">
                            <FileText className="w-7 h-7" />
                          </div>
                          <div className="min-w-0 space-y-1.5">
                            <h3 className="text-sm font-bold text-slate-900">
                              {getCleanDocDisplayTitle(selectedDoc)}
                            </h3>
                            <p className="text-xs text-slate-600">
                              Source: <strong className="text-slate-800">
                                {teletherapySignedOriginal === 'included'
                                  ? 'SignWell signed original'
                                  : teletherapySignedOriginal === 'unavailable'
                                  ? 'Signed original unavailable'
                                  : selectedDoc.source === 'INBOUND_FAX'
                                  ? 'Electronic Inbound Fax (Faxage)'
                                  : selectedDoc.doc_type === 'SOAP' && !preserveStoredDailyNoteFile(selectedDoc)
                                  ? 'Generated from the stored daily treatment note'
                                  : 'Manual Upload / Digital Intake'}
                              </strong>
                              {' '}• DOS / Date: <span className="font-mono text-slate-700">{selectedDoc.dos || selectedDoc.document_date || selectedDoc.created_at?.slice(0, 10)}</span>
                            </p>
                          </div>
                        </div>
                        <span className="px-3 py-1 rounded-md text-xs font-mono font-bold uppercase bg-slate-50 text-slate-700 border border-slate-200 self-start shrink-0">
                          {formatDocTypeLabel(selectedDoc.doc_type)}
                        </span>
                      </div>
                    )}

                    {/* Metadata Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
                      <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                        <span className="text-slate-500 block text-[10px] uppercase font-semibold tracking-wide">{teletherapySignedOriginal === 'included' ? 'SignWell signed original' : teletherapySignedOriginal === 'unavailable' ? 'Signed original unavailable' : selectedDoc.doc_type === 'SOAP' && !preserveStoredDailyNoteFile(selectedDoc) ? 'Generated preview' : 'Attached File'}</span>
                        <span className="text-slate-900 font-medium truncate block mt-1.5" title={selectedDoc.file_name || 'Document'}>
                          {selectedDoc.file_name || 'Attached PDF Document'}
                        </span>
                      </div>
                      <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                        <span className="text-slate-500 block text-[10px] uppercase font-semibold tracking-wide">File Format & Size</span>
                        <span className="text-slate-800 font-mono block mt-1.5">
                          {selectedDoc.file_size ? `${Math.round(selectedDoc.file_size / 1024)} KB` : 'PDF'} • {selectedDoc.mime_type || 'application/pdf'}
                        </span>
                      </div>
                      <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                        <span className="text-slate-500 block text-[10px] uppercase font-semibold tracking-wide">Attributed Provider</span>
                        <span className="text-slate-900 font-medium block mt-1.5">
                          {selectedDoc.provider_name || 'Clinical Staff'}
                        </span>
                      </div>
                      <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                        <span className="text-slate-500 block text-[10px] uppercase font-semibold tracking-wide">Verification Status</span>
                        <span className="text-emerald-800 font-semibold flex items-center space-x-1 mt-1.5">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          <span>Medical Record on File</span>
                        </span>
                      </div>
                    </div>

                    {/* Ordering Physician Certification on File if Applicable */}
                    {(selectedDoc.doc_type === 'PHYSICIAN_SIGNED_POC' || selectedDoc.physician_signature || selectedDoc.physician_name) && (
                      <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 space-y-1.5 text-xs">
                        <div className="flex items-center space-x-2 text-slate-800 font-bold">
                          <ShieldCheck className="w-4 h-4 text-emerald-700" />
                          <span>Ordering Physician Certification</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px] text-slate-700 pt-1">
                          <div>
                            <span className="text-slate-500">Physician: </span>
                            <strong className="text-slate-900">{selectedDoc.physician_name || 'Ordering Physician on File'}</strong>
                          </div>
                          <div>
                            <span className="text-slate-500">Signature: </span>
                            <span className="font-semibold text-emerald-800">{selectedDoc.physician_signature || 'Certified on File'}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">Period: </span>
                            <span className="font-mono text-slate-800">{selectedDoc.poc_end_date ? `Through ${selectedDoc.poc_end_date}` : 'Plan Period'}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Clinical Summary / Ingestion Notes */}
                    {selectedDoc.notes && (
                      <div className="p-3.5 bg-slate-50 rounded-lg border border-slate-200 space-y-1.5">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Clinical Notes / Record Summary</span>
                        <p className="text-xs text-slate-800 leading-relaxed">{selectedDoc.notes}</p>
                      </div>
                    )}

                    {/* Action Links */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-start gap-2.5 sm:gap-3 pt-2 w-full sm:w-auto">
                      <button
                        type="button"
                        onClick={() => {
                          if (previewBlobUrl) {
                            window.open(previewBlobUrl, '_blank');
                          } else {
                            handleDownloadPdf(selectedDoc, true);
                          }
                        }}
                        disabled={downloadingPdf}
                        className="flex items-center justify-center px-4 py-2.5 sm:py-2 rounded-lg bg-sky-600 hover:bg-sky-500 text-white font-bold text-xs space-x-2 shadow transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                        title="Open PDF in new tab"
                      >
                        {downloadingPdf ? (
                          <Loader2 className="w-4 h-4 text-white animate-spin" />
                        ) : (
                          <ExternalLink className="w-4 h-4 text-white" />
                        )}
                        <span>{downloadingPdf ? 'Opening PDF...' : 'Open in New Tab'}</span>
                      </button>
                      <a
                        href={`/api/clinical/docs/${selectedDoc.id}/download`}
                        download={getDocDownloadFileName(selectedDoc)}
                        className="flex items-center justify-center px-4 py-2.5 sm:py-2 rounded-lg bg-white hover:bg-slate-50 text-slate-700 font-bold text-xs border border-slate-200 space-x-2 shadow-2xs transition-colors w-full sm:w-auto"
                      >
                        <Download className="w-4 h-4" />
                        <span>Download Attached File</span>
                      </a>
                    </div>

                    {/* Embedded Standard PDF Viewer */}
                    <div className="mt-2 rounded-xl overflow-hidden border border-slate-200 bg-white">
                      <div className="px-3.5 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between text-xs text-slate-500">
                        <span className="font-mono flex items-center space-x-2">
                          <FileText className="w-3.5 h-3.5 text-slate-500" />
                          <span className="text-slate-800 font-medium">Document Viewer — {getDocDownloadFileName(selectedDoc)}</span>
                        </span>
                        <span className="text-[11px] text-slate-500 font-mono">Standard Allegiant Viewer</span>
                      </div>
                      {previewLoading ? (
                        <div className="w-full h-[620px] bg-slate-50 flex flex-col items-center justify-center space-y-3">
                          <Loader2 className="w-8 h-8 text-slate-500 animate-spin" />
                          <span className="text-xs text-slate-500 font-medium">Loading document PDF preview...</span>
                        </div>
                      ) : previewError ? (
                        <div className="w-full h-[320px] bg-slate-50 flex flex-col items-center justify-center space-y-3 p-6 text-center">
                          <AlertTriangle className="w-8 h-8 text-amber-600" />
                          <p className="text-sm text-slate-800 font-medium">{previewError}</p>
                          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2.5 sm:gap-3 pt-2 w-full max-w-xs sm:max-w-none">
                            <button
                              type="button"
                              onClick={() => {
                                if (previewBlobUrl) {
                                  window.open(previewBlobUrl, '_blank');
                                } else {
                                  handleDownloadPdf(selectedDoc, true);
                                }
                              }}
                              className="flex items-center justify-center px-4 py-2.5 sm:py-2 rounded-lg bg-sky-600 hover:bg-sky-500 text-white font-bold text-xs space-x-2 shadow transition w-full sm:w-auto"
                            >
                              <ExternalLink className="w-4 h-4" />
                              <span>Open in New Tab</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDownloadPdf(selectedDoc, false)}
                              className="flex items-center justify-center px-4 py-2.5 sm:py-2 rounded-lg bg-white hover:bg-slate-50 text-slate-700 font-bold text-xs space-x-2 border border-slate-200 shadow-2xs transition w-full sm:w-auto"
                            >
                              <Download className="w-4 h-4" />
                              <span>Download Attached File</span>
                            </button>
                          </div>
                        </div>
                      ) : previewBlobUrl ? (
                        (() => {
                          const isEmbeddedPdfSupported = typeof navigator !== 'undefined' && (
                            'pdfViewerEnabled' in navigator ? navigator.pdfViewerEnabled : true
                          );

                          if (!isEmbeddedPdfSupported || previewFailed) {
                            return (
                              <div className="w-full h-[620px] bg-slate-50 flex flex-col items-center justify-center p-6 text-center space-y-4 rounded-b-xl">
                                <div className="p-3.5 rounded-full bg-white border border-slate-200 text-slate-500">
                                  <FileText className="w-8 h-8 text-slate-500" />
                                </div>
                                <div className="space-y-1 max-w-md">
                                  <p className="text-sm font-semibold text-slate-800">PDF preview unavailable in this browser</p>
                                  <p className="text-xs text-slate-500 leading-relaxed">
                                    Your browser environment does not support embedded PDF rendering directly inside this frame.
                                  </p>
                                </div>
                                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2.5 sm:gap-3 pt-2 w-full max-w-xs sm:max-w-none">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (previewBlobUrl) {
                                        window.open(previewBlobUrl, '_blank');
                                      } else {
                                        handleDownloadPdf(selectedDoc, true);
                                      }
                                    }}
                                    className="flex items-center justify-center px-4 py-2.5 sm:py-2 rounded-lg bg-sky-600 hover:bg-sky-500 text-white font-medium text-xs space-x-2 transition shadow w-full sm:w-auto cursor-pointer"
                                  >
                                    <ExternalLink className="w-3.5 h-3.5" />
                                    <span>Open in New Tab</span>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDownloadPdf(selectedDoc, false)}
                                    className="flex items-center justify-center px-4 py-2.5 sm:py-2 rounded-lg bg-white hover:bg-slate-50 text-slate-700 font-medium text-xs border border-slate-200 space-x-2 transition shadow-2xs w-full sm:w-auto cursor-pointer"
                                  >
                                    <Download className="w-4 h-4" />
                                    <span>Download Attached File</span>
                                  </button>
                                </div>
                              </div>
                            );
                          }

                          return (
                            <iframe
                              src={`${previewBlobUrl}#toolbar=1&view=FitH`}
                              className="w-full h-[720px] border-0 bg-white block rounded-b-xl"
                              title={selectedDoc.title || 'Document PDF Preview'}
                              onError={() => setPreviewFailed(true)}
                            />
                          );
                        })()
                      ) : (
                        <div className="w-full h-[320px] bg-slate-50 flex flex-col items-center justify-center space-y-3 p-6 text-center">
                          <AlertTriangle className="w-8 h-8 text-amber-600" />
                          <p className="text-sm text-slate-800 font-medium">Unable to preview this document directly in the browser.</p>
                          <button
                            type="button"
                            onClick={() => handleDownloadPdf(selectedDoc)}
                            className="px-4 py-2 rounded-lg bg-sky-600 hover:bg-sky-500 text-white font-bold text-xs flex items-center space-x-2 shadow"
                          >
                            <Download className="w-4 h-4" />
                            <span>Download PDF Instead</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {/* Document Surface Container within Allegiant Shell */}
                  <div className={`relative mx-2 sm:mx-6 my-4 p-4 sm:p-8 rounded-2xl border space-y-6 print:m-0 print:p-0 print:bg-white print:border-none print:shadow-none print:text-slate-950 print:space-y-4 ${
                    isClinicalLightMobile
                      ? 'bg-white border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.06)] text-slate-800'
                      : 'bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] border-slate-600/70 shadow-[0_12px_36px_rgba(0,0,0,0.55),0_0_0_1px_rgba(148,163,184,0.08),inset_0_1px_0_rgba(255,255,255,0.08)] text-slate-100'
                  }`}>
                    {/* Cool Specular Top Hairline Reflection */}
                    {!isClinicalLightMobile && (
                      <div className="absolute top-0 left-4 right-4 h-[1px] bg-gradient-to-r from-transparent via-slate-300/30 to-transparent pointer-events-none print:hidden" />
                    )}

                    {/* Specialized Header Badges for EVAL, POC, QPR */}
                    {isAuthoredPlanDoc(selectedDoc.doc_type) && (
                      <div className={`p-3.5 rounded-xl border text-xs flex items-center space-x-3 print:bg-slate-50 print:border-slate-300 print:text-slate-900 ${
                        isClinicalLightMobile
                          ? 'bg-indigo-50/80 border-indigo-200 text-indigo-900 shadow-xs'
                          : 'bg-gradient-to-b from-[#1e2740] via-[#161d30] to-[#111726] border-indigo-500/40 text-indigo-200 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(99,102,241,0.1)]'
                      }`}>
                        <Calendar className={`w-4 h-4 shrink-0 print:text-indigo-700 ${isClinicalLightMobile ? 'text-indigo-600' : 'text-indigo-400'}`} />
                        <div>
                          <span className={`font-bold uppercase tracking-wider print:text-indigo-900 ${isClinicalLightMobile ? 'text-indigo-900' : 'text-indigo-300'}`}>Plan of Care Certification Period: </span>
                          <span className={`font-mono font-bold ml-1 print:text-slate-950 ${isClinicalLightMobile ? 'text-indigo-950' : 'text-indigo-100'}`}>
                            {selectedDoc.poc_start_date || 'Active'} to {selectedDoc.poc_end_date || 'Plan Period'}
                          </span>
                        </div>
                      </div>
                    )}

                    {selectedDoc.doc_type === 'QPR' && (
                      <div className={`p-3.5 rounded-xl border text-xs flex items-center space-x-3 print:bg-slate-50 print:border-slate-300 print:text-slate-900 ${
                        isClinicalLightMobile
                          ? 'bg-amber-50/80 border-amber-200 text-amber-900 shadow-xs'
                          : 'bg-gradient-to-b from-[#24202c] via-[#1a1720] to-[#131118] border-amber-500/40 text-amber-200 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(245,158,11,0.1)]'
                      }`}>
                        <Award className={`w-4 h-4 shrink-0 print:text-amber-700 ${isClinicalLightMobile ? 'text-amber-600' : 'text-amber-400'}`} />
                        <div>
                          <span className={`font-bold uppercase tracking-wider print:text-amber-900 ${isClinicalLightMobile ? 'text-amber-900' : 'text-amber-300'}`}>Quarterly Progress Reporting Period: </span>
                          <span className={`font-mono font-bold ml-1 print:text-slate-950 ${isClinicalLightMobile ? 'text-amber-950' : 'text-amber-100'}`}>{selectedDoc.qpr_quarter || 'Q1 2026'}</span>
                        </div>
                      </div>
                    )}

                    {/* Evaluation Scores (if EVAL) */}
                    {isAuthoredEvalDoc(selectedDoc.doc_type) && selectedDoc.eval_scores_json && (
                      <div className={`p-4 rounded-xl shadow-xs space-y-2 print:bg-white print:border-slate-300 print:p-2.5 break-inside-avoid ${
                        isClinicalLightMobile
                          ? 'bg-slate-50 border border-slate-200/90'
                          : 'bg-gradient-to-b from-[#18263e] to-[#132034] border border-slate-600/70 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.04)]'
                      }`}>
                        <div className={`text-[11px] font-bold uppercase tracking-wider flex items-center space-x-2 print:text-slate-900 ${
                          isClinicalLightMobile ? 'text-slate-800' : 'text-sky-400'
                        }`}>
                          <Layers className={`w-3.5 h-3.5 print:text-slate-700 ${isAssistantMobile ? 'text-cyan-600' : isSupervisorMobile ? 'text-purple-600' : 'text-sky-400'}`} />
                          <span>Standardized Clinical Assessment Scores</span>
                        </div>
                        <pre className={`p-3 rounded-lg font-mono text-[11px] overflow-x-auto print:bg-slate-50 print:border-slate-300 print:text-slate-900 ${
                          isClinicalLightMobile
                            ? 'bg-white border border-slate-200 text-slate-800'
                            : 'bg-[#0a101b] border border-slate-700/80 text-slate-200'
                        }`}>
                          {selectedDoc.eval_scores_json}
                        </pre>
                      </div>
                    )}

                    {/* Document Body Sections: Structured Display if sectionData exists and not editing */}
                    {!isEditing && sectionData && (isAuthoredEvalDoc(selectedDoc.doc_type) || isAuthoredPlanDoc(selectedDoc.doc_type) || selectedDoc.doc_type === 'QPR') ? (
                      <ClinicalDocStructuredDisplay doc={selectedDoc} sectionData={sectionData} lightTheme={isClinicalLightMobile} />
                    ) : selectedDoc.doc_type === 'SOAP' ? (
                      <DailyTreatmentNoteBody
                        doc={selectedDoc}
                        light={isClinicalLightMobile}
                        editing={isEditing && !isSupervisorReview ? {
                          narrative: dailyNarrative,
                          goals: dailyGoals,
                          observations: dailyObservations,
                          onNarrative: setDailyNarrative,
                          onGoals: setDailyGoals,
                          onObservations: setDailyObservations
                        } : undefined}
                      />
                    ) : (
                      <div className="space-y-4 text-xs">
                        {/* Subjective */}
                        <div className="space-y-1.5 break-inside-avoid">
                          <div className={`text-[11px] font-bold uppercase tracking-wider print:text-slate-950 print:font-bold ${
                            isClinicalLightMobile ? 'text-slate-800' : 'text-slate-300'
                          }`}>
                            {isAuthoredEvalDoc(selectedDoc.doc_type) ? 'Case History & Background' : selectedDoc.doc_type === 'SOAP' ? 'Session narrative' : 'Subjective (S)'}
                          </div>
                          {isEditing && !isSupervisorReview ? (
                            <textarea
                              value={editSubjective}
                              onChange={(e) => setEditSubjective(e.target.value)}
                              rows={3}
                              className={`w-full rounded-xl p-3 shadow-xs leading-relaxed text-xs font-normal focus:outline-none ${
                                isAssistantMobile
                                  ? 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-cyan-500'
                                  : isSupervisorMobile
                                  ? 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-purple-400'
                                  : 'bg-[#0a101b] border border-slate-700 text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30'
                              }`}
                            />
                          ) : (
                            <div className={`p-3.5 rounded-xl border transition-colors leading-relaxed shadow-xs select-text print:bg-white print:border-slate-300 print:text-slate-900 print:p-2.5 print:shadow-none ${
                              isClinicalLightMobile
                                ? 'bg-slate-50 border-slate-200/90 text-slate-800'
                                : 'bg-[#0a101b] border border-slate-700/80 hover:border-slate-600/80 text-slate-100'
                            }`}>
                              {selectedDoc.subjective || 'No subjective statement recorded.'}
                            </div>
                          )}
                        </div>

                        {/* Objective */}
                        <div className="space-y-1.5 break-inside-avoid">
                          <div className={`text-[11px] font-bold uppercase tracking-wider print:text-slate-950 print:font-bold ${
                            isClinicalLightMobile ? 'text-slate-800' : 'text-slate-300'
                          }`}>
                            {isAuthoredPlanDoc(selectedDoc.doc_type) ? 'Treatment Goals & Measurable Objectives' : selectedDoc.doc_type === 'QPR' ? 'Goal Performance Data' : selectedDoc.doc_type === 'SOAP' ? 'Additional clinical observations' : 'Objective (O)'}
                          </div>
                          {isEditing && !isSupervisorReview ? (
                            <textarea
                              value={editObjective}
                              onChange={(e) => setEditObjective(e.target.value)}
                              rows={3}
                              className={`w-full rounded-xl p-3 shadow-xs leading-relaxed text-xs font-normal focus:outline-none ${
                                isAssistantMobile
                                  ? 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-cyan-500'
                                  : isSupervisorMobile
                                  ? 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-purple-400'
                                  : 'bg-[#0a101b] border border-slate-700 text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30'
                              }`}
                            />
                          ) : (
                            <div className={`p-3.5 rounded-xl border transition-colors leading-relaxed shadow-xs select-text print:bg-white print:border-slate-300 print:text-slate-900 print:p-2.5 print:shadow-none ${
                              isClinicalLightMobile
                                ? 'bg-slate-50 border-slate-200/90 text-slate-800'
                                : 'bg-[#0a101b] border border-slate-700/80 hover:border-slate-600/80 text-slate-100'
                            }`}>
                              {selectedDoc.objective || 'No objective clinical observations recorded.'}
                            </div>
                          )}
                        </div>

                        {/* Assessment */}
                        <div className="space-y-1.5 break-inside-avoid">
                          <div className={`text-[11px] font-bold uppercase tracking-wider print:text-slate-950 print:font-bold ${
                            isClinicalLightMobile ? 'text-slate-800' : 'text-slate-300'
                          }`}>
                            {selectedDoc.doc_type === 'SOAP' ? 'Stored assessment text' : 'Assessment (A) — Clinical Impressions & Response'}
                          </div>
                          {isEditing && !isSupervisorReview ? (
                            <textarea
                              value={editAssessment}
                              onChange={(e) => setEditAssessment(e.target.value)}
                              rows={3}
                              className={`w-full rounded-xl p-3 shadow-xs leading-relaxed text-xs font-normal focus:outline-none ${
                                isAssistantMobile
                                  ? 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-cyan-500'
                                  : isSupervisorMobile
                                  ? 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-purple-400'
                                  : 'bg-[#0a101b] border border-slate-700 text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30'
                              }`}
                            />
                          ) : (
                            <div className={`p-3.5 rounded-xl border transition-colors leading-relaxed shadow-xs select-text print:bg-white print:border-slate-300 print:text-slate-900 print:p-2.5 print:shadow-none ${
                              isClinicalLightMobile
                                ? 'bg-slate-50 border-slate-200/90 text-slate-800'
                                : 'bg-[#0a101b] border border-slate-700/80 hover:border-slate-600/80 text-slate-100'
                            }`}>
                              {selectedDoc.assessment || 'No clinical assessment statement recorded.'}
                            </div>
                          )}
                        </div>

                        {/* Plan / Recommendations */}
                        <div className="space-y-1.5 break-inside-avoid">
                          <div className={`text-[11px] font-bold uppercase tracking-wider print:text-slate-950 print:font-bold ${
                            isClinicalLightMobile ? 'text-slate-800' : 'text-slate-300'
                          }`}>
                            {selectedDoc.doc_type === 'SOAP' ? 'Stored plan text' : 'Plan (P) — Frequency, Duration & Interventions'}
                          </div>
                          {isEditing && !isSupervisorReview ? (
                            <textarea
                              value={editPlan}
                              onChange={(e) => setEditPlan(e.target.value)}
                              rows={3}
                              className={`w-full rounded-xl p-3 shadow-xs leading-relaxed text-xs font-normal focus:outline-none ${
                                isAssistantMobile
                                  ? 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-cyan-500'
                                  : isSupervisorMobile
                                  ? 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-purple-400'
                                  : 'bg-[#0a101b] border border-slate-700 text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30'
                              }`}
                            />
                          ) : (
                            <div className={`p-3.5 rounded-xl border transition-colors leading-relaxed shadow-xs select-text print:bg-white print:border-slate-300 print:text-slate-900 print:p-2.5 print:shadow-none ${
                              isClinicalLightMobile
                                ? 'bg-slate-50 border-slate-200/90 text-slate-800'
                                : 'bg-[#0a101b] border border-slate-700/80 hover:border-slate-600/80 text-slate-100'
                            }`}>
                              {selectedDoc.plan || 'No plan recorded.'}
                            </div>
                          )}
                        </div>

                        {/* Recommendations (if EVAL or QPR) */}
                        {(selectedDoc.recommendations || isEditing) && (
                          <div className="space-y-1.5 break-inside-avoid">
                            <div className={`text-[11px] font-bold uppercase tracking-wider print:text-slate-950 print:font-bold ${
                              isClinicalLightMobile ? 'text-slate-800' : 'text-slate-300'
                            }`}>
                              Clinical Recommendations
                            </div>
                            {isEditing && !isSupervisorReview ? (
                              <textarea
                                value={editRecommendations}
                                onChange={(e) => setEditRecommendations(e.target.value)}
                                rows={2}
                                className={`w-full rounded-xl p-3 shadow-xs leading-relaxed text-xs font-normal focus:outline-none ${
                                  isAssistantMobile
                                    ? 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-cyan-500'
                                    : isSupervisorMobile
                                    ? 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-purple-400'
                                    : 'bg-[#0a101b] border border-slate-700 text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30'
                                }`}
                              />
                            ) : (
                              <div className={`p-3.5 rounded-xl border transition-colors leading-relaxed shadow-xs select-text print:bg-white print:border-slate-300 print:text-slate-900 print:p-2.5 print:shadow-none ${
                                isClinicalLightMobile
                                  ? 'bg-slate-50 border-slate-200/90 text-slate-800'
                                  : 'bg-[#0a101b] border border-slate-700/80 hover:border-slate-600/80 text-slate-100'
                              }`}>
                                {selectedDoc.recommendations}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Goals Addressed & Historical Snapshot Section */}
                        {selectedDoc.goals_addressed_json && selectedDoc.goals_addressed_json !== '[]' && selectedDoc.goals_addressed_json !== '{}' && (
                          <div className={`space-y-2 p-4 rounded-xl border print:bg-white print:border-slate-300 print:p-3 print:shadow-none break-inside-avoid ${
                            isClinicalLightMobile
                              ? 'bg-slate-50 border-slate-200/90 shadow-xs'
                              : 'bg-gradient-to-b from-[#18263e] to-[#132034] border-slate-600/70 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.04)]'
                          }`}>
                            <div className={`text-[11px] font-bold uppercase tracking-wider flex items-center space-x-1.5 print:text-slate-950 ${
                              isClinicalLightMobile ? 'text-slate-800' : 'text-slate-200'
                            }`}>
                              <Target className={`w-3.5 h-3.5 print:text-emerald-700 ${isClinicalLightMobile ? 'text-emerald-600' : 'text-emerald-400'}`} />
                              <span>
                                {isAuthoredEvalDoc(selectedDoc.doc_type) ? 'Established Canonical Treatment Goals' :
                                 isAuthoredPlanDoc(selectedDoc.doc_type) ? 'Plan of Care Treatment Goals & Targets' :
                                 selectedDoc.doc_type === 'QPR' ? 'Quarterly Goal-by-Goal Progress Review' :
                                 'Visit Targeted Goals & Performance Data'}
                              </span>
                            </div>
                            {(() => {
                              try {
                                const rawGoals = selectedDoc.goals_addressed_json;
                                const parsedGoals = Array.isArray(rawGoals)
                                  ? rawGoals
                                  : typeof rawGoals === 'string'
                                    ? JSON.parse(rawGoals)
                                    : null;
                                if (Array.isArray(parsedGoals) && parsedGoals.length > 0) {
                                  return (
                                    <div className="space-y-2 mt-2">
                                      {parsedGoals.map((g: any, idx: number) => (
                                        <div key={idx} className={`p-3 rounded-lg border space-y-1.5 print:bg-slate-50 print:border-slate-300 print:p-2.5 print:text-slate-900 ${
                                          isClinicalLightMobile
                                            ? 'bg-white border-slate-200 text-slate-800 shadow-xs'
                                            : 'bg-[#0a101b] border-slate-700/80'
                                        }`}>
                                          <div className="flex justify-between items-start">
                                            <span className={`font-semibold text-xs print:text-slate-950 ${
                                              isClinicalLightMobile ? 'text-slate-900' : 'text-slate-100'
                                            }`}>
                                              Goal {idx + 1}: {g.description}
                                            </span>
                                            {g.status && (
                                              <span className={`text-[10px] uppercase font-mono px-2 py-0.5 rounded font-bold shrink-0 ml-2 print:bg-slate-200 print:text-slate-900 print:border-slate-400 ${
                                                isClinicalLightMobile
                                                  ? 'bg-sky-50 text-sky-800 border border-sky-200'
                                                  : 'bg-sky-950/60 text-sky-300 border border-sky-500/40'
                                              }`}>
                                                {g.status}
                                              </span>
                                            )}
                                          </div>
                                          <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] font-mono print:text-slate-800 ${
                                            isClinicalLightMobile ? 'text-slate-600' : 'text-slate-300'
                                          }`}>
                                            {g.baseline_performance && (
                                              <span>Baseline: {g.baseline_performance}</span>
                                            )}
                                            {g.target_percentage !== undefined && (
                                              <span className={`font-bold print:text-emerald-800 ${isClinicalLightMobile ? 'text-emerald-700' : 'text-emerald-400'}`}>Target: {g.target_percentage}%</span>
                                            )}
                                            {(g.performance_accuracy !== undefined || g.accuracy !== undefined) && (
                                              <span className={`font-bold print:text-sky-900 ${isClinicalLightMobile ? 'text-sky-700' : 'text-sky-400'}`}>Session Accuracy: {g.accuracy ?? g.performance_accuracy}%</span>
                                            )}
                                            {g.current_percentage !== undefined && (
                                              <span className={`font-bold print:text-sky-900 ${isClinicalLightMobile ? 'text-sky-700' : 'text-sky-400'}`}>Current Progress: {g.current_percentage}%</span>
                                            )}
                                          </div>
                                          {(g.cueing || g.cueing_notes) && (
                                            <div className={`text-[11px] print:text-slate-700 ${isClinicalLightMobile ? 'text-slate-600' : 'text-slate-300'}`}>
                                              <span className={`font-medium print:text-slate-600 ${isClinicalLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>Cueing:</span> {g.cueing || g.cueing_notes}
                                            </div>
                                          )}
                                          {g.progress_notes && (
                                            <div className={`text-[11px] print:text-slate-700 ${isClinicalLightMobile ? 'text-slate-600' : 'text-slate-300'}`}>
                                              <span className={`font-medium print:text-slate-600 ${isClinicalLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>Progress Notes:</span> {g.progress_notes}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  );
                                }
                              } catch {
                                return <div className="text-slate-400 text-xs italic print:text-slate-700">Goal performance data could not be read.</div>;
                              }
                              return null;
                            })()}
                          </div>
                        )}

                        {/* Medical Necessity */}
                        <div className="space-y-1.5 break-inside-avoid">
                          <div className={`text-[11px] font-bold uppercase tracking-wider print:text-slate-950 print:font-bold ${
                            isClinicalLightMobile ? 'text-amber-800' : 'text-amber-400'
                          }`}>
                            Medical Necessity
                          </div>
                          {isEditing && !isSupervisorReview ? (
                            <textarea
                              value={editMedicalNecessity}
                              onChange={(e) => setEditMedicalNecessity(e.target.value)}
                              rows={2}
                              className={`w-full rounded-xl p-3 italic shadow-xs leading-relaxed text-xs focus:outline-none ${
                                isClinicalLightMobile
                                  ? 'bg-amber-50/50 border border-amber-200 text-amber-950 placeholder:text-amber-600/50 focus:border-amber-400'
                                  : 'bg-[#0a101b] border border-amber-500/40 text-amber-100 placeholder:text-amber-500/50 focus:border-amber-500 focus:ring-1 focus:ring-amber-500/30'
                              }`}
                            />
                          ) : (
                            <div className={`p-3.5 rounded-xl border leading-relaxed italic select-text print:bg-white print:border-slate-300 print:text-slate-900 print:p-2.5 print:shadow-none ${
                              isClinicalLightMobile
                                ? 'bg-amber-50/70 border-amber-200 text-amber-950 shadow-xs'
                                : 'bg-gradient-to-b from-[#18140e] to-[#0f0c08] border-amber-500/35 text-amber-100 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(245,158,11,0.1)]'
                            }`}>
                              {selectedDoc.medical_necessity || 'Documented medical necessity for skilled speech therapy.'}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Signatures Workflow Section */}
                  <div className={`p-6 border-t space-y-4 no-print ${
                    isClinicalLightMobile
                      ? 'bg-slate-50/90 border-slate-200/90'
                      : 'bg-gradient-to-b from-[#18263e] to-[#132034] border-slate-600/70'
                  }`}>
                    <div className="flex items-center justify-between">
                      <h3 className={`text-xs font-bold uppercase tracking-widest flex items-center space-x-2 ${
                        isClinicalLightMobile ? 'text-slate-800' : 'text-slate-200'
                      }`}>
                        <PenTool className={`w-3.5 h-3.5 ${isAssistantMobile ? 'text-teal-600' : isSupervisorMobile ? 'text-purple-600' : 'text-sky-400'}`} />
                        <span>Verified Signature State & Billing Readiness Gates</span>
                      </h3>
                      <span className={`text-[10px] ${isClinicalLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
                        All signatures update canonical billing readiness immediately
                      </span>
                    </div>

                    {/* Layout based on Document Type */}
                    {isAuthoredPlanDoc(selectedDoc.doc_type) ? (
                      /* Plan of Care: 1. Clinician, 2. Supervisor (if SLPA), 3. Physician Certification */
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* 1. Clinician Signature */}
                        <div className={`p-4 rounded-xl space-y-2.5 ${
                          isClinicalLightMobile
                            ? 'bg-white border border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.06)]'
                            : 'bg-[#0e1626]/85 border border-slate-700/70 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.04)]'
                        }`}>
                          <div className={`text-[11px] font-bold ${isClinicalLightMobile ? 'text-slate-800' : 'text-slate-300'}`}>1. Clinician Signature</div>
                          {selectedDoc.clinician_signature ? (
                            <div className="text-xs text-emerald-600 space-y-1">
                              <div className="flex items-center space-x-1">
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                <span className="font-semibold">{selectedDoc.clinician_signature}</span>
                              </div>
                              <div className={`text-[10px] font-mono ${isClinicalLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
                                Signed: {formatSignatureTimestamp(selectedDoc.clinician_signed_at)}
                              </div>
                              {selectedDoc.status === 'PENDING_SIGNATURES' && (
                                <button
                                  onClick={() => handleSignClinician(selectedDoc.id)}
                                  className="w-full mt-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-1.5 rounded-lg text-xs transition-colors shadow flex items-center justify-center space-x-1"
                                >
                                  <CheckCircle2 className="w-3.5 h-3.5" />
                                  <span>Complete & Finalize Plan of Care</span>
                                </button>
                              )}
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <input
                                type="text"
                                value={clinicianSigName}
                                onChange={(e) => setClinicianSigName(e.target.value)}
                                placeholder="Clinician Full Name"
                                className={`w-full rounded-lg p-2 text-xs font-medium shadow-xs focus:outline-none ${
                                  isClinicalLightMobile
                                    ? isAssistantMobile
                                      ? 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200'
                                      : 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-purple-400 focus:ring-1 focus:ring-purple-200'
                                    : 'bg-[#0a101b] border border-slate-700/80 text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30'
                                }`}
                              />
                              {/admin|administrator/i.test(clinicianSigName) && (
                                <p className={`text-[10px] ${isClinicalLightMobile ? 'text-amber-600' : 'text-amber-400'}`}>
                                  Administrative placeholder detected. Enter authentic clinician name.
                                </p>
                              )}
                              <button
                                onClick={() => handleSignClinician(selectedDoc.id)}
                                className={`w-full font-bold py-1.5 rounded-lg text-xs transition-colors shadow text-white ${
                                  isAssistantMobile
                                    ? 'bg-teal-600 hover:bg-teal-500'
                                    : 'bg-sky-600 hover:bg-sky-500'
                                }`}
                              >
                                Sign & Attest Plan of Care
                              </button>
                            </div>
                          )}
                        </div>

                        {/* 2. Supervisor Co-Sign */}
                        <div className={`p-4 rounded-xl space-y-2.5 ${
                          isClinicalLightMobile
                            ? 'bg-white border border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.06)]'
                            : 'bg-[#0e1626]/85 border border-slate-700/70 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.04)]'
                        }`}>
                          <div className={`text-[11px] font-bold ${isClinicalLightMobile ? 'text-slate-800' : 'text-slate-300'}`}>2. Supervisor Co-Sign</div>
                          {!isSlpaDoc ? (
                            <div className={`p-2.5 rounded-lg space-y-1 ${
                              isClinicalLightMobile ? 'bg-slate-50 border border-slate-200/80' : 'bg-[#0a101b] border border-slate-700/70'
                            }`}>
                              <div className={`flex items-center space-x-1.5 text-xs font-semibold ${isClinicalLightMobile ? 'text-slate-700' : 'text-slate-300'}`}>
                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                                <span>Not Required</span>
                              </div>
                              <p className={`text-[10px] ${isClinicalLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
                                Authored by licensed SLP ({selectedDoc.provider_name}).
                              </p>
                            </div>
                          ) : selectedDoc.supervisor_approval_status === 'APPROVED' ? (
                            <div className={`text-xs space-y-1 ${isClinicalLightMobile ? 'text-purple-700' : 'text-indigo-400'}`}>
                              <div className="flex items-center space-x-1">
                                <ShieldCheck className="w-3.5 h-3.5" />
                                <span className="font-semibold">{selectedDoc.supervisor_signature || 'Co-Signed & Approved'}</span>
                              </div>
                              <div className={`text-[10px] font-mono ${isClinicalLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
                                Supervised: {formatSignatureTimestamp(selectedDoc.supervisor_signed_at)}
                              </div>
                            </div>
                          ) : selectedDoc.supervisor_approval_status === 'RETURNED' ? (
                            <div className={`p-2.5 rounded-xl text-[11px] space-y-1.5 ${
                              isClinicalLightMobile
                                ? 'bg-amber-50/90 border border-amber-200 text-amber-950'
                                : 'bg-amber-950/40 border border-amber-800/50'
                            }`}>
                              <div className={`flex items-center space-x-1 font-bold ${isClinicalLightMobile ? 'text-amber-700' : 'text-amber-400'}`}>
                                <AlertTriangle className="w-3.5 h-3.5" />
                                <span>Returned for Revision</span>
                              </div>
                              <p className={`text-[10px] leading-relaxed ${isClinicalLightMobile ? 'text-amber-900' : 'text-slate-300'}`}>
                                "{selectedDoc.supervisor_return_comments || 'Please revise and resubmit.'}"
                              </p>
                              {(user.role === 'SUPERVISOR' || user.role === 'ADMIN') && onNavigateTab && (
                                <button
                                  onClick={() => onNavigateTab('notes')}
                                  className={`w-full mt-1.5 font-semibold py-1 rounded-lg text-xs transition-colors shadow flex items-center justify-center space-x-1 ${
                                    isClinicalLightMobile
                                      ? 'bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300'
                                      : 'bg-slate-800 hover:bg-slate-700 text-amber-300 border border-amber-500/30'
                                  }`}
                                >
                                  <span>Review in Note Approval &rarr;</span>
                                </button>
                              )}
                            </div>
                          ) : (
                            <div className={`p-2.5 rounded-xl text-[11px] space-y-2 text-center ${
                              isClinicalLightMobile
                                ? 'bg-purple-50/60 border border-purple-200/80'
                                : 'bg-[#0a101b] border border-slate-700/70'
                            }`}>
                              <div className={`font-medium ${isClinicalLightMobile ? 'text-purple-950' : 'text-slate-300'}`}>Awaiting Supervising SLP Review & Co-Signature</div>
                              {(user.role === 'SUPERVISOR' || user.role === 'ADMIN') && onNavigateTab && (
                                <button
                                  onClick={() => onNavigateTab('notes')}
                                  className={`w-full font-semibold py-1.5 rounded-lg text-xs transition-colors flex items-center justify-center space-x-1 ${
                                    isClinicalLightMobile
                                      ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-xs'
                                      : 'bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-300 border border-indigo-500/30'
                                  }`}
                                >
                                  <span>Open in Note Approval &rarr;</span>
                                </button>
                              )}
                            </div>
                          )}
                        </div>

                        {/* 3. Physician Certification (Faxage Route) */}
                        <div className={`p-4 rounded-xl space-y-2.5 ${
                          isClinicalLightMobile
                            ? 'bg-white border border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.06)]'
                            : 'bg-[#0e1626]/85 border border-slate-700/70 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.04)]'
                        }`}>
                          <div className="flex items-center justify-between">
                            <div className={`text-[11px] font-bold ${isClinicalLightMobile ? 'text-slate-800' : 'text-slate-300'}`}>3. Physician Certification (Faxage)</div>
                            {selectedDoc.physician_signature ? (
                              <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase ${
                                isClinicalLightMobile
                                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                  : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                              }`}>
                                Certified
                              </span>
                            ) : (
                              <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase ${
                                isClinicalLightMobile
                                  ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                  : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                              }`}>
                                Fax Pending
                              </span>
                            )}
                          </div>

                          {selectedDoc.physician_signature ? (
                            <div className={`p-2.5 rounded-lg space-y-1 text-xs ${
                              isClinicalLightMobile ? 'bg-slate-50 border border-slate-200/80' : 'bg-[#0a101b] border border-slate-700/70'
                            }`}>
                              <div className="flex items-center space-x-1.5 text-emerald-600 font-semibold">
                                <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                                <span>{selectedDoc.physician_signature}</span>
                              </div>
                              <div className={`text-[10px] font-mono ${isClinicalLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
                                Certified: {selectedDoc.physician_signed_at ? formatSignatureTimestamp(selectedDoc.physician_signed_at) : 'Recorded on File'}
                              </div>
                              <div className={`text-[10px] ${isClinicalLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
                                Route: Electronic Fax (Faxage) returned certification.
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-2 text-xs">
                              <div className={`p-2 rounded-lg space-y-1 ${
                                isClinicalLightMobile ? 'bg-slate-50 border border-slate-200/80' : 'bg-[#0a101b] border border-slate-700/70'
                              }`}>
                                <div className={`text-[10px] flex justify-between ${isClinicalLightMobile ? 'text-slate-600' : 'text-slate-300'}`}>
                                  <span className={isClinicalLightMobile ? 'text-slate-500' : 'text-slate-400'}>Ordering MD:</span>
                                  <span className={`font-semibold ${isClinicalLightMobile ? 'text-slate-800' : 'text-slate-200'}`}>{selectedDoc.physician_name || 'Ordering Physician on File'}</span>
                                </div>
                                <div className={`text-[10px] flex justify-between ${isClinicalLightMobile ? 'text-slate-600' : 'text-slate-300'}`}>
                                  <span className={isClinicalLightMobile ? 'text-slate-500' : 'text-slate-400'}>Physician Fax:</span>
                                  <span className={`font-mono ${isClinicalLightMobile ? 'text-sky-700' : 'text-sky-400'}`}>{selectedDoc.physician_fax || 'Fax on File'}</span>
                                </div>
                              </div>

                              {canFaxClient && (
                              <button
                                onClick={() => setShowFaxModal(true)}
                                className="w-full bg-sky-600 hover:bg-sky-500 text-white font-bold py-1.5 rounded-lg text-xs transition-colors shadow flex items-center justify-center space-x-1.5"
                              >
                                <Printer className="w-3.5 h-3.5" />
                                <span>Dispatch POC via Faxage</span>
                              </button>
                              )}

                              {canFaxClient && (
                              <div className={`pt-1.5 border-t space-y-1 ${isClinicalLightMobile ? 'border-slate-200' : 'border-slate-700/70'}`}>
                                <input
                                  type="text"
                                  value={physicianSigName}
                                  onChange={(e) => setPhysicianSigName(e.target.value)}
                                  placeholder={selectedDoc.physician_name ? `Dr. ${selectedDoc.physician_name}` : "Ordering Physician Name (from returned fax)"}
                                  className={`w-full rounded-lg p-2 text-xs font-medium shadow-xs focus:outline-none ${
                                    isClinicalLightMobile
                                      ? isAssistantMobile
                                        ? 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200'
                                        : 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-purple-400 focus:ring-1 focus:ring-purple-200'
                                      : 'bg-[#0a101b] border border-slate-700/80 text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30'
                                  }`}
                                />
                                <button
                                  onClick={() => handleRecordPhysicianSignature(selectedDoc.id)}
                                  disabled={physicianSigning}
                                  className={`w-full py-1 rounded-lg text-[11px] font-semibold transition-colors disabled:opacity-50 ${
                                    isClinicalLightMobile
                                      ? 'bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-200'
                                      : 'bg-slate-800 hover:bg-slate-700 text-slate-200'
                                  }`}
                                >
                                  {physicianSigning ? 'Recording...' : 'Record Returned Fax Signature'}
                                </button>
                              </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : selectedDoc.doc_type === 'QPR' ? (
                      /* Quarterly Progress Report: 1. Clinician, 2. Supervisor (if SLPA) */
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* 1. Clinician Signature */}
                        <div className={`p-4 rounded-xl space-y-2.5 ${
                          isClinicalLightMobile
                            ? 'bg-white border border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.06)]'
                            : 'bg-[#0e1626]/85 border border-slate-700/70 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.04)]'
                        }`}>
                          <div className={`text-[11px] font-bold ${isClinicalLightMobile ? 'text-slate-800' : 'text-slate-300'}`}>1. Clinician Signature</div>
                          {selectedDoc.clinician_signature ? (
                            <div className="text-xs text-emerald-600 space-y-1">
                              <div className="flex items-center space-x-1">
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                <span className="font-semibold">{selectedDoc.clinician_signature}</span>
                              </div>
                              <div className={`text-[10px] font-mono ${isClinicalLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
                                Signed: {formatSignatureTimestamp(selectedDoc.clinician_signed_at)}
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <input
                                type="text"
                                value={clinicianSigName}
                                onChange={(e) => setClinicianSigName(e.target.value)}
                                placeholder="Clinician Full Name"
                                className={`w-full rounded-lg p-2 text-xs font-medium shadow-xs focus:outline-none ${
                                  isClinicalLightMobile
                                    ? isAssistantMobile
                                      ? 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200'
                                      : 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-purple-400 focus:ring-1 focus:ring-purple-200'
                                    : 'bg-[#0a101b] border border-slate-700/80 text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30'
                                }`}
                              />
                              {/admin|administrator/i.test(clinicianSigName) && (
                                <p className={`text-[10px] ${isClinicalLightMobile ? 'text-amber-600' : 'text-amber-400'}`}>
                                  Administrative placeholder detected. Enter authentic clinician name.
                                </p>
                              )}
                              <button
                                onClick={() => handleSignClinician(selectedDoc.id)}
                                className={`w-full font-bold py-1.5 rounded-lg text-xs transition-colors shadow text-white ${
                                  isAssistantMobile
                                    ? 'bg-teal-600 hover:bg-teal-500'
                                    : 'bg-sky-600 hover:bg-sky-500'
                                }`}
                              >
                                Sign Clinician Note
                              </button>
                            </div>
                          )}
                        </div>

                        {/* 2. Supervisor Co-Sign */}
                        <div className={`p-4 rounded-xl space-y-2.5 ${
                          isClinicalLightMobile
                            ? 'bg-white border border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.06)]'
                            : 'bg-[#0e1626]/85 border border-slate-700/70 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.04)]'
                        }`}>
                          <div className={`text-[11px] font-bold ${isClinicalLightMobile ? 'text-slate-800' : 'text-slate-300'}`}>2. Supervisor Co-Sign</div>
                          {!isSlpaDoc ? (
                            <div className={`p-2.5 rounded-lg space-y-1 ${
                              isClinicalLightMobile ? 'bg-slate-50 border border-slate-200/80' : 'bg-[#0a101b] border border-slate-700/70'
                            }`}>
                              <div className={`flex items-center space-x-1.5 text-xs font-semibold ${isClinicalLightMobile ? 'text-slate-700' : 'text-slate-300'}`}>
                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                                <span>Not Required</span>
                              </div>
                              <p className={`text-[10px] ${isClinicalLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
                                Authored by licensed SLP ({selectedDoc.provider_name}).
                              </p>
                            </div>
                          ) : selectedDoc.supervisor_approval_status === 'APPROVED' ? (
                            <div className={`text-xs space-y-1 ${isClinicalLightMobile ? 'text-purple-700' : 'text-indigo-400'}`}>
                              <div className="flex items-center space-x-1">
                                <ShieldCheck className="w-3.5 h-3.5" />
                                <span className="font-semibold">{selectedDoc.supervisor_signature || 'Co-Signed & Approved'}</span>
                              </div>
                              <div className={`text-[10px] font-mono ${isClinicalLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
                                Supervised: {formatSignatureTimestamp(selectedDoc.supervisor_signed_at)}
                              </div>
                            </div>
                          ) : selectedDoc.supervisor_approval_status === 'RETURNED' ? (
                            <div className={`p-2.5 rounded-xl text-[11px] space-y-1.5 ${
                              isClinicalLightMobile
                                ? 'bg-amber-50/90 border border-amber-200 text-amber-950'
                                : 'bg-amber-950/40 border border-amber-800/50'
                            }`}>
                              <div className={`flex items-center space-x-1 font-bold ${isClinicalLightMobile ? 'text-amber-700' : 'text-amber-400'}`}>
                                <AlertTriangle className="w-3.5 h-3.5" />
                                <span>Returned for Revision</span>
                              </div>
                              <p className={`text-[10px] leading-relaxed ${isClinicalLightMobile ? 'text-amber-900' : 'text-slate-300'}`}>
                                "{selectedDoc.supervisor_return_comments || 'Please revise and resubmit.'}"
                              </p>
                              {(user.role === 'SUPERVISOR' || user.role === 'ADMIN') && onNavigateTab && (
                                <button
                                  onClick={() => onNavigateTab('notes')}
                                  className={`w-full mt-1.5 font-semibold py-1 rounded-lg text-xs transition-colors shadow flex items-center justify-center space-x-1 ${
                                    isClinicalLightMobile
                                      ? 'bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300'
                                      : 'bg-slate-800 hover:bg-slate-700 text-amber-300 border border-amber-500/30'
                                  }`}
                                >
                                  <span>Review in Note Approval &rarr;</span>
                                </button>
                              )}
                            </div>
                          ) : (
                            <div className={`p-2.5 rounded-xl text-[11px] space-y-2 text-center ${
                              isClinicalLightMobile
                                ? 'bg-purple-50/60 border border-purple-200/80'
                                : 'bg-[#0a101b] border border-slate-700/70'
                            }`}>
                              <div className={`font-medium ${isClinicalLightMobile ? 'text-purple-950' : 'text-slate-300'}`}>Awaiting Supervising SLP Review & Co-Signature</div>
                              {(user.role === 'SUPERVISOR' || user.role === 'ADMIN') && onNavigateTab && (
                                <button
                                  onClick={() => onNavigateTab('notes')}
                                  className={`w-full font-semibold py-1.5 rounded-lg text-xs transition-colors flex items-center justify-center space-x-1 ${
                                    isClinicalLightMobile
                                      ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-xs'
                                      : 'bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-300 border border-indigo-500/30'
                                  }`}
                                >
                                  <span>Open in Note Approval &rarr;</span>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      /* SOAP Notes & Formal Evaluations: 1. Clinician, 2. Guardian (In-Person/eSign/Exception), 3. Supervisor */
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* 1. Clinician Signature */}
                        <div className={`p-4 rounded-xl space-y-2.5 ${
                          isClinicalLightMobile
                            ? 'bg-white border border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.06)]'
                            : 'bg-[#0e1626]/85 border border-slate-700/70 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.04)]'
                        }`}>
                          <div className={`text-[11px] font-bold ${isClinicalLightMobile ? 'text-slate-800' : 'text-slate-300'}`}>1. Clinician Signature</div>
                          {selectedDoc.clinician_signature ? (
                            <div className="text-xs text-emerald-600 space-y-1">
                              <div className="flex items-center space-x-1">
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                <span className="font-semibold">{selectedDoc.clinician_signature}</span>
                              </div>
                              <div className={`text-[10px] font-mono ${isClinicalLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
                                Signed: {formatSignatureTimestamp(selectedDoc.clinician_signed_at)}
                              </div>
                            </div>
                          ) : isSupervisorReview ? (
                            <div className={`p-2.5 rounded-lg text-[11px] text-center ${
                              isClinicalLightMobile ? 'bg-slate-50 border border-slate-200 text-slate-500' : 'bg-[#0a101b] border border-slate-700/70 text-slate-400'
                            }`}>
                              Pending Treating SLPA Signature
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <input
                                type="text"
                                value={clinicianSigName}
                                onChange={(e) => setClinicianSigName(e.target.value)}
                                placeholder="Clinician Full Name"
                                className={`w-full rounded-lg p-2 text-xs font-medium shadow-xs focus:outline-none ${
                                  isClinicalLightMobile
                                    ? isAssistantMobile
                                      ? 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200'
                                      : 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-purple-400 focus:ring-1 focus:ring-purple-200'
                                    : 'bg-[#0a101b] border border-slate-700/80 text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30'
                                }`}
                              />
                              {/admin|administrator/i.test(clinicianSigName) && (
                                <p className={`text-[10px] ${isClinicalLightMobile ? 'text-amber-600' : 'text-amber-400'}`}>
                                  Administrative placeholder detected. Enter authentic clinician name.
                                </p>
                              )}
                              <button
                                onClick={() => handleSignClinician(selectedDoc.id)}
                                className={`w-full font-bold py-1.5 rounded-lg text-xs transition-colors shadow text-white ${
                                  isAssistantMobile
                                    ? 'bg-teal-600 hover:bg-teal-500'
                                    : 'bg-sky-600 hover:bg-sky-500'
                                }`}
                              >
                                Sign Clinician Note
                              </button>
                            </div>
                          )}
                        </div>

                        {/* 2. Guardian Signature with Delivery Methods: In-Person / eSign / Exception */}
                        <div className={`p-4 rounded-xl space-y-2.5 ${
                          isClinicalLightMobile
                            ? 'bg-white border border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.06)]'
                            : 'bg-[#0e1626]/85 border border-slate-700/70 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.04)]'
                        }`}>
                          <div className={`text-[11px] font-bold ${isClinicalLightMobile ? 'text-slate-800' : 'text-slate-300'}`}>2. Guardian Signature</div>
                          {soapGuardianEvidenceIncomplete ? (
                            <div className={`p-2.5 rounded-lg text-[11px] space-y-2 ${
                              isClinicalLightMobile
                                ? 'bg-amber-50 border border-amber-200 text-amber-900'
                                : 'bg-amber-500/10 border border-amber-500/30 text-amber-100'
                            }`}>
                              <div>Guardian signature evidence is incomplete.</div>
                              <button
                                type="button"
                                onClick={() => handleSyncEsign(selectedDoc.id)}
                                disabled={actionInProgress}
                                className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold border ${
                                  isClinicalLightMobile
                                    ? 'bg-white text-slate-700 border-slate-200'
                                    : 'bg-slate-800 text-slate-200 border-slate-600'
                                }`}
                              >
                                Sync Status
                              </button>
                            </div>
                          ) : guardianSignatureShownSigned(selectedDoc) ? (
                            <div className="text-xs text-emerald-600 space-y-1.5">
                              <div className="flex items-center space-x-1 font-semibold">
                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                                <span>Guardian Signature — Signed {formatSignatureTimestamp(selectedDoc.guardian_signed_at || selectedDoc.esign_completed_at, '')}</span>
                                {(selectedDoc as any).late_pay_consequence && (
                                  <span className="block text-rose-700">{GUARDIAN_OVERDUE_PAY_MESSAGE}</span>
                                )}
                              </div>
                              <div className={`text-[10px] ${isClinicalLightMobile ? 'text-slate-700' : 'text-slate-300'}`}>
                                Signer: <span className={`font-medium ${isClinicalLightMobile ? 'text-slate-900' : 'text-white'}`}>{selectedDoc.guardian_signature}</span> ({selectedDoc.guardian_relationship || 'Parent / Legal Guardian'})
                              </div>
                              <div className={`text-[10px] font-mono ${isClinicalLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
                                Signed: {formatSignatureTimestamp(selectedDoc.guardian_signed_at)}
                              </div>
                              <div className={`text-[10px] ${isClinicalLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
                                Method: {selectedDoc.esign_completed_at ? 'Electronic Signature (SignWell)' : 'In-Person Point of Care'}
                              </div>

                              {/* Prior Dispatches Audit Trail toggle if available */}
                              {(() => {
                                let dispatches: any[] = [];
                                if (selectedDoc.esign_evidence_json) {
                                  try {
                                    const parsed = JSON.parse(selectedDoc.esign_evidence_json);
                                    if (Array.isArray(parsed.dispatches)) dispatches = parsed.dispatches;
                                  } catch (e) {}
                                }
                                if (dispatches.length === 0 && selectedDoc.esign_envelope_id) {
                                  dispatches.push({
                                    documentId: selectedDoc.id,
                                    envelopeId: selectedDoc.esign_envelope_id,
                                    sentAt: selectedDoc.esign_sent_at || '',
                                    recipientName: selectedDoc.esign_signer_name || selectedDoc.client_guardian_name || selectedDoc.guardian_name || 'Guardian',
                                    recipientEmail: selectedDoc.esign_signer_email || '',
                                    status: 'COMPLETED',
                                    completedAt: selectedDoc.esign_completed_at || selectedDoc.guardian_signed_at
                                  });
                                }
                                if (dispatches.length === 0) return null;
                                return (
                                  <div className="pt-1">
                                    <button
                                      onClick={() => setShowDocAuditTrail(!showDocAuditTrail)}
                                      className={`text-[10px] underline ${isClinicalLightMobile ? 'text-slate-500 hover:text-slate-800' : 'text-slate-400 hover:text-slate-200'}`}
                                    >
                                      {showDocAuditTrail ? 'Hide eSign Audit History' : `View eSign Audit History (${dispatches.length})`}
                                    </button>
                                    {showDocAuditTrail && (
                                      <div className={`mt-1.5 p-2 rounded-lg border space-y-1 text-[10px] ${
                                        isClinicalLightMobile ? 'bg-slate-50 border-slate-200 text-slate-700' : 'bg-[#0a101b] border-slate-700/70 text-slate-300'
                                      }`}>
                                        {dispatches.map((d: any, idx: number) => (
                                          <div key={d.envelopeId || idx} className={`border-b pb-1 last:border-0 last:pb-0 ${isClinicalLightMobile ? 'border-slate-200' : 'border-slate-700/70'}`}>
                                            <div className="flex items-center justify-between">
                                              <span className={`font-mono ${isClinicalLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>Env: {d.envelopeId || 'N/A'}</span>
                                              <span className="font-semibold text-emerald-600">{d.status}</span>
                                            </div>
                                            <div className={isClinicalLightMobile ? 'text-slate-600' : 'text-slate-400'}>To: {d.recipientName} ({d.recipientEmail})</div>
                                            <div className={isClinicalLightMobile ? 'text-slate-500' : 'text-slate-500'}>Sent: {formatConciseDateTime(d.sentAt)} {d.completedAt ? `• Completed: ${formatSignatureTimestamp(d.completedAt, '')}` : ''}</div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          ) : selectedDoc.guardian_signature_status === 'WAIVED_DOCUMENTED' ? (
                            <div className="text-xs text-sky-600 space-y-1">
                              <div className="flex items-center space-x-1">
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                <span className="font-semibold">Document Signature Exception Recorded</span>
                              </div>
                              <div className={`text-[10px] ${isClinicalLightMobile ? 'text-slate-600' : 'text-slate-400'}`}>
                                {selectedDoc.guardian_relationship || 'Medicaid/DDD Exception — Guardian Unavailable at POC'}
                              </div>
                              <div className={`text-[10px] font-mono ${isClinicalLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
                                Recorded: {selectedDoc.guardian_signed_at ? formatSignatureTimestamp(selectedDoc.guardian_signed_at) : 'Recorded on File'}
                              </div>
                            </div>
                          ) : isSupervisorReview ? (
                            <div className={`p-2.5 rounded-lg text-[11px] text-center ${
                              isClinicalLightMobile ? 'bg-slate-50 border border-slate-200 text-slate-500' : 'bg-[#0a101b] border border-slate-700/70 text-slate-400'
                            }`}>
                              {(selectedDoc as any).is_guardian_signature_overdue || selectedDoc.guardian_signature_status === 'OVERDUE'
                                ? ((selectedDoc as any).late_pay_consequence
                                  ? GUARDIAN_OVERDUE_PAY_MESSAGE
                                  : ((selectedDoc as any).guardian_signature_display_label || 'Guardian Signature — OVERDUE'))
                                : (selectedDoc as any).guardian_deadline_label
                                ? `Due by ${(selectedDoc as any).guardian_deadline_label}`
                                : 'Pending Guardian Signature'}
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {String(selectedDoc.esign_status || '').toUpperCase() === 'FAILED' && (
                                <div className={`p-2.5 rounded-lg text-[11px] ${
                                  isClinicalLightMobile
                                    ? 'bg-rose-50 border border-rose-200 text-rose-900'
                                    : 'bg-rose-500/15 border border-rose-500/40 text-rose-200'
                                }`}>
                                  <div className="font-semibold">SignWell request did not send.</div>
                                  <div className="mt-1">
                                    {selectedDoc.esign_failure_reason || 'No envelope was created.'}
                                  </div>
                                </div>
                              )}
                              {/* In-Flight eSign Envelope Tracker if active */}
                              {selectedDoc.esign_envelope_id && (selectedDoc.esign_status === 'SENT' || selectedDoc.esign_status === 'PENDING') && (
                                <div className={`p-2.5 rounded-lg border space-y-1.5 ${
                                  isClinicalLightMobile
                                    ? 'bg-indigo-50/80 border-indigo-200 text-indigo-950 shadow-xs'
                                    : 'bg-gradient-to-b from-indigo-950/35 via-[#0d1226]/90 to-[#090d1c]/95 border-indigo-500/35'
                                }`}>
                                  <div className="flex items-center justify-between text-[11px]">
                                    <span className={`font-semibold flex items-center space-x-1.5 ${isClinicalLightMobile ? 'text-indigo-900' : 'text-sky-300'}`}>
                                      <PenTool className={`w-3.5 h-3.5 ${isClinicalLightMobile ? 'text-indigo-600' : 'text-sky-400'}`} />
                                      <span>Guardian Signature — Sent {formatConciseDateTime(selectedDoc.esign_sent_at)}</span>
                                    </span>
                                    <span className={`font-mono px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                                      isClinicalLightMobile ? 'bg-indigo-100 text-indigo-800' : 'bg-sky-900/60 text-sky-300'
                                    }`}>
                                      Waiting on Signer
                                    </span>
                                  </div>
                                  <div className={`text-[11px] ${isClinicalLightMobile ? 'text-slate-700' : 'text-slate-300'}`}>
                                    Signer: <span className={`font-semibold ${isClinicalLightMobile ? 'text-slate-900' : 'text-white'}`}>{selectedDoc.esign_signer_name || selectedDoc.client_guardian_name || selectedDoc.guardian_name || 'Parent / Legal Guardian'}</span>
                                    {selectedDoc.esign_signer_email && (
                                      <span className={`ml-1 ${isClinicalLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>({selectedDoc.esign_signer_email})</span>
                                    )}
                                  </div>
                                  {(selectedDoc.is_guardian_signature_overdue || selectedDoc.guardian_signature_status === 'OVERDUE') && (
                                    <div className={`p-2 rounded text-xs flex items-center space-x-2 ${
                                      isClinicalLightMobile ? 'bg-rose-50 border border-rose-200 text-rose-900' : 'bg-rose-500/15 border border-rose-500/40 text-rose-200'
                                    }`}>
                                      <AlertTriangle className={`w-4 h-4 shrink-0 ${isClinicalLightMobile ? 'text-rose-600' : 'text-rose-400'}`} />
                                      <div>
                                        <strong className={`font-semibold ${isClinicalLightMobile ? 'text-rose-900' : 'text-rose-300'}`}>
                                          {(selectedDoc as any).late_pay_consequence
                                            ? GUARDIAN_OVERDUE_PAY_MESSAGE
                                            : ((selectedDoc as any).guardian_signature_display_label || 'Guardian Signature — OVERDUE')}
                                        </strong>
                                      </div>
                                    </div>
                                  )}
                                  {selectedDoc.doc_type === 'INTAKE_PACKET' || selectedDoc.doc_type === 'DDD_PACKET' ? (
                                    <div className={`text-[10px] ${isClinicalLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
                                      Native SignWell automated reminders active (days 3, 6, 10).
                                    </div>
                                  ) : (
                                    <div className={`text-[10px] ${isClinicalLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
                                      {selectedDoc.encounter_clock_missing
                                        ? GUARDIAN_CLOCK_MISSING_MESSAGE
                                        : selectedDoc.guardian_deadline_label
                                        ? `Due by ${selectedDoc.guardian_deadline_label}`
                                        : GUARDIAN_CLOCK_MISSING_MESSAGE}
                                    </div>
                                  )}
                                  <div className={`flex space-x-1.5 pt-1 border-t ${isClinicalLightMobile ? 'border-indigo-100' : 'border-indigo-900/40'}`}>
                                    <button
                                      onClick={() => handleSyncEsign(selectedDoc.id)}
                                      disabled={actionInProgress}
                                      title="Manual sync fallback"
                                      className={`flex-1 py-1 rounded-lg text-[10px] font-semibold flex items-center justify-center space-x-1 border transition-colors ${
                                        isClinicalLightMobile
                                          ? 'bg-white hover:bg-slate-100 text-slate-700 border-slate-200'
                                          : 'bg-slate-800 hover:bg-slate-700 text-slate-300 border-slate-700'
                                      }`}
                                    >
                                      <RefreshCw className={`w-3 h-3 ${actionInProgress ? 'animate-spin' : ''}`} />
                                      <span>Sync Status (Fallback)</span>
                                    </button>
                                    <button
                                      onClick={handleOpenEsign}
                                      className={`px-2.5 py-1 rounded-lg text-[10px] border transition-colors ${
                                        isClinicalLightMobile
                                          ? 'bg-white hover:bg-slate-100 text-slate-700 border-slate-200'
                                          : 'bg-slate-800 hover:bg-slate-700 text-slate-300 border-slate-700'
                                      }`}
                                    >
                                      Re-dispatch
                                    </button>
                                  </div>
                                </div>
                              )}

                              {!selectedDoc.guardian_email && (
                                <div className={`p-2 rounded-lg text-[10px] flex items-center space-x-1.5 ${
                                  isClinicalLightMobile
                                    ? 'bg-amber-50 border border-amber-200 text-amber-900'
                                    : 'bg-amber-500/10 border border-amber-500/20 text-amber-300'
                                }`}>
                                  <AlertTriangle className={`w-3.5 h-3.5 shrink-0 ${isClinicalLightMobile ? 'text-amber-600' : 'text-amber-400'}`} />
                                  <span>Guardian email missing on client chart — update chart before sending eSign.</span>
                                </div>
                              )}

                              <input
                                type="text"
                                value={guardianSigName}
                                onChange={(e) => setGuardianSigName(e.target.value)}
                                placeholder="Guardian Name on File"
                                className={`w-full rounded-lg p-2 text-xs font-medium shadow-xs focus:outline-none ${
                                  isClinicalLightMobile
                                    ? isAssistantMobile
                                      ? 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200'
                                      : 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-purple-400 focus:ring-1 focus:ring-purple-200'
                                    : 'bg-[#0a101b] border border-slate-700/80 text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30'
                                }`}
                              />
                              <select
                                value={guardianRelationship}
                                onChange={(e) => setGuardianRelationship(e.target.value)}
                                className={`w-full rounded-lg p-2 text-xs font-medium shadow-xs focus:outline-none ${
                                  isClinicalLightMobile
                                    ? isAssistantMobile
                                      ? 'bg-white border border-slate-200 text-slate-900 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200'
                                      : 'bg-white border border-slate-200 text-slate-900 focus:border-purple-400 focus:ring-1 focus:ring-purple-200'
                                    : 'bg-[#0a101b] border border-slate-700/80 text-slate-100 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30'
                                }`}
                              >
                                <option value="Parent / Legal Guardian">Parent / Legal Guardian</option>
                                <option value="Mother">Mother</option>
                                <option value="Father">Father</option>
                                <option value="Foster Parent / Case Worker">Foster Parent / Case Worker</option>
                                <option value="Legal Representative / POA">Legal Representative / POA</option>
                              </select>

                              {selectedDoc.doc_type === 'SOAP' && (
                                <div className="space-y-2">
                                  <p className={`text-xs leading-relaxed ${isClinicalLightMobile ? 'text-slate-700' : 'text-slate-200'}`}>
                                    {inPersonEncounterAcknowledgment({
                                      date: selectedDoc.dos,
                                      timeIn: selectedDoc.start_time,
                                      timeOut: selectedDoc.end_time
                                    })}
                                  </p>
                                  <div className="flex items-center justify-end text-[11px]">
                                    <button
                                      type="button"
                                      onClick={clearSoapGuardianPad}
                                      className={`font-semibold cursor-pointer ${
                                        isClinicalLightMobile ? 'text-cyan-700 hover:text-cyan-900 underline' : 'text-sky-400 hover:text-sky-300 underline'
                                      }`}
                                    >
                                      Clear Pad
                                    </button>
                                  </div>
                                  <div className="border border-slate-300 bg-white rounded-xl overflow-hidden touch-none shadow-xs">
                                    <canvas
                                      ref={soapGuardianCanvasRef}
                                      width={500}
                                      height={130}
                                      onMouseDown={startSoapGuardianDrawing}
                                      onMouseMove={drawSoapGuardianSignature}
                                      onMouseUp={stopSoapGuardianDrawing}
                                      onMouseLeave={stopSoapGuardianDrawing}
                                      onTouchStart={startSoapGuardianDrawing}
                                      onTouchMove={drawSoapGuardianSignature}
                                      onTouchEnd={stopSoapGuardianDrawing}
                                      className="w-full h-32 cursor-crosshair bg-white"
                                    />
                                  </div>
                                  {(!guardianSigName.trim() || !guardianRelationship.trim() || !soapGuardianSignatureData) && (
                                    <p className={`text-xs font-medium ${isClinicalLightMobile ? 'text-amber-800' : 'text-amber-200'}`}>
                                      Guardian/designee signature is required before this session note can be submitted.
                                    </p>
                                  )}
                                </div>
                              )}

                              {isAuthoredEvalDoc(selectedDoc.doc_type) && (
                                <div className="space-y-2">
                                  <div className="flex items-center justify-end text-[11px]">
                                    <button
                                      type="button"
                                      onClick={clearSoapGuardianPad}
                                      className={`font-semibold cursor-pointer ${
                                        isClinicalLightMobile ? 'text-cyan-700 hover:text-cyan-900 underline' : 'text-sky-400 hover:text-sky-300 underline'
                                      }`}
                                    >
                                      Clear Pad
                                    </button>
                                  </div>
                                  <div className="border border-slate-300 bg-white rounded-xl overflow-hidden touch-none shadow-xs">
                                    <canvas
                                      ref={soapGuardianCanvasRef}
                                      width={500}
                                      height={130}
                                      onMouseDown={startSoapGuardianDrawing}
                                      onMouseMove={drawSoapGuardianSignature}
                                      onMouseUp={stopSoapGuardianDrawing}
                                      onMouseLeave={stopSoapGuardianDrawing}
                                      onTouchStart={startSoapGuardianDrawing}
                                      onTouchMove={drawSoapGuardianSignature}
                                      onTouchEnd={stopSoapGuardianDrawing}
                                      className="w-full h-32 cursor-crosshair bg-white"
                                    />
                                  </div>
                                  {(!guardianSigName.trim() || !guardianRelationship.trim() || !soapGuardianSignatureData) && (
                                    <p className={`text-xs font-medium ${isClinicalLightMobile ? 'text-amber-800' : 'text-amber-200'}`}>
                                      Guardian name, relationship, and signature are required before this evaluation can be signed.
                                    </p>
                                  )}
                                </div>
                              )}

                              {/* Primary & Secondary Delivery Actions */}
                              <div className="space-y-2">
                                <div className="flex space-x-1.5">
                                  <button
                                    onClick={() => handleSignGuardian(selectedDoc.id, false)}
                                    disabled={
                                      (selectedDoc.doc_type === 'SOAP' || isAuthoredEvalDoc(selectedDoc.doc_type)) &&
                                      (!guardianSigName.trim() || !guardianRelationship.trim() || !soapGuardianSignatureData)
                                    }
                                    className={`flex-1 font-bold py-1.5 rounded-lg text-xs transition-colors shadow text-white disabled:opacity-50 ${
                                      isAssistantMobile
                                        ? 'bg-teal-600 hover:bg-teal-500'
                                        : 'bg-sky-600 hover:bg-sky-500'
                                    }`}
                                  >
                                    Sign In-Person
                                  </button>
                                  {(isAuthoredEvalDoc(selectedDoc.doc_type) ||
                                    selectedDoc.doc_type === 'INTAKE_PACKET' ||
                                    selectedDoc.doc_type === 'DDD_PACKET' ||
                                    selectedDoc.doc_type === 'INTAKE' ||
                                    soapGuardianEsignAccess(selectedDoc).allowed) && (
                                    <button
                                      onClick={handleOpenEsign}
                                      title="Dispatch electronic signature via SignWell"
                                      className="bg-indigo-600 hover:bg-indigo-500 text-white font-semibold py-1.5 px-2.5 rounded-lg text-xs transition-colors flex items-center space-x-1 shadow"
                                    >
                                      <PenTool className="w-3 h-3" />
                                      <span>Send to Guardian for eSign</span>
                                    </button>
                                  )}
                                </div>
                                {selectedDoc.doc_type === 'SOAP' && !soapGuardianEsignAccess(selectedDoc).allowed && (
                                  <div className={`text-[10px] p-2 rounded-lg text-center ${
                                    isClinicalLightMobile
                                      ? 'bg-slate-50 border border-slate-200 text-slate-600'
                                      : 'bg-[#0a101b] border border-slate-700/70 text-slate-400'
                                  }`}>
                                    Mobile SOAP standard requires in-person guardian signature. SignWell remote dispatch is restricted to Office-approved exceptions.
                                  </div>
                                )}
                              </div>

                              {selectedDoc.doc_type !== 'SOAP' && (
                              <button
                                onClick={() => handleSignGuardian(selectedDoc.id, true)}
                                title="Document Medicaid/DDD guardian exception"
                                className={`w-full py-1 px-2 rounded-lg text-[10px] font-semibold border transition-colors text-center ${
                                  isClinicalLightMobile
                                    ? 'bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200'
                                    : 'bg-[#0a101b] hover:bg-slate-800 text-slate-400 hover:text-slate-300 border-slate-700/70'
                                }`}
                              >
                                Document Signature Exception
                              </button>
                              )}
                            </div>
                          )}
                        </div>

                        {/* 3. Supervisor Co-Signature & Quality Review */}
                        <div className={`p-4 rounded-xl space-y-2.5 ${
                          isClinicalLightMobile
                            ? 'bg-white border border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.06)]'
                            : 'bg-[#0e1626]/85 border border-slate-700/70 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.04)]'
                        }`}>
                          <div className={`text-[11px] font-bold ${isClinicalLightMobile ? 'text-slate-800' : 'text-slate-300'}`}>3. Supervisor Co-Sign</div>
                          {!isSlpaDoc ? (
                            <div className={`p-2.5 rounded-lg space-y-1 ${
                              isClinicalLightMobile ? 'bg-slate-50 border border-slate-200/80' : 'bg-[#0a101b] border border-slate-700/70'
                            }`}>
                              <div className={`flex items-center space-x-1.5 text-xs font-semibold ${isClinicalLightMobile ? 'text-slate-700' : 'text-slate-300'}`}>
                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                                <span>Not Required</span>
                              </div>
                              <p className={`text-[10px] ${isClinicalLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
                                Authored by licensed SLP ({selectedDoc.provider_name}).
                              </p>
                            </div>
                          ) : selectedDoc.supervisor_approval_status === 'APPROVED' ? (
                            <div className={`text-xs space-y-1 ${isClinicalLightMobile ? 'text-purple-700' : 'text-indigo-400'}`}>
                              <div className="flex items-center space-x-1">
                                <ShieldCheck className="w-3.5 h-3.5" />
                                <span className="font-semibold">{selectedDoc.supervisor_signature || 'Co-Signed & Approved'}</span>
                              </div>
                              <div className={`text-[10px] font-mono ${isClinicalLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
                                Supervised: {formatSignatureTimestamp(selectedDoc.supervisor_signed_at)}
                              </div>
                            </div>
                          ) : selectedDoc.supervisor_approval_status === 'RETURNED' ? (
                            <div className={`p-2.5 rounded-xl text-[11px] space-y-1.5 ${
                              isClinicalLightMobile
                                ? 'bg-amber-50/90 border border-amber-200 text-amber-950'
                                : 'bg-amber-950/40 border border-amber-800/50'
                            }`}>
                              <div className={`flex items-center space-x-1 font-bold ${isClinicalLightMobile ? 'text-amber-700' : 'text-amber-400'}`}>
                                <AlertTriangle className="w-3.5 h-3.5" />
                                <span>Returned for Revision</span>
                              </div>
                              <p className={`text-[10px] leading-relaxed ${isClinicalLightMobile ? 'text-amber-900' : 'text-slate-300'}`}>
                                "{selectedDoc.supervisor_return_comments || 'Please revise and resubmit.'}"
                              </p>
                              {(user.role === 'SUPERVISOR' || user.role === 'ADMIN') && onNavigateTab && (
                                <button
                                  onClick={() => onNavigateTab('notes')}
                                  className={`w-full mt-1.5 font-semibold py-1 rounded-lg text-xs transition-colors shadow flex items-center justify-center space-x-1 ${
                                    isClinicalLightMobile
                                      ? 'bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300'
                                      : 'bg-slate-800 hover:bg-slate-700 text-amber-300 border border-amber-500/30'
                                  }`}
                                >
                                  <span>Review in Note Approval &rarr;</span>
                                </button>
                              )}
                            </div>
                          ) : (
                            <div className={`p-2.5 rounded-xl text-[11px] space-y-2 text-center ${
                              isClinicalLightMobile
                                ? 'bg-purple-50/60 border border-purple-200/80'
                                : 'bg-[#0a101b] border border-slate-700/70'
                            }`}>
                              <div className={`font-medium ${isClinicalLightMobile ? 'text-purple-950' : 'text-slate-300'}`}>Awaiting Supervising SLP Review & Co-Signature</div>
                              {(user.role === 'SUPERVISOR' || user.role === 'ADMIN') && onNavigateTab && (
                                <button
                                  onClick={() => onNavigateTab('notes')}
                                  className={`w-full font-semibold py-1.5 rounded-lg text-xs transition-colors flex items-center justify-center space-x-1 ${
                                    isClinicalLightMobile
                                      ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-xs'
                                      : 'bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-300 border border-indigo-500/30'
                                  }`}
                                >
                                  <span>Open in Note Approval &rarr;</span>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Expandable Operational & Billing Identity Evidence Panel for Owner / RC / Admin */}
                  {isOperationalUser && (
                    <div className="px-4 sm:px-6 pb-2 no-print">
                      <BillingOperationalEvidencePanel
                        selectedDoc={selectedDoc}
                        userRole={user.role}
                        canViewDetailedEvidence={true}
                      />
                    </div>
                  )}

                  {/* Clean Print Signature Evidence for SOAP Notes (Only printed when window.print is called) */}
                  {selectedDoc.doc_type === 'SOAP' && (
                    <div className="hidden print:block border-t-2 border-slate-400 mt-6 pt-6 break-inside-avoid text-slate-900">
                      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-900 mb-4">
                        Clinical Authentication & Signatures
                      </div>
                      <div className="grid grid-cols-2 gap-8">
                        {/* Treating Clinician Signature */}
                        <div className="border-t border-slate-900 pt-2 space-y-1">
                          <div className="flex justify-between items-baseline">
                            <span className="font-bold text-xs">Treating Clinician:</span>
                            <span className="text-[11px] font-mono">
                              {selectedDoc.clinician_signature_date ? new Date(selectedDoc.clinician_signature_date).toLocaleDateString() : ''}
                            </span>
                          </div>
                          {selectedDoc.clinician_signature ? (
                            <div className="text-xs">
                              <div className="font-serif italic text-base leading-snug">
                                {selectedDoc.clinician_signature}
                              </div>
                              <div className="text-[10px] text-slate-600">
                                Electronically signed by {selectedDoc.clinician_signature_name || selectedDoc.provider_name || 'Treating Clinician'}
                                {selectedDoc.clinician_signature_date && ` on ${new Date(selectedDoc.clinician_signature_date).toLocaleString()}`}
                              </div>
                            </div>
                          ) : (
                            <div className="h-8 border-b border-dashed border-slate-300 text-[10px] text-slate-400 italic flex items-end">
                              Signature on file / Pending clinician signature
                            </div>
                          )}
                        </div>

                        {/* Supervisor Co-Signature (if applicable) */}
                        {(selectedDoc.supervisor_approval_status === 'APPROVED' || selectedDoc.provider_role === 'SLPA') && (
                          <div className="border-t border-slate-900 pt-2 space-y-1">
                            <div className="flex justify-between items-baseline">
                              <span className="font-bold text-xs">Supervising Clinician (SLP):</span>
                              <span className="text-[11px] font-mono">
                                {selectedDoc.supervisor_signature_date ? new Date(selectedDoc.supervisor_signature_date).toLocaleDateString() : ''}
                              </span>
                            </div>
                            {selectedDoc.supervisor_signature ? (
                              <div className="text-xs">
                                <div className="font-serif italic text-base leading-snug">
                                  {selectedDoc.supervisor_signature}
                                </div>
                                <div className="text-[10px] text-slate-600">
                                  Electronically co-signed by {selectedDoc.supervisor_name || 'Supervising SLP'}
                                  {selectedDoc.supervisor_signature_date && ` on ${new Date(selectedDoc.supervisor_signature_date).toLocaleString()}`}
                                </div>
                              </div>
                            ) : (
                              <div className="h-8 border-b border-dashed border-slate-300 text-[10px] text-slate-400 italic flex items-end">
                                Pending supervisor co-signature
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* True Document Record Details Footer (Compact Subordinate Technical Metadata - OWNER & REFERRAL_COORDINATOR Only) */}
              {(() => {
                const showRecordDetailsFooter = user.role === 'OWNER' || user.role === 'REFERRAL_COORDINATOR';
                if (!showRecordDetailsFooter) return null;

                const displayTitle = getCleanDocDisplayTitle(selectedDoc);
                const dosDisplay = selectedDoc.dos || selectedDoc.document_date || selectedDoc.created_at?.slice(0, 10) || '';
                const officialDocTitle = cleanOfficialTitle(selectedDoc.title, displayTitle, dosDisplay);

                return (
                  <footer
                    id="clinical-document-record-details-footer"
                    className="border-t border-slate-800/90 bg-[#0a0f18]/90 px-4 sm:px-6 py-3 text-[11px] font-mono text-slate-400 no-print"
                  >
                    <div className="flex items-center gap-1.5 text-slate-500 font-semibold mb-1.5 text-[10px] uppercase tracking-wider">
                      <FileText className="w-3 h-3 text-slate-500" />
                      <span>Record Details</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 leading-relaxed">
                      <span>
                        Client ID: <strong className="text-slate-300 font-medium">{selectedDoc.client_id}</strong>
                      </span>
                      <span>
                        Document ID: <strong className="text-slate-300 font-medium">{selectedDoc.id}</strong>
                      </span>
                      <span>
                        Type: <span className="text-sky-300/90 font-bold uppercase">{formatDocTypeLabel(selectedDoc.doc_type)}</span>
                      </span>
                      {officialDocTitle && officialDocTitle !== displayTitle && (
                        <span className="truncate max-w-full" title={selectedDoc.title}>
                          Official Title: <span className="text-slate-300 font-medium">{officialDocTitle}</span>
                        </span>
                      )}
                      {selectedDoc.file_name && (
                        <span className="truncate max-w-full" title={selectedDoc.file_name}>
                          Official File: <span className="text-slate-300 font-medium">{selectedDoc.file_name}</span>
                        </span>
                      )}
                      {(selectedDoc.source || selectedDoc.doc_type === 'INTAKE_PACKET' || selectedDoc.doc_type === 'DDD_PACKET') && (
                        <span>
                          Source: <span className="text-slate-300 font-medium">
                            {selectedDoc.doc_type === 'INTAKE_PACKET' || selectedDoc.doc_type === 'DDD_PACKET'
                              ? 'Digital Client Intake / Dual eSign Workflow'
                              : selectedDoc.source === 'MANUAL_UPLOAD'
                              ? 'Manual Upload'
                              : selectedDoc.source === 'INCOMING_FAX'
                              ? 'Electronic Inbound Fax'
                              : selectedDoc.source}
                          </span>
                        </span>
                      )}
                      {selectedDoc.mime_type && (
                        <span>
                          MIME: <span className="text-slate-300 font-medium">{selectedDoc.mime_type}</span>
                        </span>
                      )}
                      {selectedDoc.file_size ? (
                        <span>
                          Size: <span className="text-slate-300 font-medium">{Math.round(selectedDoc.file_size / 1024)} KB</span>
                        </span>
                      ) : null}
                      {selectedDoc.service_id && (
                        <span>
                          Service ID: <strong className="text-slate-300 font-medium">{selectedDoc.service_id}</strong>
                        </span>
                      )}
                      {selectedDoc.fax_id && (
                        <span>
                          Fax ID: <strong className="text-slate-300 font-medium">{selectedDoc.fax_id}</strong>
                        </span>
                      )}
                      {selectedDoc.provider_role && (
                        <span>
                          Provider Role: <span className="text-slate-300 font-medium">{selectedDoc.provider_role}</span>
                        </span>
                      )}
                      {selectedDoc.status && (
                        <span>
                          Status: <span className="text-slate-300 font-medium">{selectedDoc.status}</span>
                        </span>
                      )}
                    </div>
                  </footer>
                );
              })()}
            </div>
          ) : isOwnerOrRc ? (
            <div className="relative rounded-2xl border border-slate-200/90 bg-white shadow-xs overflow-hidden">
              {/* Overview Header Band */}
              <div className="px-5 py-4 sm:px-6 sm:py-4.5 desktop-header-shell bg-gradient-to-r from-[#2a394d] via-[#1a2a42] to-[#0d1727] border-b border-slate-700/80">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 rounded-lg bg-slate-800/80 border border-slate-700 text-sky-400">
                        <FileText className="w-5 h-5" />
                      </div>
                      <h2 className="text-lg font-bold text-white tracking-tight">
                        Period Documentation Overview
                      </h2>
                    </div>
                    <p className="text-xs text-slate-300">
                      Operational audit, signature verification, and billing readiness summary for active context.
                    </p>
                  </div>

                  {/* Active Context Badges */}
                  <div className="flex flex-wrap items-center gap-1.5 text-[11px] font-mono">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-800/90 border border-slate-700 text-slate-200 shadow-xs">
                      <Calendar className="w-3.5 h-3.5 text-emerald-400" />
                      <span className="font-semibold text-slate-200">{resolvedPeriod.periodLabel}</span>
                    </span>
                    {selectedProviderDisplayName !== 'All Therapists' && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-slate-800/90 border border-slate-700 text-sky-300 font-semibold shadow-xs">
                        <Stethoscope className="w-3 h-3 text-sky-400" />
                        <span>{selectedProviderDisplayName}</span>
                      </span>
                    )}
                    {selectedClientDisplayName !== 'All Clients' && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-slate-800/90 border border-slate-700 text-sky-300 font-semibold shadow-xs">
                        <User className="w-3 h-3 text-sky-400" />
                        <span>{selectedClientDisplayName}</span>
                      </span>
                    )}
                    {docTypeFilter !== 'ALL' && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-800/90 border border-slate-700 text-slate-200 font-bold shadow-xs">
                        <span>TYPE: {docTypeFilter}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Card Content Area */}
              <div className="p-5 sm:p-6 space-y-6">

              {/* 3 Primary Metric Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 space-y-1 shadow-xs">
                  <div className="text-[10.5px] font-mono uppercase tracking-wider text-slate-500 font-semibold flex items-center justify-between">
                    <span>Documents in Period</span>
                    <FileText className="w-3.5 h-3.5 text-sky-600" />
                  </div>
                  <div className="text-2xl font-bold font-mono text-slate-900">
                    {periodOverview.totalDocs}
                  </div>
                  <div className="text-[10px] text-slate-500">
                    Filtered by active parameters
                  </div>
                </div>

                <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 space-y-1 shadow-xs">
                  <div className="text-[10.5px] font-mono uppercase tracking-wider text-slate-500 font-semibold flex items-center justify-between">
                    <span>Clients Served</span>
                    <Users className="w-3.5 h-3.5 text-indigo-600" />
                  </div>
                  <div className="text-2xl font-bold font-mono text-slate-900">
                    {periodOverview.uniqueClients}
                  </div>
                  <div className="text-[10px] text-slate-500">
                    Unique active client charts
                  </div>
                </div>

                <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 space-y-1 shadow-xs">
                  <div className="text-[10.5px] font-mono uppercase tracking-wider text-slate-500 font-semibold flex items-center justify-between">
                    <span>Treating Clinicians</span>
                    <Stethoscope className="w-3.5 h-3.5 text-emerald-600" />
                  </div>
                  <div className="text-2xl font-bold font-mono text-slate-900">
                    {periodOverview.uniqueTherapists}
                  </div>
                  <div className="text-[10px] text-slate-500">
                    Contributing provider records
                  </div>
                </div>
              </div>

              {/* Document Type Breakdown (6-card interactive grid) */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-800 uppercase tracking-wider font-mono">
                    Document Types in Period
                  </span>
                  {docTypeFilter !== 'ALL' && (
                    <button
                      type="button"
                      onClick={() => setDocTypeFilter('ALL')}
                      className="text-[10px] font-mono text-sky-700 hover:text-sky-900 underline cursor-pointer"
                    >
                      Clear type filter
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                  {[
                    { tag: 'SOAP' as const, label: 'SOAP Note', count: periodOverview.byType.SOAP, color: 'text-sky-700', dot: 'bg-sky-600' },
                    { tag: 'EVAL' as const, label: 'Evaluation', count: periodOverview.byType.EVAL, color: 'text-blue-700', dot: 'bg-blue-600' },
                    { tag: 'POC' as const, label: 'Plan of Care', count: periodOverview.byType.POC, color: 'text-indigo-700', dot: 'bg-indigo-600' },
                    { tag: 'QPR' as const, label: 'Progress Rep', count: periodOverview.byType.QPR, color: 'text-emerald-700', dot: 'bg-emerald-600' },
                    { tag: 'INTAKE' as const, label: 'Intake Packet', count: periodOverview.byType.INTAKE, color: 'text-teal-700', dot: 'bg-teal-600' },
                    { tag: 'EXTERNAL' as const, label: 'External/Doc', count: periodOverview.byType.EXTERNAL, color: 'text-purple-700', dot: 'bg-purple-600' },
                  ].map((item) => {
                    const isSelected = docTypeFilter === item.tag;
                    return (
                      <button
                        key={item.tag}
                        type="button"
                        onClick={() => setDocTypeFilter((prev) => (prev === item.tag ? 'ALL' : item.tag))}
                        className={`p-2.5 rounded-lg border text-left transition-all cursor-pointer ${
                          isSelected
                            ? 'bg-slate-100 border-slate-400 shadow-xs'
                            : 'bg-white hover:bg-slate-50 border-slate-200'
                        }`}
                        title={`Filter records by ${item.tag}`}
                      >
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className={`w-1.5 h-1.5 rounded-full ${item.dot}`} />
                          <span className="text-[10px] font-mono font-bold text-slate-700 truncate">{item.tag}</span>
                        </div>
                        <div className={`text-base font-bold font-mono ${item.color}`}>
                          {item.count}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Signature Verification & Compliance Status Row */}
              <div className="space-y-2">
                <span className="text-xs font-bold text-slate-800 uppercase tracking-wider font-mono">
                  Signature & Clearance Status
                </span>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  <div className="p-2.5 rounded-lg bg-emerald-50 border border-emerald-200 space-y-0.5">
                    <div className="text-[10px] font-mono text-emerald-800 font-bold flex items-center justify-between">
                      <span>Cleared</span>
                      <ShieldCheck className="w-3 h-3 text-emerald-600" />
                    </div>
                    <div className="text-lg font-bold font-mono text-emerald-900">
                      {periodOverview.status.cleared}
                    </div>
                    <div className="text-[9.5px] text-emerald-700">Ready for billing</div>
                  </div>

                  <div className="p-2.5 rounded-lg bg-sky-50 border border-sky-200 space-y-0.5">
                    <div className="text-[10px] font-mono text-sky-800 font-bold flex items-center justify-between">
                      <span>Clinician Sig</span>
                      <Clock className="w-3 h-3 text-sky-600" />
                    </div>
                    <div className="text-lg font-bold font-mono text-sky-900">
                      {periodOverview.status.pendingClinician}
                    </div>
                    <div className="text-[9.5px] text-sky-700">Pending author</div>
                  </div>

                  <div className="p-2.5 rounded-lg bg-amber-50 border border-amber-200 space-y-0.5">
                    <div className="text-[10px] font-mono text-amber-800 font-bold flex items-center justify-between">
                      <span>Guardian Sig</span>
                      <Clock className="w-3 h-3 text-amber-600" />
                    </div>
                    <div className="text-lg font-bold font-mono text-amber-900">
                      {periodOverview.status.pendingGuardian}
                    </div>
                    <div className="text-[9.5px] text-amber-700">eSign pending</div>
                  </div>

                  <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-200 space-y-0.5">
                    <div className="text-[10px] font-mono text-slate-800 font-bold flex items-center justify-between">
                      <span>Supervisor</span>
                      <Clock className="w-3 h-3 text-slate-600" />
                    </div>
                    <div className="text-lg font-bold font-mono text-slate-900">
                      {periodOverview.status.pendingSupervisor}
                    </div>
                    <div className="text-[9.5px] text-slate-600">SLPA co-sign</div>
                  </div>

                  <div className="p-2.5 rounded-lg bg-rose-50 border border-rose-200 space-y-0.5">
                    <div className="text-[10px] font-mono text-rose-800 font-bold flex items-center justify-between">
                      <span>Needs Action</span>
                      <AlertTriangle className="w-3 h-3 text-rose-600" />
                    </div>
                    <div className="text-lg font-bold font-mono text-rose-900">
                      {periodOverview.status.needsAttention}
                    </div>
                    <div className="text-[9.5px] text-rose-700">Overdue / Returned</div>
                  </div>
                </div>
              </div>

              {/* Needs Attention / Compliance Action Queue */}
              <div className="space-y-2 pt-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-800 uppercase tracking-wider font-mono">
                      Action Required in Current Period
                    </span>
                    {periodOverview.needsAttentionList.length > 0 && (
                      <span className="px-1.5 py-0.2 rounded-full bg-rose-100 text-rose-800 border border-rose-200 text-[10px] font-mono font-bold">
                        {periodOverview.needsAttentionList.length}
                      </span>
                    )}
                  </div>
                  <span className="text-[10.5px] text-slate-500">
                    Click any record to inspect and verify
                  </span>
                </div>

                {periodOverview.needsAttentionList.length === 0 ? (
                  <div className="p-6 rounded-xl bg-emerald-50/70 border border-emerald-200 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 border border-emerald-200 flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-emerald-900">
                        All Records in Period Compliant or Cleared
                      </div>
                      <div className="text-[11px] text-emerald-700">
                        No overdue guardian signatures, returned supervisor revisions, or pending items require attention in this view.
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-xl border border-slate-200 bg-white overflow-hidden divide-y divide-slate-100 max-h-80 overflow-y-auto custom-scrollbar">
                    {periodOverview.needsAttentionList.map((item, idx) => {
                      const severityBadge =
                        item.severity === 'rose'
                          ? 'bg-rose-50 text-rose-800 border-rose-200'
                          : item.severity === 'indigo'
                          ? 'bg-indigo-50 text-indigo-800 border-indigo-200'
                          : item.severity === 'sky'
                          ? 'bg-sky-50 text-sky-800 border-sky-200'
                          : 'bg-amber-50 text-amber-800 border-amber-200';

                      return (
                        <div
                          key={item.doc.id || idx}
                          onClick={() => { void openDocument(item.doc.id, item.doc); }}
                          className="p-3 hover:bg-slate-50 transition-colors cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-2.5"
                        >
                          <div className="space-y-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold text-slate-900 tracking-tight truncate">
                                {item.clientName}
                              </span>
                              <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-slate-100 text-slate-700 border border-slate-200 shrink-0">
                                {item.docType}
                              </span>
                            </div>
                            <div className="text-[10.5px] text-slate-500 flex flex-wrap items-center gap-2">
                              <span>DOS: <strong className="text-slate-800 font-mono">{item.dos}</strong></span>
                              <span>•</span>
                              <span>Therapist: <strong className="text-slate-800">{item.therapistName}</strong></span>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                            <span className={`text-[10px] font-mono px-2 py-0.5 rounded border font-semibold ${severityBadge}`}>
                              {item.statusLabel}
                            </span>
                            <div className="text-slate-400 group-hover:text-slate-700 flex items-center text-xs font-semibold">
                              <ArrowRight className="w-3.5 h-3.5" />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
          ) : (
            <div className="p-12 bg-white rounded-xl border border-slate-200 text-center text-xs text-slate-600 shadow-xs">
              Select a clinical document from the left or click "Create Document" to draft an Evaluation, Plan of Care, or Progress Report.
            </div>
          )}
        </div>
      </div>

      {/* New Document Modal */}
      {showNewDocModal && (
        <NewClinicalDocModal
          user={user}
          initialDocType={newDocInitialType}
          onClose={() => setShowNewDocModal(false)}
          onSuccess={async (newId) => {
            await fetchDocs(newId);
            onRefreshStats();
            setMobileMode('DOCUMENTS');
          }}
        />
      )}

      {/* Outgoing Fax Modal for selected completed document */}
      {canFaxClient && showFaxModal && selectedDoc && (
        <ClientFaxModal
          clientId={selectedDoc.client_id}
          clientName={`${selectedDoc.first_name} ${selectedDoc.last_name}`}
          recipientFax={selectedDoc.physician_fax || ''}
          recipientName={selectedDoc.physician_name || 'Ordering Physician / Primary Care Provider'}
          clientDocuments={[selectedDoc]}
          onClose={() => setShowFaxModal(false)}
        />
      )}

      {/* Supervisor Return Revision Modal */}
      {showReturnModal && selectedDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-xs">
          <div className="bg-white border border-slate-200 rounded-2xl max-w-md w-full p-5 space-y-4 shadow-xl text-slate-900">
            <div className="flex justify-between items-center border-b border-slate-200 pb-3">
              <div className="flex items-center space-x-2 text-amber-800 font-bold text-sm">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span>Return Note for Clinical Revision</span>
              </div>
              <button
                onClick={() => setShowReturnModal(false)}
                className="text-slate-400 hover:text-slate-700 text-lg leading-none cursor-pointer"
              >
                &times;
              </button>
            </div>
            <p className="text-xs text-slate-600">
              Provide feedback for <span className="font-semibold text-slate-900">{selectedDoc.provider_name}</span> regarding what needs correction in this {formatDocTypeLabel(selectedDoc.doc_type)} note.
            </p>
            <textarea
              rows={4}
              value={returnComments}
              onChange={(e) => setReturnComments(e.target.value)}
              placeholder="e.g. Please document specific percentage accuracy for speech production drill and clarify home practice recommendations."
              className="w-full bg-slate-50 border border-slate-300 rounded-lg p-3 text-xs text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 shadow-xs"
            />
            <div className="flex justify-end space-x-2 pt-2">
              <button
                onClick={() => setShowReturnModal(false)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 border border-slate-200 cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSignSupervisor(selectedDoc.id, 'RETURN', returnComments)}
                disabled={actionInProgress}
                className="px-4 py-1.5 rounded-lg text-xs font-bold bg-amber-600 hover:bg-amber-700 text-white shadow-xs cursor-pointer"
              >
                {actionInProgress ? 'Returning...' : 'Confirm Return Note'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Electronic Signature (eSign) Dispatch Modal */}
      {showEsignModal && selectedDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-xs">
          <div className="bg-white max-w-lg w-full rounded-2xl border border-slate-200 shadow-xl p-6 space-y-4 max-h-[90vh] overflow-y-auto text-slate-900">
            <div className="flex justify-between items-center border-b border-slate-200 pb-3">
              <div className="flex items-center space-x-2 text-slate-900 font-bold text-sm">
                <PenTool className="w-4 h-4 text-sky-600" />
                <span>Dispatch Document for Electronic Signature</span>
              </div>
              <button
                onClick={() => setShowEsignModal(false)}
                className="text-slate-400 hover:text-slate-700 p-1 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="text-xs text-slate-600 space-y-1">
              <p>
                Document: <span className="font-semibold text-slate-900">[{formatDocTypeLabel(selectedDoc.doc_type)}] {getCleanDocDisplayTitle(selectedDoc)}</span>
              </p>
              <p className="text-slate-500 text-[11px]">
                Client: {selectedDoc.first_name} {selectedDoc.last_name} (ID: {selectedDoc.client_id})
              </p>
            </div>

            {/* Canonical Client Chart Guardian Record Banner */}
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs space-y-1.5">
              <div className="text-slate-600 font-semibold flex items-center justify-between">
                <span>Client Chart Guardian Record:</span>
                <span className="text-[10px] text-slate-500 font-mono">Canonical Client Chart</span>
              </div>
              <div className="text-slate-700 flex items-center flex-wrap gap-x-3 gap-y-1">
                <div>
                  <span className="text-slate-500 mr-1">Name:</span>
                  <strong className="text-slate-900">{selectedDoc.client_guardian_name || selectedDoc.guardian_name || selectedDoc.guardian_signature || 'Not specified on chart'}</strong>
                </div>
                <div>
                  <span className="text-slate-500 mr-1">Email:</span>
                  <strong className={(selectedDoc.client_guardian_email || selectedDoc.guardian_email) ? 'text-sky-800 font-mono' : 'text-amber-800 font-mono'}>
                    {selectedDoc.client_guardian_email || selectedDoc.guardian_email || 'MISSING ON CHART'}
                  </strong>
                </div>
                {selectedDoc.guardian_phone && (
                  <div>
                    <span className="text-slate-500 mr-1">Phone:</span>
                    <span className="text-slate-700 font-mono">{selectedDoc.guardian_phone}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Missing Contact Warning */}
            {(!esignSignerEmail.trim() || !esignSignerName.trim()) && esignSignerRole === 'GUARDIAN' && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs flex items-start space-x-2.5">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold text-amber-950">Guardian signature contact is missing — update the client chart before sending</div>
                  <p className="text-[11px] text-amber-800 mt-1">
                    Electronic signature dispatch via SignWell requires a valid guardian full name and email address. Please update the client chart before dispatching this document.
                  </p>
                </div>
              </div>
            )}

            <form onSubmit={handleSendEsign} className="space-y-3.5 text-xs">
              <div>
                <label className="block text-slate-700 mb-1 font-semibold">Signer Role</label>
                <select
                  value={esignSignerRole}
                  onChange={(e) => handleEsignRoleChange(e.target.value as any)}
                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 font-medium shadow-xs"
                >
                  <option value="GUARDIAN">Parent / Legal Guardian</option>
                  <option value="THERAPIST">Treating Clinician / SLP</option>
                  <option value="OTHER">Other Authorized Representative</option>
                </select>
              </div>

              <div>
                <label className="block text-slate-700 mb-1 font-semibold">Signer Full Name</label>
                <input
                  type="text"
                  required
                  value={esignSignerName}
                  onChange={(e) => setEsignSignerName(e.target.value)}
                  placeholder="e.g. Maria Gonzalez"
                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 font-medium shadow-xs"
                />
              </div>

              <div>
                <label className="block text-slate-700 mb-1 font-semibold">Signer Email Address</label>
                <input
                  type="email"
                  required
                  value={esignSignerEmail}
                  onChange={(e) => setEsignSignerEmail(e.target.value)}
                  placeholder="signer@example.com"
                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 font-medium shadow-xs"
                />
              </div>

              <div>
                <label className="block text-slate-700 mb-1 font-semibold">Subject Line</label>
                <input
                  type="text"
                  required
                  value={esignSubject}
                  onChange={(e) => setEsignSubject(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 font-medium shadow-xs"
                />
              </div>

              <div>
                <label className="block text-slate-700 mb-1 font-semibold">Signer Instructions / Message</label>
                <textarea
                  rows={4}
                  required
                  value={esignMessage}
                  onChange={(e) => setEsignMessage(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 font-sans shadow-xs"
                />
              </div>

              {/* Signature Follow-up Notice */}
              {selectedDoc.doc_type === 'INTAKE_PACKET' || selectedDoc.doc_type === 'DDD_PACKET' ? (
                <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-[11px] text-slate-700 flex items-center space-x-2">
                  <Bell className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                  <span>Native SignWell automated reminders will be scheduled for days 3, 6, and 10 until signed.</span>
                </div>
              ) : (
                <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-[11px] text-slate-700 flex items-start space-x-2">
                  <Clock className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-slate-900">{guardianRemoteDeadlineSentence({ date: selectedDoc.dos, timeOut: selectedDoc.end_time })}</span>
                  </div>
                </div>
              )}

              {/* Truthful Dispatch Feedback */}
              {esignFeedback && (
                <div
                  className={`p-3 rounded-lg border text-xs space-y-1 ${
                    esignFeedback.success
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                      : 'bg-amber-50 border-amber-200 text-amber-900'
                  }`}
                >
                  <div className="flex items-center space-x-1.5 font-bold">
                    {esignFeedback.success ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        <span>Envelope Dispatched ({esignFeedback.status || 'SENT'})</span>
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                        <span>eSign Gateway Blocked / Safety Gate</span>
                      </>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-700">{esignFeedback.message}</p>
                  {esignFeedback.envelopeId && (
                    <div className="p-1.5 bg-emerald-50 rounded border border-emerald-300 text-[10px] text-emerald-900 font-mono">
                      Envelope ID: {esignFeedback.envelopeId} ({esignFeedback.provider})
                    </div>
                  )}
                  {!esignFeedback.success && (
                    <div className="p-1.5 bg-slate-100 rounded border border-slate-300 text-[10px] text-slate-700 font-mono">
                      Required Configuration: SIGNWELL_API_KEY in environment secrets.
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end space-x-2 pt-2 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowEsignModal(false)}
                  className="px-3 py-1.5 rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 font-semibold cursor-pointer shadow-xs"
                >
                  Close
                </button>
                <button
                  type="submit"
                  disabled={esignSending || !esignSignerName.trim() || !esignSignerEmail.trim()}
                  className="px-4 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold flex items-center space-x-1.5 shadow-xs cursor-pointer"
                >
                  <PenTool className="w-3.5 h-3.5" />
                  <span>{esignSending ? 'Dispatching to SignWell...' : 'Send to Guardian for eSign'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
